/* global process */
"use strict";
import _ from "lodash";
import moment from "moment";
import { ENGLISH, SID_LOOKUP, SPANISH } from "./app.constants";
import { previousIfForbidden } from "./app.utils";
import { MOCK_HOUSING_SITE } from "./grower/housing/constants";
import {
    ACTIVE_GROWER_ADMIN,
    ADD_TERMINATE_EMPLOYEES,
    CAN_EXPORT_DATA_GROWER,
    CREATE_FARM,
    EDIT_FARM,
    EDIT_GROWER_ADMINS,
    EDIT_GROWER_INTEGRATIONS,
    EDIT_GROWER_ORGANIZATION,
    MANAGE_COMMUNICATION,
    MANAGE_ONBOARDING,
    MANAGE_SAFETY,
    MANAGE_DISCIPLINE,
    MANAGE_HOUSING,
} from "./common/auth/auth.constants";
import {
    COMMON_DATE_FORMAT,
    DATE_FORMAT,
    EMPLOYEE_ACTIVE,
} from "./common/common/common.constants";
import {
    getContactWorkerDisplayName,
    getLatestMembership,
    lazyLoad,
    mapUserWithGrowerMembershipName,
    mapUsersWithGrowerMembershipNames,
    urlParamFilter,
    urlParamSort,
} from "./common/common/common.utils";
import { getFieldCropSids } from "./common/spray/application_record_fields.utils";
import {
    TRAINING_REPORT_LIST_STATE,
    TRAINING_REPORT_STATE,
} from "./grower/safety/components/trainingReport/training_report.utils";
import {
    ACTIVE,
    CLOSED,
    COMPLIANCE_CATEGORY,
    EMPLOYEE_ENGAGEMENT_CATEGORY,
    HOUSING_CATEGORY,
    SENT_STATUS,
} from "./common/communication/constants";
import {
    INITIATOR,
    COMPLETE as PAPERWORK_COMPLETE,
    PENDING as PAPERWORK_PENDING,
    RECORD_SOURCE_EXTERNAL,
    SIGNER,
} from "./common/paperwork/constants";
import { getNextRole, SORTS } from "./common/paperwork/paperwork.utils";
import { getLatestPrefillAttemptByRequestSid } from "./grower/paperwork/utils";
import { NOTE, NOW, REMOTE, WORKER_COMMENT } from "./common/safety/constants";
import {
    CANCELLED,
    COMPLETE,
    DYNAMIC,
    GENERAL_TRAINING,
    GROWER_CERTIFIED,
    OBJECTIVES_READY,
    PENDING,
    PENDING as SAFETY_PENDING,
    STATIC,
    FARM_CONTINUING_EDUCATION,
} from "./common/safety/safety.constants";
import {
    IN_PROGRESS_MEETING_STATUS,
    TOPIC_FILTERS,
} from "./common/safety/safety.filters";
import {
    STATUS_HIRED,
    STATUS_NEW,
    STATUS_ONBOARDED,
} from "./grower/jobapplication/constants";

export const growersState = {
    name: "app.growers",
    url: "/growers",
    abstract: true,
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(/* webpackChunkName: "common.safety" */ "./common/safety"),
            import(/* webpackChunkName: "grower" */ "./grower"),
            import(
                /* webpackChunkName: "lazy.communication" */ "./lazy/communication"
            ),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
};

export const growerState = {
    name: "app.growers.grower",
    url: "/{grower_sid}",
    abstract: true,
    redirectTo: "app.growers.grower.manage",
    component: "growerBase",
    resolvePolicy: { when: "EAGER" },
    resolve: {
        grower: function (GrowerOrganizationService, $transition$) {
            "ngInject";
            return GrowerOrganizationService.retrieve(
                $transition$.params().grower_sid,
            );
        },
        timeTrackingVendorConfigurations: function (
            TimeTrackingVendorConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return TimeTrackingVendorConfigurationService.list(
                {
                    grower_organization__sid: $transition$.params().grower_sid,
                    active: true,
                },
                {
                    expand: ["time_tracking_vendor"],
                },
            );
        },
        farms: function (FarmService, $transition$) {
            "ngInject";
            return FarmService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        myGrowerMembership: function (
            GrowerMembershipService,
            account,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                grower_organization__sid: $transition$.params().grower_sid,
                user__sid: account.sid,
            });
        },
        orgs: function (TradeOrganizationService, $transition$) {
            "ngInject";
            return TradeOrganizationService.list({
                growerorganization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        permissions: {
            only: [ACTIVE_GROWER_ADMIN],
            redirectTo: previousIfForbidden,
        },
    },
};

export const growerSetupState = {
    name: "app.growers.growerSetup",
    url: "/{grower_sid}/setup",
    redirectTo: "app.growers.growerSetup.intro",
    component: "growerSetup",
    resolve: {
        grower: function (GrowerOrganizationService, $transition$) {
            "ngInject";
            return GrowerOrganizationService.retrieve(
                $transition$.params().grower_sid,
            );
        },
    },
    data: {
        title: "routes.GROWER_SETUP",
        mixpanelEvent: "Viewed grower setup",
    },
};

export const growerSetupIntroState = {
    name: "app.growers.growerSetup.intro",
    url: "",
    component: "growerSetupIntro",
    data: {
        title: "routes.GROWER_SETUP",
        mixpanelEvent: "Viewed grower setup intro",
    },
};

export const growerSetupAdminsState = {
    name: "app.growers.growerSetup.admins",
    url: "/admins",
    component: "growerSetupAdmins",
    resolve: {
        managementMemberships: function (
            GrowerManagementMembershipService,
            $transition$,
        ) {
            "ngInject";
            return GrowerManagementMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        users: function (managementMemberships, AccountService) {
            "ngInject";
            let userSids = _.uniq(
                _.compact(_.map(managementMemberships, "user")),
            );
            if (!userSids.length) {
                return [];
            }
            return AccountService.several(userSids);
        },
        growerMemberships: function (
            users,
            GrowerMembershipService,
            $transition$,
        ) {
            "ngInject";
            let userSids = _.uniq(_.compact(_.map(users, SID_LOOKUP)));
            if (!userSids.length) {
                return [];
            }
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                user__sid__in: _.join(userSids),
            });
        },
        permissions: function (GrowerManagementPermissionService) {
            "ngInject";
            return GrowerManagementPermissionService.list();
        },
    },
    data: {
        title: "routes.GROWER_SETUP",
        mixpanelEvent: "Viewed grower setup admins",
    },
};

export const growerSetupWpsTrainerState = {
    name: "app.growers.growerSetup.wpsTrainer",
    url: "/wps-trainer",
    component: "growerSetupWpsTrainer",
    resolve: {
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        users: function (growerMemberships, AccountService) {
            "ngInject";
            let userSids = _.uniq(_.compact(_.map(growerMemberships, "user")));
            if (!userSids.length) {
                return [];
            }
            return AccountService.several(userSids);
        },
        credentialTypes: function (CredentialTypeService) {
            "ngInject";
            return CredentialTypeService.list({ active: true });
        },
    },
    data: {
        title: "routes.GROWER_SETUP",
        mixpanelEvent: "Viewed grower setup WPS trainer",
    },
};

export const growerSetupFirstCrewState = {
    name: "app.growers.growerSetup.firstCrew",
    url: "/first-crew",
    component: "growerSetupFirstCrew",
    resolve: {
        farms: function (FarmService, $transition$) {
            "ngInject";
            return FarmService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        myGrowerMembership: function (
            GrowerMembershipService,
            account,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                grower_organization__sid: $transition$.params().grower_sid,
                user__sid: account.sid,
            });
        },
    },
    data: {
        title: "routes.GROWER_SETUP",
        mixpanelEvent: "Viewed grower setup first crew",
    },
};

export const growerSetupFirstFieldState = {
    name: "app.growers.growerSetup.firstField",
    url: "/first-field",
    component: "growerSetupFirstField",
    resolve: {
        farms: function (FarmService, $transition$) {
            "ngInject";
            return FarmService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        crops: function (CropService) {
            "ngInject";
            return CropService.list();
        },
    },
    data: {
        title: "routes.GROWER_SETUP",
        mixpanelEvent: "Viewed grower setup first field",
    },
};

export const growerSetupEmployeeIdNumbersState = {
    name: "app.growers.growerSetup.employeeIdNumbers",
    url: "/employee-id-numbers",
    component: "growerSetupEmployeeIdNumbers",
    data: {
        title: "routes.GROWER_SETUP",
        mixpanelEvent: "Viewed grower setup employee ID numbers",
    },
};

export const growerSetupHiringQaState = {
    name: "app.growers.growerSetup.hiringQa",
    url: "/hiring-qa",
    component: "growerSetupHiringQa",
    data: {
        title: "routes.GROWER_SETUP",
        mixpanelEvent: "Viewed grower setup hiring Q and A",
    },
};

export const growerSetupSafetyAlertsState = {
    name: "app.growers.growerSetup.safetyAlerts",
    url: "/safety-alerts",
    component: "growerSetupSafetyAlerts",
    resolve: {
        alertThemes: function (AlertThemeService, $transition$) {
            "ngInject";
            return AlertThemeService.list({
                category: COMPLIANCE_CATEGORY,
                administrator__growerorganization__sid:
                    $transition$.params().grower_sid,
            });
        },
        alertSubscriptions: function (AlertSubscriptionService, $transition$) {
            "ngInject";
            return AlertSubscriptionService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.GROWER_SETUP",
        mixpanelEvent: "Viewed grower setup safety alerts",
    },
};

export const growerSetupEngagementAlertsState = {
    name: "app.growers.growerSetup.engagementAlerts",
    url: "/employee-engagement-alerts",
    component: "growerSetupEngagementAlerts",
    resolve: {
        alertThemes: function (AlertThemeService, $transition$) {
            "ngInject";
            return AlertThemeService.list({
                category: EMPLOYEE_ENGAGEMENT_CATEGORY,
                administrator__growerorganization__sid:
                    $transition$.params().grower_sid,
            });
        },
        alertSubscriptions: function (AlertSubscriptionService, $transition$) {
            "ngInject";
            return AlertSubscriptionService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.GROWER_SETUP",
        mixpanelEvent: "Viewed grower setup employee engagement alerts",
    },
};

export const growerSetupDoneState = {
    name: "app.growers.growerSetup.done",
    url: "/done",
    component: "growerSetupDone",
    data: {
        title: "routes.GROWER_SETUP",
        mixpanelEvent: "Viewed grower setup done",
    },
};

export const growerDashboardState = {
    name: "app.growers.grower.farms",
    url: "/farms",
    component: "growerDashboard",
    resolve: {
        farms: function (FarmService, $transition$) {
            "ngInject";
            return FarmService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.GROWER_DASHBOARD_TITLE",
        mixpanelEvent: "Viewed grower dashboard",
        breadcrumb: "crumbs.FARMS",
    },
};

export const dairyDashboardState = {
    name: "app.growers.grower.dairy",
    url: "/dairy",
    component: "dairyDashboard",
    resolve: {
        alertThemes: function (AlertThemeService, $transition$) {
            "ngInject";
            return AlertThemeService.list({
                category: EMPLOYEE_ENGAGEMENT_CATEGORY,
                administrator__growerorganization__sid:
                    $transition$.params().grower_sid,
            });
        },
        alertSubscriptions: function (AlertSubscriptionService, $transition$) {
            "ngInject";
            return AlertSubscriptionService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        cePrograms: function (SafetyMeetingProgramService, $transition$) {
            "ngInject";
            return SafetyMeetingProgramService.list({
                administrator__growerorganization__sid:
                    $transition$.params().grower_sid,
                program_type: FARM_CONTINUING_EDUCATION,
                active: true,
            });
        },
        programSubscriptions: function (
            SafetyMeetingProgramSubscriptionService,
            $transition$,
        ) {
            "ngInject";
            return SafetyMeetingProgramSubscriptionService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        crews: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        nextProgramElements: function (
            SafetyMeetingProgramElementService,
            $transition$,
        ) {
            "ngInject";
            // Fetch upcoming safety meeting program elements (start_date >= today)
            return SafetyMeetingProgramElementService.list().then(
                function (elements) {
                    // Group by program sid
                    let grouped = _.groupBy(elements, "safety_meeting_program");
                    // For each program, pick latest element by start_date
                    return _.mapValues(grouped, function (programElements) {
                        let latestEl = _.maxBy(programElements, function (el) {
                            return el.start_date;
                        });
                        return latestEl;
                    });
                },
            );
        },
        cowCareAgreementDocumentPackage: function (
            $transition$,
            DocumentPackageService,
        ) {
            "ngInject";
            return DocumentPackageService.retrieve(
                process.env.CCA_DOCUMENT_PACKAGE_SID,
            );
        },
        cowCareAgreementDocumentPackageTranslateds: function (
            DocumentPackageTranslatedService,
        ) {
            "ngInject";
            return DocumentPackageTranslatedService.list({
                document_package__sid: process.env.CCA_DOCUMENT_PACKAGE_SID,
            });
        },
        cowCareAgreementDocumentPackageTranslated: function (
            cowCareAgreementDocumentPackage,
            cowCareAgreementDocumentPackageTranslateds,
        ) {
            "ngInject";
            return cowCareAgreementDocumentPackage.getDocumentPackageTranslated(
                cowCareAgreementDocumentPackageTranslateds,
            );
        },
        ccaCustomization: function (
            $transition$,
            DocumentPackageCustomizationService,
        ) {
            "ngInject";
            return DocumentPackageCustomizationService.first({
                document_package__sid: process.env.CCA_DOCUMENT_PACKAGE_SID,
                owner__sid: $transition$.params().grower_sid,
            });
        },
        ccaCustomizationTranslateds: function (
            $transition$,
            ccaCustomization,
            DocumentPackageCustomizationTranslatedService,
        ) {
            "ngInject";
            if (!ccaCustomization) {
                return [];
            }
            return DocumentPackageCustomizationTranslatedService.list({
                document_package_customization__sid: ccaCustomization.sid,
            });
        },
        currentEmployees: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        growerMemberships: function (currentEmployees) {
            "ngInject";
            return currentEmployees;
        },
        employees: function (AccountService, growerMemberships) {
            "ngInject";
            return AccountService.several(_.map(growerMemberships, "user"));
        },
        workers: function (employees, growerMemberships) {
            "ngInject";
            return _.map(employees, function (user) {
                return {
                    user: user,
                    growerMembership: getLatestMembership(
                        growerMemberships,
                        user.sid,
                    ),
                };
            });
        },
        unexpiredCowCareAgreements: function (DocumentPackageRecordService) {
            "ngInject";
            return DocumentPackageRecordService.list({
                document_package__sid: process.env.CCA_DOCUMENT_PACKAGE_SID,
                completion_date__gt: moment()
                    .subtract(365, "days")
                    .toISOString(),
            });
        },
    },
    data: {
        title: "routes.DAIRY_DASHBOARD_TITLE",
        mixpanelEvent: "Viewed dairy dashboard",
        breadcrumb: "crumbs.DAIRY_DASHBOARD",
    },
};

export const downloadFARMExportState = {
    name: "app.growers.grower.dairy.downloadFarmExport",
    url: "/download-farm-export",
    component: "downloadFARMExport",
    data: {
        title: "routes.DOWNLOAD_FARM_EXPORT_TITLE",
        mixpanelEvent: "Viewed download FARM export",
        breadcrumb: "crumbs.DOWNLOAD_FARM_EXPORT",
    },
};

export const fieldClockRedirectState = {
    name: "app.growers.grower.fromFieldClock",
    url: "/from-fieldclock?company?employee_id?view",
    resolve: {
        employeeConfiguration: function (
            $transition$,
            TimeTrackingEmployeeConfigurationService,
        ) {
            "ngInject";
            let view = $transition$.params().view;
            if (view == "new-employee") {
                return;
            }
            return TimeTrackingEmployeeConfigurationService.first({
                grower_membership__grower_organization__sid:
                    $transition$.params().grower_sid,
                employee_external_id: $transition$.params().employee_id,
                time_tracking_vendor_configuration__company_external_id:
                    $transition$.params().company,
            });
        },
        growerMembership: function (
            $transition$,
            employeeConfiguration,
            GrowerMembershipService,
        ) {
            "ngInject";
            let view = $transition$.params().view;
            if (view == "new-employee" || !employeeConfiguration) {
                return;
            }
            return GrowerMembershipService.retrieve(
                employeeConfiguration.grower_membership,
            );
        },
        redirect: function (
            $transition$,
            growerMembership,
            farms,
            ToastService,
        ) {
            "ngInject";
            let view = $transition$.params().view;
            let state;
            let params = {
                grower_sid: $transition$.params().grower_sid,
            };
            if (view == "new-employee") {
                state = newWorkerState;
            } else {
                if (growerMembership) {
                    params.member_sid = growerMembership.sid;
                    if (view == "edit-employee") {
                        state = memberEditProductsState;
                    } else if (view == "show-employee") {
                        state = memberState;
                    } else {
                        state = manageRosterState;
                    }
                } else {
                    state = manageRosterState;
                    ToastService.create("toasts.TIME_TRACKING_MISSING");
                }
            }
            return $transition$.router.stateService.target(state, params);
        },
    },
    data: {
        mixpanelEvent: "Viewed FieldClock redirect",
    },
    redirectTo: (trans) => {
        return trans.injector().getAsync("redirect");
    },
};

export const growerHelpState = {
    name: "app.growers.grower.help",
    url: "/help",
    component: "supportDashboard",
    data: {
        title: "routes.GROWER_HELP_TITLE",
        mixpanelEvent: "Viewed grower support dashboard",
    },
};

export const jobApplicationDashboardState = {
    name: "app.growers.grower.jobApplications",
    url: "/job-applications?onboarding_just_started",
    params: {
        onboarding_just_started: {
            value: false,
            squash: true,
            type: "bool",
        },
    },
    component: "jobApplicationDashboard",
    resolve: {
        newJobApplications: function (JobApplicationService, $transition$) {
            "ngInject";
            return JobApplicationService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                status: STATUS_NEW,
            });
        },
        hiredJobApplications: function (JobApplicationService, $transition$) {
            "ngInject";
            return JobApplicationService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                status: STATUS_HIRED,
            });
        },
        onboardedJobApplications: function (
            JobApplicationService,
            $transition$,
        ) {
            "ngInject";
            return JobApplicationService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                status: STATUS_ONBOARDED,
            });
        },
        hiredGrowerMemberships: function (
            GrowerMembershipService,
            hiredJobApplications,
        ) {
            "ngInject";
            let membershipSids = _.compact(
                _.map(hiredJobApplications, "grower_membership"),
            );
            return GrowerMembershipService.several(_.uniq(membershipSids));
        },
        onboardedGrowerMemberships: function (
            GrowerMembershipService,
            onboardedJobApplications,
        ) {
            "ngInject";
            let membershipSids = _.compact(
                _.map(onboardedJobApplications, "grower_membership"),
            );
            if (!membershipSids.length) {
                return [];
            }
            return GrowerMembershipService.several(_.uniq(membershipSids));
        },
        onboardingJustStarted: function ($transition$) {
            "ngInject";
            return $transition$.params().onboarding_just_started;
        },
    },
    data: {
        title: "routes.JOB_APPLICATION_DASHBOARD_TITLE",
        mixpanelEvent: "Viewed job application dashboard",
    },
};

export const applicationOnboardingState = {
    name: "app.growers.grower.jobApplications.onboarding",
    url: "/onboarding",
    abstract: true,
};

export const applicationOnboardingAssignCrewsState = {
    name: "app.growers.grower.jobApplications.onboarding.crews",
    url: "/crews?job_applications",
    params: {
        job_applications: {
            array: true,
            value: [],
        },
    },
    component: "applicationOnboardingAssignCrews",
    resolve: {
        jobApplications: function ($transition$, JobApplicationService) {
            "ngInject";
            let jobApplicationSids = _.uniq(
                $transition$.params().job_applications || [],
            );
            return JobApplicationService.several(jobApplicationSids);
        },
        growerMemberships: function (jobApplications, GrowerMembershipService) {
            "ngInject";
            let membershipSids = _.uniq(
                _.compact(_.map(jobApplications, "grower_membership")),
            );
            return GrowerMembershipService.several(membershipSids);
        },
        crews: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.NEW_WORKER_ASSIGN_CREWS_TITLE",
        mixpanelEvent: "Viewed application onboarding assign crews",
    },
};

export const applicationOnboardingPaperworkState = {
    name: "app.growers.grower.jobApplications.onboarding.paperwork",
    url: "/paperwork?job_applications",
    params: {
        job_applications: {
            array: true,
            value: [],
        },
    },
    component: "applicationOnboardingPaperwork",
    resolve: {
        jobApplications: function ($transition$, JobApplicationService) {
            "ngInject";
            let jobApplicationSids = _.uniq(
                $transition$.params().job_applications || [],
            );
            return JobApplicationService.several(jobApplicationSids);
        },
        growerMemberships: function (jobApplications, GrowerMembershipService) {
            "ngInject";
            let membershipSids = _.uniq(
                _.compact(_.map(jobApplications, "grower_membership")),
            );
            return GrowerMembershipService.several(membershipSids);
        },
        packets: function (PacketService, $transition$) {
            "ngInject";
            return PacketService.list({
                active: true,
                owner__sid: $transition$.params().grower_sid,
            });
        },
        packetMembers: function (PacketMemberService, packets) {
            "ngInject";
            return PacketMemberService.list({
                active: true,
                packet__sid__in: _.join(_.map(packets, SID_LOOKUP)),
            });
        },
    },
    data: {
        title: "routes.NEW_WORKER_PAPERWORK_TITLE",
        mixpanelEvent: "Viewed application onboarding paperwork",
    },
};

export const applicationOnboardingTrainingState = {
    name: "app.growers.grower.jobApplications.onboarding.training",
    url: "/training?job_applications",
    params: {
        job_applications: {
            array: true,
            value: [],
        },
    },
    component: "applicationOnboardingTraining",
    resolve: {
        jobApplications: function ($transition$, JobApplicationService) {
            "ngInject";
            let jobApplicationSids = _.uniq(
                $transition$.params().job_applications || [],
            );
            return JobApplicationService.several(jobApplicationSids);
        },
        growerMemberships: function (jobApplications, GrowerMembershipService) {
            "ngInject";
            let membershipSids = _.uniq(
                _.compact(_.map(jobApplications, "grower_membership")),
            );
            return GrowerMembershipService.several(membershipSids);
        },
        courses: function (SafetyCourseService, $transition$) {
            "ngInject";
            return SafetyCourseService.list({
                active: true,
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        wpsCredentials: function ($transition$, CredentialService) {
            "ngInject";
            return CredentialService.list({
                archived: false,
                credential_type__wps_qualified: true,
                expiration_date__gt: moment().format(DATE_FORMAT),
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        wpsCredentialedUsers: function (wpsCredentials, AccountService) {
            "ngInject";
            let wpsTrainerUserSids = _.map(wpsCredentials, "subject");
            return AccountService.several(wpsTrainerUserSids);
        },
        wpsCredentialedGrowerMemberships: function (
            wpsCredentials,
            GrowerMembershipService,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                user__sid__in: _.join(_.map(wpsCredentials, "subject")),
            });
        },
        wpsTrainers: function (
            wpsCredentialedUsers,
            wpsCredentialedGrowerMemberships,
        ) {
            "ngInject";
            return _.map(wpsCredentialedUsers, function (user) {
                return {
                    user: user,
                    growerMembership: _.find(wpsCredentialedGrowerMemberships, [
                        "user",
                        user.sid,
                    ]),
                };
            });
        },
    },
    data: {
        title: "routes.NEW_WORKER_TRAINING_TITLE",
        mixpanelEvent: "Viewed application onboarding training",
    },
};

export const jobApplicationState = {
    name: "app.growers.grower.jobApplications.jobApplication",
    url: "/{job_application_sid}",
    abstract: true,
    resolve: {
        jobApplication: function (JobApplicationService, $transition$) {
            "ngInject";
            return JobApplicationService.retrieve(
                $transition$.params().job_application_sid,
            );
        },
    },
};

export const verifyApplicantState = {
    name: "app.growers.grower.jobApplications.jobApplication.verifyApplicant",
    url: "/verify-applicant",
    component: "verifyApplicant",
    resolve: {
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        users: function (AccountService, growerMemberships) {
            "ngInject";
            return AccountService.several(_.map(growerMemberships, "user"));
        },
        workers: function (users, growerMemberships) {
            "ngInject";
            return _.map(users, function (user) {
                return {
                    user: user,
                    growerMembership: getLatestMembership(
                        growerMemberships,
                        user.sid,
                    ),
                };
            });
        },
    },
    data: {
        title: "routes.VERIFY_APPLICANT_TITLE",
        mixpanelEvent: "Viewed verify applicant",
        breadcrumb: "crumbs.VERIFY_APPLICANT",
    },
};

export const reviewJobApplicationState = {
    name: "app.growers.grower.jobApplications.jobApplication.reviewJobApplication",
    url: "/review",
    component: "reviewJobApplication",
    resolve: {
        growerMembership: function (jobApplication, GrowerMembershipService) {
            "ngInject";
            if (!jobApplication.grower_membership) {
                return null;
            }
            return GrowerMembershipService.retrieve(
                jobApplication.grower_membership,
            );
        },
        terminationConfiguration: function (
            TerminationConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return TerminationConfigurationService.first({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        terminations: function (growerMembership, TerminationService) {
            "ngInject";
            if (!growerMembership) {
                return [];
            }
            return TerminationService.list({
                grower_membership: growerMembership.sid,
            });
        },
        inactiveMemberships: function (
            growerMembership,
            MembershipService,
            $transition$,
        ) {
            "ngInject";
            if (!growerMembership) {
                return [];
            }
            return MembershipService.list({
                active: false,
                grower_membership__sid: growerMembership.sid,
                group__grower_organization__sid:
                    $transition$.params().grower_sid,
            });
        },
        groups: function (inactiveMemberships, GroupService) {
            "ngInject";
            let groupSids = _.uniq(
                _.compact(_.map(inactiveMemberships, "group")),
            );
            if (!groupSids.length) {
                return [];
            }
            return GroupService.several(groupSids);
        },
    },
    data: {
        title: "routes.REVIEW_JOB_APPLICATION_TITLE",
        mixpanelEvent: "Viewed review job application",
        breadcrumb: "crumbs.REVIEW_JOB_APPLICATION",
    },
};

export const growerManageOnboardingState = {
    name: "app.growers.grower.manageOnboarding",
    url: "/onboarding",
    component: "growerManageOnboarding",
    resolve: {
        documents: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.list({
                active: true,
                status: DocumentPackageService.LIVE,
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        documentPackageTranslateds: function (
            DocumentPackageTranslatedService,
            documents,
        ) {
            "ngInject";
            return DocumentPackageTranslatedService.list({
                document_package__sid__in: _.join(_.map(documents, SID_LOOKUP)),
            });
        },
        customizations: function (
            DocumentPackageCustomizationService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageCustomizationService.list({
                owner__sid: $transition$.params().grower_sid,
            });
        },
        administrators: function (TradeOrganizationService, $transition$) {
            "ngInject";
            return TradeOrganizationService.list({
                growerorganization__sid: $transition$.params().grower_sid,
            });
        },
        packets: function (PacketService, $transition$) {
            "ngInject";
            return PacketService.list({
                active: true,
                owner__sid: $transition$.params().grower_sid,
            });
        },
        packetMembers: function (PacketMemberService, packets) {
            "ngInject";
            return PacketMemberService.list({
                active: true,
                packet__sid__in: _.join(_.map(packets, SID_LOOKUP)),
            });
        },
    },
    data: {
        title: "routes.GROWER_MANAGE_ONBOARDING_TITLE",
        mixpanelEvent: "Viewed grower manage onboarding",
        breadcrumb: "crumbs.GROWER_MANAGE_ONBOARDING",
        permissions: {
            only: [MANAGE_ONBOARDING],
            redirectTo: previousIfForbidden,
        },
    },
};

export const paperworkStageManagerState = {
    name: "app.growers.grower.manageOnboarding.stageManager",
    url: "/onboarding-center",
    component: "paperworkStageManager",
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(/* webpackChunkName: "lazy.paperwork" */ "./lazy/paperwork"),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    data: {
        title: "routes.PAPERWORK_STAGE_MANAGER_TITLE",
        mixpanelEvent: "Viewed paperwork stage manager",
        breadcrumb: "crumbs.PAPERWORK_STAGE_MANAGER",
    },
};

export const sendPaperworkBulkState = {
    name: "app.growers.grower.manageOnboarding.stageManager.sendPaperwork",
    url: "/request-documents?document_package_sid?document_package_customization_sid?initiator_fill_first",
    params: {
        actionItems: {
            array: true,
        },
        document_package_sid: {
            value: null,
            squash: true,
        },
        document_package_customization_sid: {
            value: null,
            squash: true,
        },
        initiator_fill_first: {
            value: true,
            squash: true,
            type: "bool",
        },
    },
    component: "sendPaperworkBulk",
    resolve: {
        crews: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        farms: function (FarmService, $transition$) {
            "ngInject";
            return FarmService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        users: function (AccountService, growerMemberships) {
            "ngInject";
            return AccountService.several(_.map(growerMemberships, "user"));
        },
        workers: function (growerMemberships, users) {
            "ngInject";
            return _.map(users, function (user) {
                return {
                    user: user,
                    growerMembership: _.find(growerMemberships, [
                        "user",
                        user.sid,
                    ]),
                };
            });
        },
        memberships: function (MembershipService, $transition$) {
            "ngInject";
            return MembershipService.list({
                active: true,
                group__grower_organization__sid:
                    $transition$.params().grower_sid,
            });
        },
        preselectedUserSids: function ($transition$) {
            "ngInject";
            return $transition$.params().actionItems || [];
        },
        documents: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.list({
                accessor__sid: $transition$.params().grower_sid,
                active: true,
                status: DocumentPackageService.LIVE,
            });
        },
        documentPackageTranslateds: function (
            DocumentPackageTranslatedService,
            documents,
        ) {
            "ngInject";
            return DocumentPackageTranslatedService.list({
                document_package__sid__in: _.join(
                    _.uniq(_.map(documents, SID_LOOKUP)),
                ),
            });
        },
        packets: function (PacketService, $transition$) {
            "ngInject";
            return PacketService.list({
                active: true,
                owner__sid: $transition$.params().grower_sid,
            });
        },
        packetMembers: function (PacketMemberService, packets) {
            "ngInject";
            return PacketMemberService.list({
                active: true,
                packet__sid__in: _.join(_.map(packets, SID_LOOKUP)),
            });
        },
        administrators: function (TradeOrganizationService, $transition$) {
            "ngInject";
            return TradeOrganizationService.list({
                growerorganization__sid: $transition$.params().grower_sid,
            });
        },
        customizations: function (
            DocumentPackageCustomizationService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageCustomizationService.list({
                owner__sid: $transition$.params().grower_sid,
            });
        },
        customizationTranslateds: function (
            DocumentPackageCustomizationTranslatedService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageCustomizationTranslatedService.list({
                document_package_customization__owner__sid:
                    $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.SEND_PAPERWORK_BULK_TITLE",
        mixpanelEvent: "Viewed send paperwork bulk",
        breadcrumb: "crumbs.SEND_PAPERWORK_BULK",
    },
};

export const growerOnboardingReportState = {
    name: "app.growers.grower.manageOnboarding.report",
    url: "/report?from?to?crews?employees?onboarding_status?needs_my_signature?needs_employer_signature?needs_employee_signature?needs_external_signature?paperwork_not_started?",
    params: {
        from: {
            value: () => moment().startOf("day").toISOString(),
            squash: true,
            inherit: false,
        },
        to: {
            value: () => moment().toISOString(),
            squash: true,
            inherit: false,
        },
        crews: {
            array: true,
            value: [],
        },
        employees: {
            array: true,
            value: [],
        },
        onboarding_status: {
            value: "",
            squash: true,
            inherit: false,
        },
        needs_my_signature: {
            type: "bool",
            value: false,
            squash: true,
            inherit: false,
        },
        needs_employer_signature: {
            type: "bool",
            value: false,
            squash: true,
            inherit: false,
        },
        needs_employee_signature: {
            type: "bool",
            value: false,
            squash: true,
            inherit: false,
        },
        needs_external_signature: {
            type: "bool",
            value: false,
            squash: true,
            inherit: false,
        },
        paperwork_not_started: {
            type: "bool",
            value: false,
            squash: true,
            inherit: false,
        },
    },
    component: "companyOnboardingReport",
    resolve: {
        documentRequests: function (
            DocumentPackageRequestService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageRequestService.list({
                cancelled: false,
                requester__sid: $transition$.params().grower_sid,
                created__gt: $transition$.params().from,
                created__lt: $transition$.params().to,
                omit: "data",
            });
        },
        incompleteDocumentPackageRequests: function (
            DocumentPackageRequestService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageRequestService.list({
                requester__sid: $transition$.params().grower_sid,
                completed: false,
                cancelled: false,
                omit: "data",
            });
        },
        signatureRequestsForIncompleteDocumentPackageRequests: function (
            SignatureRequestService,
            $transition$,
        ) {
            "ngInject";
            return SignatureRequestService.list({
                document_package_request__requester__sid:
                    $transition$.params().grower_sid,
                document_package_request__completed: false,
                document_package_request__cancelled: false,
            });
        },
        documentPackages: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.list({
                pending_document_package_request__requester__sid:
                    $transition$.params().grower_sid,
            });
        },
        managers: function (
            AccountService,
            GrowerMembershipService,
            $transition$,
        ) {
            "ngInject";
            return AccountService.list({
                grower_manager__grower_organization__sid:
                    $transition$.params().grower_sid,
            }).then(function (managers) {
                if (!managers.length) {
                    return [];
                }
                return GrowerMembershipService.list({
                    grower_organization__sid: $transition$.params().grower_sid,
                    user__sid__in: _.join(_.map(managers, SID_LOOKUP)),
                }).then(
                    function (managerMemberships) {
                        return mapUsersWithGrowerMembershipNames(
                            managers,
                            managerMemberships,
                        );
                    },
                    function () {
                        return managers;
                    },
                );
            });
        },
        groups: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        query: function ($transition$) {
            "ngInject";
            return {
                fromDate: $transition$.params().from,
                toDate: $transition$.params().to,
                crews: $transition$.params().crews,
                employees: $transition$.params().employees,
                onboardingStatus: $transition$.params().onboarding_status,
                needsMySignature: $transition$.params().needs_my_signature,
                needsEmployerSignature:
                    $transition$.params().needs_employer_signature,
                needsEmployeeSignature:
                    $transition$.params().needs_employee_signature,
                needsExternalSignature:
                    $transition$.params().needs_external_signature,
                paperworkNotStarted:
                    $transition$.params().paperwork_not_started,
            };
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        memberships: function (MembershipService, $transition$) {
            "ngInject";
            return MembershipService.list({
                group__grower_organization__sid:
                    $transition$.params().grower_sid,
            });
        },
        users: function (AccountService, $transition$) {
            "ngInject";
            return AccountService.list({
                growermembership__grower_organization__sid:
                    $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.GROWER_ONBOARDING_REPORT_TITLE",
        mixpanelEvent: "Viewed grower onboarding report",
        breadcrumb: "crumbs.GROWER_ONBOARDING_REPORT",
    },
};

export const growerDocumentReportState = {
    name: "app.growers.grower.manageOnboarding.report.documentReport",
    url: "/document-report",
    component: "companyDocumentReport",
    resolve: {
        documents: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.list({
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        documentPackageTranslateds: function (
            DocumentPackageTranslatedService,
            documents,
        ) {
            "ngInject";
            return DocumentPackageTranslatedService.list({
                document_package__sid__in: _.join(
                    _.uniq(_.map(documents, SID_LOOKUP)),
                ),
            });
        },
        crews: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.GROWER_DOCUMENT_REPORT_TITLE",
        mixpanelEvent: "Viewed grower detailed document report",
        breadcrumb: "crumbs.GROWER_DOCUMENT_REPORT",
    },
};

export const employeeInformationReportState = {
    name: "app.growers.grower.manageOnboarding.report.employeeInformationReport",
    url: "/employee-information-report",
    component: "employeeInformationReport",
    resolve: {
        crews: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.EMPLOYEE_INFORMATION_REPORT_TITLE",
        mixpanelEvent: "Viewed employee information report",
        breadcrumb: "crumbs.EMPLOYEE_INFORMATION_REPORT",
    },
};

export const identificationDocumentReportState = {
    name: "app.growers.grower.manageOnboarding.report.identificationDocumentReport",
    url: "/identification-document-report",
    component: "identificationDocumentReport",
    resolve: {
        identificationDocumentTypes: function (
            IdentificationDocumentTypeService,
        ) {
            "ngInject";
            return IdentificationDocumentTypeService.list();
        },
    },
    data: {
        title: "routes.IDENTIFICATION_DOCUMENT_REPORT_TITLE",
        mixpanelEvent: "Viewed identification document report",
        breadcrumb: "crumbs.IDENTIFICATION_DOCUMENT_REPORT",
    },
};

export const onboardingDocumentListState = {
    name: "app.growers.grower.manageOnboarding.documents",
    url: "/documents",
    component: "onboardingDocumentList",
    resolve: {
        tags: function (DocumentPackageTagService) {
            "ngInject";
            return DocumentPackageTagService.list();
        },
        documentPackageTranslateds: function (
            DocumentPackageTranslatedService,
            documents,
        ) {
            "ngInject";
            return DocumentPackageTranslatedService.list({
                document_package__sid__in: _.join(
                    _.uniq(_.map(documents, SID_LOOKUP)),
                ),
            });
        },
    },
    data: {
        title: "routes.GROWER_ONBOARDING_DOCUMENT_LIST",
        mixpanelEvent: "Viewed onboarding document list",
        breadcrumb: "crumbs.GROWER_ONBOARDING_DOCUMENT_LIST",
    },
};

export const uploadOnboardingDocumentState = {
    name: "app.growers.grower.manageOnboarding.documents.upload",
    url: "/upload",
    component: "uploadOnboardingDocument",
    data: {
        title: "routes.GROWER_UPLOAD_ONBOARDING_DOCUMENT",
        mixpanelEvent: "Viewed upload onboarding document",
        breadcrumb: "crumbs.GROWER_UPLOAD_ONBOARDING_DOCUMENT",
    },
};

export const growerNonHarvustDocumentsState = {
    name: "app.growers.grower.manageOnboarding.nonHarvustDocuments",
    url: "/non-harvust-documents?grower_memberships",
    params: {
        grower_memberships: {
            array: true,
            value: [],
            squash: true,
            inherit: false,
        },
    },
    component: "uploadExternalDocuments",
    resolve: {
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.several(
                $transition$.params().grower_memberships,
            );
        },
        preselectedUserSids: function ($transition$, growerMemberships) {
            "ngInject";
            return _.map(growerMemberships, "user");
        },
        redirectState: function () {
            "ngInject";
            return growerManageOnboardingState;
        },
    },
    data: {
        title: "routes.GROWER_NON_HARVUST_DOCUMENTS_TITLE",
        mixpanelEvent: "Viewed non-Harvust documents",
        breadcrumb: "crumbs.GROWER_NON_HARVUST_DOCUMENTS",
    },
};

export const onboardingDocumentDetailState = {
    name: "app.growers.grower.manageOnboarding.documents.view",
    url: "/{documentpackage_sid}",
    component: "onboardingDocumentDetail",
    resolve: {
        document: function (documents, DocumentPackageService, $transition$) {
            "ngInject";
            const documentSid = $transition$.params().documentpackage_sid;
            const documentFromList = _.find(documents, [
                SID_LOOKUP,
                documentSid,
            ]);
            if (documentFromList) {
                return documentFromList;
            }
            return DocumentPackageService.retrieve(documentSid);
        },
        documentPackageTranslateds: function (
            DocumentPackageTranslatedService,
            document,
        ) {
            "ngInject";
            return DocumentPackageTranslatedService.list({
                document_package__sid: document.sid,
            });
        },
        documentPackageTranslated: function (
            document,
            documentPackageTranslateds,
        ) {
            "ngInject";
            return document.getDocumentPackageTranslated(
                documentPackageTranslateds,
            );
        },
        administrators: function (administrators) {
            "ngInject";
            return administrators;
        },
        packetMembers: function (PacketMemberService, document, grower) {
            "ngInject";
            return PacketMemberService.list({
                active: true,
                document_package__sid: document.sid,
                packet__owner__sid: grower.sid,
            });
        },
        packets: function (PacketService, packetMembers) {
            "ngInject";
            const packetSids = _.uniq(
                _.compact(_.map(packetMembers, "packet")),
            );
            if (!packetSids.length) {
                return [];
            }
            return PacketService.several(packetSids).then(function (packets) {
                return _.filter(packets, ["active", true]);
            });
        },
    },
    data: {
        title: "routes.GROWER_ONBOARDING_DOCUMENT_LIST",
        mixpanelEvent: "Viewed onboarding document detail",
        breadcrumb: "crumbs.DOCUMENT_PACKAGE_DETAIL",
    },
};

export const growerDocumentReportDownloadDocumentsState = {
    name: "app.growers.grower.manageOnboarding.report.documentReport.download",
    url: "/download-documents",
    params: {
        actionItems: {
            squash: true,
            inherit: false,
        },
    },
    component: "documentDownloader",
    resolve: {
        actionItems: function ($transition$) {
            "ngInject";
            return _.filter($transition$.params().actionItems, function (item) {
                return !_.isNil(item.document_package_record);
            });
        },
        redirectState: function () {
            "ngInject";
            return growerDocumentReportState;
        },
    },
    data: {
        title: "routes.DOWNLOAD_DOCUMENTS_TITLE",
        mixpanelEvent: "Viewed download documents",
        breadcrumb: "crumbs.GROWER_DOCUMENT_REPORT_DOWNLOAD_DOCUMENTS",
        permissions: {
            only: [CAN_EXPORT_DATA_GROWER],
            redirectTo: previousIfForbidden,
        },
    },
    redirectTo: parentIfEmpty,
};

export const documentPackageRecordDataDownloadState = {
    name: "app.growers.grower.manageOnboarding.report.documentPackageRecordDataDownload",
    url: "/data-download",
    component: "documentPackageRecordDataDownload",
    data: {
        title: "routes.DOCUMENTPACKAGERECORD_DATA_DOWNLOAD_TITLE",
        mixpanelEvent: "Viewed documentpackagerecord data download",
        breadcrumb: "crumbs.DOCUMENTPACKAGERECORD_DATA_DOWNLOAD",
        permissions: {
            only: [CAN_EXPORT_DATA_GROWER],
            redirectTo: previousIfForbidden,
        },
    },
};

export const growerExportEmployeeDataState = {
    name: "app.growers.grower.manageOnboarding.report.export",
    url: "/export",
    params: {
        actionItems: {
            squash: true,
            inherit: false,
        },
    },
    component: "companyExportEmployeeData",
    resolve: {
        actionItems: function ($transition$) {
            "ngInject";
            return $transition$.params().actionItems;
        },
        exportFormats: function (ExportFormatService, $transition$) {
            "ngInject";
            return ExportFormatService.list({
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        users: function (actionItems) {
            "ngInject";
            return _.map(actionItems, function (item) {
                return mapUserWithGrowerMembershipName(
                    item.user,
                    item.growerMembership,
                );
            });
        },
    },
    data: {
        title: "routes.COMPANY_EXPORT_EMPLOYEE_DATA_TITLE",
        mixpanelEvent: "Viewed employee data export",
        breadcrumb: "crumbs.COMPANY_EXPORT_EMPLOYEE_DATA",
        permissions: {
            only: [CAN_EXPORT_DATA_GROWER],
            redirectTo: previousIfForbidden,
        },
    },
    redirectTo: parentIfEmpty,
};

export const growerDownloadDocumentsState = {
    name: "app.growers.grower.manageOnboarding.report.download",
    url: "/download-documents",
    params: {
        actionItems: {
            squash: true,
            inherit: false,
        },
    },
    component: "downloadDocuments",
    resolve: {
        subjects: function ($transition$) {
            "ngInject";
            return _.map($transition$.params().actionItems, function (item) {
                return mapUserWithGrowerMembershipName(
                    item.user,
                    item.growerMembership,
                );
            });
        },
        actionItems: function ($transition$) {
            "ngInject";
            return $transition$.params().actionItems;
        },
        redirectState: function () {
            "ngInject";
            return growerOnboardingReportState;
        },
    },
    data: {
        title: "routes.DOWNLOAD_DOCUMENTS_TITLE",
        mixpanelEvent: "Viewed download documents",
        breadcrumb: "crumbs.DOWNLOAD_DOCUMENTS",
        permissions: {
            only: [CAN_EXPORT_DATA_GROWER],
            redirectTo: previousIfForbidden,
        },
    },
    redirectTo: parentIfEmpty,
};

export const growerManagementState = {
    name: "app.growers.grower.management",
    url: "/management",
    component: "growerManagement",
    resolve: {
        managementMemberships: function (
            $transition$,
            GrowerManagementMembershipService,
        ) {
            "ngInject";
            return GrowerManagementMembershipService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        managerUsers: function (managementMemberships, AccountService) {
            "ngInject";
            return AccountService.several(_.map(managementMemberships, "user"));
        },
        growerMemberships: function (
            GrowerMembershipService,
            $transition$,
            managementMemberships,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                user__sid__in: _.join(_.map(managementMemberships, "user")),
            });
        },
        management: function (
            growerMemberships,
            managerUsers,
            managementMemberships,
        ) {
            "ngInject";
            return _.map(managerUsers, function (user) {
                return {
                    user: user,
                    membership: _.find(managementMemberships, [
                        "user",
                        user.sid,
                    ]),
                    growerMembership: _.find(growerMemberships, [
                        "user",
                        user.sid,
                    ]),
                };
            });
        },
        users: function (AccountService, $transition$) {
            "ngInject";
            return AccountService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        selectableUsers: function (managerUsers, users) {
            "ngInject";
            return _.differenceBy(users, managerUsers, SID_LOOKUP);
        },
        growerMembershipsForSelect: function (
            GrowerMembershipService,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        workers: function (selectableUsers, growerMembershipsForSelect) {
            "ngInject";
            return _.compact(
                _.map(selectableUsers, function (user) {
                    return _.find(growerMembershipsForSelect, [
                        "user",
                        user.sid,
                    ]);
                }),
            );
        },
    },
    data: {
        title: "routes.GROWER_MANAGEMENT_TITLE",
        permissions: {
            only: [EDIT_GROWER_ADMINS],
            redirectTo: previousIfForbidden,
        },
        mixpanelEvent: "Viewed grower managers dashboard",
        breadcrumb: "crumbs.GROWER_MANAGEMENT",
    },
};

export const growerManagerCreateAccountState = {
    name: "app.growers.grower.management.createAccount",
    url: "/create-account",
    component: "growerManagementCreateAccount",
    resolve: {
        newMember: function ($transition$) {
            "ngInject";
            return {
                mobile: $transition$.params().mobile,
                preferred_language: SPANISH,
            };
        },
    },
    data: {
        title: "routes.GROWER_MANAGER_CREATE_ACCOUNT_TITLE",
        mixpanelEvent: "Viewed create grower manager account",
        breadcrumb: "crumbs.GROWER_MANAGER_CREATE_ACCOUNT",
    },
};

export const growerManagerEditState = {
    name: "app.growers.grower.management.edit",
    url: "/{management_membership_sid}/edit",
    component: "growerManagerEdit",
    resolve: {
        membership: function ($transition$, GrowerManagementMembershipService) {
            "ngInject";
            return GrowerManagementMembershipService.retrieve(
                $transition$.params().management_membership_sid,
            );
        },
        user: function (membership, AccountService) {
            "ngInject";
            return AccountService.retrieve(membership.user);
        },
        permissions: function (GrowerManagementPermissionService) {
            "ngInject";
            return GrowerManagementPermissionService.list();
        },
        themes: function (ThemeService) {
            "ngInject";
            return ThemeService.list({ public: true, type: "digest" });
        },
        subscriptions: function (
            user,
            NoticeSubscriptionService,
            $transition$,
        ) {
            "ngInject";
            return NoticeSubscriptionService.list({
                user__sid: user.sid,
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        activeSubscriptions: function (themes, subscriptions) {
            "ngInject";
            return _.map(themes, function (theme) {
                return {
                    theme: theme,
                    subscription: _.find(subscriptions, ["theme", theme.sid]),
                };
            });
        },
    },
    data: {
        title: "routes.GROWER_MANAGER_EDIT_TITLE",
        mixpanelEvent: "Viewed edit grower manager",
        breadcrumb: "crumbs.GROWER_MANAGER_EDIT",
    },
};

export const syncResultsListState = {
    name: "app.growers.grower.syncResultsList",
    url: "/sync-history",
    component: "syncResultsList",
    resolve: {
        records: function ($transition$, SyncRecordService) {
            "ngInject";
            return SyncRecordService.list({
                grower__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.SYNC_RESULTS_LIST_TITLE",
        mixpanelEvent: "Viewed sync results list",
        breadcrumb: "crumbs.SYNC_RESULTS_LIST",
    },
};

export const syncResultState = {
    name: "app.growers.grower.syncResultsList.view",
    url: "/{syncrecord_sid}",
    component: "syncResult",
    resolve: {
        syncRecord: function ($transition$, SyncRecordService) {
            "ngInject";
            return SyncRecordService.retrieve(
                $transition$.params().syncrecord_sid,
            );
        },
        removedUsers: function (syncRecord, AccountService) {
            "ngInject";
            return AccountService.several(syncRecord.old_employees);
        },
        removedGrowerMemberships: function (
            $transition$,
            GrowerMembershipService,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                syncs_where_deactivated: $transition$.params().syncrecord_sid,
            });
        },
        removedEmployees: function (removedUsers, removedGrowerMemberships) {
            "ngInject";
            return _.map(removedUsers, function (user) {
                return {
                    user: user,
                    growerMembership: _.find(removedGrowerMemberships, [
                        "user",
                        user.sid,
                    ]),
                };
            });
        },
        needRemovedUsers: function (syncRecord, AccountService) {
            "ngInject";
            return AccountService.several(syncRecord.need_deactivating);
        },
        needRemovedGrowerMemberships: function (
            $transition$,
            GrowerMembershipService,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                syncs_where_need_deactivating:
                    $transition$.params().syncrecord_sid,
            });
        },
        needRemovedEmployees: function (
            needRemovedUsers,
            needRemovedGrowerMemberships,
        ) {
            "ngInject";
            return _.map(needRemovedUsers, function (user) {
                return {
                    user: user,
                    growerMembership: _.find(needRemovedGrowerMemberships, [
                        "user",
                        user.sid,
                    ]),
                };
            });
        },
        reactivatedUsers: function (syncRecord, AccountService) {
            "ngInject";
            return AccountService.several(syncRecord.reactivated_employees);
        },
        reactivatedGrowerMemberships: function (
            $transition$,
            GrowerMembershipService,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                syncs_where_reactivated: $transition$.params().syncrecord_sid,
            });
        },
        newCrewMemberships: function ($transition$, MembershipService) {
            "ngInject";
            return MembershipService.list({
                active: true,
                group__grower_organization__sid:
                    $transition$.params().grower_sid,
            });
        },
        reactivatedEmployees: function (
            reactivatedUsers,
            reactivatedGrowerMemberships,
            newCrewMemberships,
        ) {
            "ngInject";
            return _.map(reactivatedUsers, function (user) {
                return {
                    user: user,
                    growerMembership: _.find(reactivatedGrowerMemberships, [
                        "user",
                        user.sid,
                    ]),
                    memberships: _.filter(newCrewMemberships, [
                        "user",
                        user.sid,
                    ]),
                };
            });
        },
        newUsers: function (syncRecord, AccountService) {
            "ngInject";
            return AccountService.several(syncRecord.new_users);
        },
        newGrowerMemberships: function ($transition$, GrowerMembershipService) {
            "ngInject";
            return GrowerMembershipService.list({
                syncs_where_created: $transition$.params().syncrecord_sid,
            });
        },
        newEmployees: function (
            newUsers,
            newGrowerMemberships,
            newCrewMemberships,
        ) {
            "ngInject";
            return _.map(newUsers, function (user) {
                return {
                    user: user,
                    growerMembership: _.find(newGrowerMemberships, [
                        "user",
                        user.sid,
                    ]),
                    memberships: _.filter(newCrewMemberships, [
                        "user",
                        user.sid,
                    ]),
                };
            });
        },
    },
    data: {
        title: "routes.SYNC_RESULTS_TITLE",
        mixpanelEvent: "Viewed sync results",
        breadcrumb: "crumbs.SYNC_RESULTS",
    },
};

export const growerReactivateEmployeesState = {
    name: "app.growers.grower.reactivate",
    url: "/reactivate?user",
    component: "growerReactivateEmployee",
    resolvePolicy: { when: "EAGER" },
    resolve: {
        user: function ($transition$, AccountService) {
            "ngInject";
            return AccountService.retrieve($transition$.params().user);
        },
        growerMembership: function ($transition$, GrowerMembershipService) {
            "ngInject";
            return GrowerMembershipService.first(
                {
                    grower_organization__sid: $transition$.params().grower_sid,
                    user__sid: $transition$.params().user,
                },
                "end_date",
                false,
            );
        },
        employee: function (growerMembership, user) {
            "ngInject";
            return {
                user: user,
                growerMembership: growerMembership,
            };
        },
    },
    data: {
        title: "routes.GROWER_REACTIVATE_EMPLOYEES_TITLE",
        mixpanelEvent: "Viewed reactivate employee",
    },
    redirectTo: parentIfNoUser,
};

export const growerSettingsState = {
    name: "app.growers.grower.settings",
    url: "/settings",
    component: "growerSettings",
    resolve: {
        billingStatus: function (grower) {
            "ngInject";
            return grower.getBillingStatus();
        },
    },
    data: {
        title: "routes.GROWER_SETTINGS_TITLE",
        mixpanelEvent: "Viewed grower settings",
        permissions: {
            only: [EDIT_GROWER_ORGANIZATION, EDIT_GROWER_INTEGRATIONS],
            redirectTo: previousIfForbidden,
        },
        breadcrumb: "crumbs.GROWER_SETTINGS",
    },
};

export const growerKiosksState = {
    name: growerSettingsState.name + ".kiosks",
    url: "/kiosks",
    component: "growerKiosks",
    resolve: {
        kiosks: function (KioskService, grower) {
            "ngInject";
            return KioskService.list({ grower_organization__sid: grower.sid });
        },
    },
    data: {
        title: "growerKiosks.HEADING",
        mixpanelEvent: "Viewed kiosks",
        breadcrumb: "growerKiosks.HEADING",
    },
};

export const growerPastDueBillingState = {
    name: "app.growers.grower.pastDueBilling",
    url: "/past-due",
    component: "growerPastDue",
    data: {
        mixpanelEvent: "Viewed grower past due",
    },
};

export const growerEditState = {
    name: "app.growers.grower.settings.edit",
    url: "/edit",
    component: "growerEdit",
    resolve: {
        grower: function (GrowerOrganizationService, $transition$) {
            "ngInject";
            return GrowerOrganizationService.retrieve(
                $transition$.params().grower_sid,
            );
        },
    },
    data: {
        title: "routes.GROWER_EDIT_TITLE",
        mixpanelEvent: "Viewed grower edit",
        breadcrumb: "crumbs.GROWER_EDIT",
    },
};

export const growerIntegrationsState = {
    name: "app.growers.grower.settings.integrations",
    url: "/integrations",
    component: "growerIntegrations",
    resolve: {
        svixUrl: function ($transition$, SvixDashboardService) {
            "ngInject";
            return SvixDashboardService.getUrl(
                $transition$.params().grower_sid,
            ).then(
                function (data) {
                    return data;
                },
                function () {
                    return null;
                },
            );
        },
        timeTrackingVendors: function (TimeTrackingVendorService) {
            "ngInject";
            return TimeTrackingVendorService.list();
        },
    },
    data: {
        title: "routes.GROWER_INTEGRATIONS_TITLE",
        mixpanelEvent: "Viewed grower integrations",
        breadcrumb: "crumbs.GROWER_INTEGRATIONS",
    },
};

export const growerBillingState = {
    name: "app.growers.grower.settings.billing",
    url: "/billing",
    component: "growerBilling",
    redirectTo: function (transition) {
        return transition
            .injector()
            .getAsync("billingStatus")
            .then(function (status) {
                if (status.billing_managed_externally) {
                    return growerSettingsState.name;
                }
            });
    },
    resolve: {
        grower: function (GrowerOrganizationService, $transition$) {
            "ngInject";
            return GrowerOrganizationService.retrieve(
                $transition$.params().grower_sid,
            );
        },
        invoices: function (grower) {
            "ngInject";
            return grower.getInvoices();
        },
        creditBalance: function (grower) {
            "ngInject";
            return grower.getCreditBalance();
        },
        defaultPaymentMethod: function (grower) {
            "ngInject";
            return grower.getDefaultPaymentMethod();
        },
        requiresActionSetupIntents: function (grower) {
            "ngInject";
            return grower.getRequiresActionSetupIntents();
        },
    },
    data: {
        title: "routes.GROWER_BILLING_TITLE",
        mixpanelEvent: "Viewed grower billing",
        breadcrumb: "crumbs.GROWER_BILLING",
    },
};

export const addCreditState = {
    name: "app.growers.grower.settings.billing.addCredit",
    url: "/add-credit",
    component: "addCredit",
    resolve: {
        historicalUsage: function (grower) {
            "ngInject";
            return grower.getHistoricalBillingUsage();
        },
        creditBalance: function (grower) {
            "ngInject";
            return grower.getCreditBalance();
        },
    },
    data: {
        title: "routes.ADD_CREDIT_TITLE",
        mixpanelEvent: "Viewed add credit",
        breadcrumb: "crumbs.ADD_CREDIT",
    },
};

export const updatePaymentMethodState = {
    name: "app.growers.grower.settings.billing.updatePaymentMethod",
    url: "/update-payment-method",
    component: "updatePaymentMethod",
    resolve: {
        clientSecret: function (grower) {
            "ngInject";
            return grower.getUpdatePaymentMethodSecret();
        },
    },
    data: {
        title: "routes.UPDATE_PAYMENT_METHOD_TITLE",
        mixpanelEvent: "Viewed update payment method",
        breadcrumb: "crumbs.UPDATE_PAYMENT_METHOD",
    },
};

export const editBillingEmailsState = {
    name: "app.growers.grower.settings.billing.editBillingEmails",
    url: "/update-billing-emails",
    component: "editBillingEmails",
    resolve: {
        grower: function (GrowerOrganizationService, $transition$) {
            "ngInject";
            return GrowerOrganizationService.retrieve(
                $transition$.params().grower_sid,
            );
        },
    },
    data: {
        title: "routes.EDIT_BILLING_EMAILS",
        mixpanelEvent: "Viewed edit billing emails",
        breadcrumb: "crumbs.EDIT_BILLING_EMAILS",
    },
};

export const farmCreateState = {
    name: "app.growers.grower.farms.create",
    url: "/create",
    component: "farmCreate",
    data: {
        title: "routes.FARM_CREATE_TITLE",
        mixpanelEvent: "Viewed farm create",
        breadcrumb: "crumbs.FARM_CREATE",
        permissions: {
            only: [CREATE_FARM],
            redirectTo: previousIfForbidden,
        },
    },
};

export const complianceExpertsState = {
    name: "app.growers.grower.complianceExperts",
    url: "/compliance-experts",
    component: "complianceExperts",
    data: {
        title: "routes.COMPLIANCE_EXPERTS_TITLE",
        mixpanelEvent: "Viewed compliance experts",
    },
};

export const manageRosterState = {
    name: "app.growers.grower.manage",
    url: "/employees?order_by?search?active?active_status?group__sid__in",
    params: {
        order_by: {
            dynamic: true,
            value: "name",
            squash: true,
            inherit: false,
        },
        search: {
            dynamic: true,
            inherit: false,
        },
        active: {
            dynamic: true,
            value: true,
            squash: true,
            inherit: false,
            type: "bool",
        },
        active_status: {
            dynamic: true,
            squash: true,
            inherit: false,
        },
        group__sid__in: {
            dynamic: true,
            array: true,
            value: [],
            squash: true,
            inherit: false,
        },
    },
    component: "manage",
    resolvePolicy: { when: "EAGER" },
    resolve: {
        groups: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        groupFarms: function (farms) {
            "ngInject";
            return farms;
        },
        managers: function (
            AccountService,
            GrowerMembershipService,
            $transition$,
        ) {
            "ngInject";
            return AccountService.list({
                grower_manager__grower_organization__sid:
                    $transition$.params().grower_sid,
            }).then(function (managers) {
                if (!managers.length) {
                    return [];
                }
                return GrowerMembershipService.list({
                    grower_organization__sid: $transition$.params().grower_sid,
                    user__sid__in: _.join(_.map(managers, SID_LOOKUP)),
                }).then(
                    function (managerMemberships) {
                        return mapUsersWithGrowerMembershipNames(
                            managers,
                            managerMemberships,
                        );
                    },
                    function () {
                        return managers;
                    },
                );
            });
        },
        searchTerm: function ($transition$) {
            "ngInject";
            return $transition$.params().search;
        },
    },
    data: {
        title: "routes.MANAGE_ROSTER_TITLE",
        mixpanelEvent: "Viewed employee dashboard",
        breadcrumb: "crumbs.MANAGE_ROSTER",
    },
};

export const manageTimeTrackingState = {
    name: "app.growers.grower.manageTimeTracking",
    url: "/time-tracking",
    component: "manageTimeTracking",
    resolvePolicy: { when: "EAGER" },
    resolve: {
        employees: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        timeTrackingVendorConfigurations: function (
            TimeTrackingVendorConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return TimeTrackingVendorConfigurationService.list(
                {
                    grower_organization__sid: $transition$.params().grower_sid,
                    active: true,
                },
                {
                    expand: ["time_tracking_vendor"],
                },
            );
        },
        timeTrackingEmployeeConfigurations: function (
            TimeTrackingEmployeeConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return TimeTrackingEmployeeConfigurationService.list({
                grower_membership__grower_organization__sid:
                    $transition$.params().grower_sid,
                grower_membership__active: true,
            });
        },
        timeTrackingVendors: function (TimeTrackingVendorService) {
            "ngInject";
            return TimeTrackingVendorService.list();
        },
    },
    data: {
        title: "routes.MANAGE_TIME_TRACKING_TITLE",
        mixpanelEvent: "Viewed manage time tracking",
        breadcrumb: "crumbs.MANAGE_TIME_TRACKING",
        mixpanelEventProperties: {
            active_time_tracking_integrations:
                "timeTrackingVendorConfigurations.length",
        },
    },
};

export const editTimeTrackingState = {
    name: "app.growers.grower.manageTimeTracking.editTimeTracking",
    url: "/edit/{member_sid}",
    component: "newWorkerTimeTracking",
    resolve: {
        user: function (AccountService, $transition$) {
            "ngInject";
            return AccountService.first({
                growermembership__sid: $transition$.params().member_sid,
            });
        },
        growerMembership: function ($transition$, GrowerMembershipService) {
            "ngInject";
            return GrowerMembershipService.retrieve(
                $transition$.params().member_sid,
            );
        },
        timeTrackingVendorConfigurations: function (
            TimeTrackingVendorConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return TimeTrackingVendorConfigurationService.list(
                {
                    grower_organization__sid: $transition$.params().grower_sid,
                    active: true,
                },
                {
                    expand: ["time_tracking_vendor"],
                },
            );
        },
        timeTrackingEmployeeConfigurations: function (
            TimeTrackingEmployeeConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return TimeTrackingEmployeeConfigurationService.list({
                grower_membership__sid: $transition$.params().member_sid,
            });
        },
        redirectState: function () {
            "ngInject";
            return manageTimeTrackingState;
        },
        isInTimeTrackingManage: function () {
            "ngInject";
            return true;
        },
    },
    data: {
        title: "routes.EDIT_MEMBER_TIME_TRACKING_TITLE",
        mixpanelEvent: "Viewed employee time tracking edit",
        breadcrumb: "crumbs.EDIT_MEMBER_TIME_TRACKING",
    },
};

export const memberState = {
    name: "app.growers.grower.manage.member",
    url: "/{member_sid}",
    component: "member",
    resolvePolicy: { when: "EAGER" },
    resolve: {
        user: function (AccountService, $transition$) {
            "ngInject";
            return AccountService.first({
                growermembership__sid: $transition$.params().member_sid,
            });
        },
        growerMembership: function ($transition$, GrowerMembershipService) {
            "ngInject";
            return GrowerMembershipService.retrieve(
                $transition$.params().member_sid,
            );
        },
        groupMemberships: function (MembershipService, $transition$) {
            "ngInject";
            return MembershipService.list({
                active: true,
                group__grower_organization__sid:
                    $transition$.params().grower_sid,
                grower_membership__sid: $transition$.params().member_sid,
            });
        },
        incompletePaperworkRequests: function (
            DocumentPackageRequestService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageRequestService.list({
                requester__sid: $transition$.params().grower_sid,
                completed: false,
                grower_membership__sid: $transition$.params().member_sid,
                cancelled: false,
            });
        },
        allSignatureRequestsRaw: function (
            SignatureRequestService,
            $transition$,
        ) {
            "ngInject";
            return SignatureRequestService.list({
                document_package_request__grower_membership__sid:
                    $transition$.params().member_sid,
                document_package_request__completed: false,
                document_package_request__requester__sid:
                    $transition$.params().grower_sid,
            });
        },
        documentPackages: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.list({
                pending_document_package_request__requester__sid:
                    $transition$.params().grower_sid,
            });
        },
        documentPackageTranslateds: function (
            DocumentPackageTranslatedService,
            documentPackages,
        ) {
            "ngInject";
            return DocumentPackageTranslatedService.list({
                document_package__sid__in: _.join(
                    _.map(documentPackages, SID_LOOKUP),
                ),
            });
        },
        signingGrowerMemberships: function (
            GrowerMembershipService,
            allSignatureRequestsRaw,
        ) {
            "ngInject";
            return GrowerMembershipService.several(
                _.uniq(
                    _.compact(
                        _.map(allSignatureRequestsRaw, "grower_membership"),
                    ),
                ),
            );
        },
        customizations: function (
            DocumentPackageCustomizationService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageCustomizationService.list({
                owner__sid: $transition$.params().grower_sid,
            });
        },
        allPaperwork: function (
            allSignatureRequestsRaw,
            incompletePaperworkRequests,
            documentPackages,
            documentPackageTranslateds,
            signingGrowerMemberships,
            customizations,
        ) {
            "ngInject";
            let paperworks = _.map(
                incompletePaperworkRequests,
                function (documentPackageRequest) {
                    let paperwork = {
                        documentPackageRequest,
                        signatureRequests: _.filter(allSignatureRequestsRaw, {
                            document_package_request:
                                documentPackageRequest.sid,
                        }),
                        documentPackage: _.find(documentPackages, [
                            SID_LOOKUP,
                            documentPackageRequest.document_package,
                        ]),
                    };
                    paperwork.documentPackageTranslated =
                        paperwork.documentPackage.getDocumentPackageTranslated(
                            documentPackageTranslateds,
                        );
                    if (documentPackageRequest.document_package_customization) {
                        paperwork.customization = _.find(customizations, [
                            SID_LOOKUP,
                            documentPackageRequest.document_package_customization,
                        ]);
                    }
                    paperwork.nextRole = getNextRole(
                        paperwork.documentPackageRequest,
                        paperwork.documentPackage,
                    );
                    paperwork.processing =
                        !paperwork.nextRole &&
                        paperwork.signatureRequests.length > 0 &&
                        _.every(paperwork.signatureRequests, [
                            "status",
                            PAPERWORK_COMPLETE,
                        ]);
                    paperwork.nextSignatureRequest = _.find(
                        allSignatureRequestsRaw,
                        [
                            SID_LOOKUP,
                            documentPackageRequest.next_pending_signature_request,
                        ],
                    );
                    if (paperwork.nextSignatureRequest) {
                        paperwork.nextSigner = _.find(
                            signingGrowerMemberships,
                            [
                                SID_LOOKUP,
                                paperwork.nextSignatureRequest
                                    .grower_membership,
                            ],
                        );
                    } else {
                        paperwork.nextSigner = undefined;
                    }
                    return paperwork;
                },
            );
            return _.orderBy(paperworks, "documentPackageRequest.created");
        },
        subjectIncompleteSafetyMeetingRequests: function (
            SafetyMeetingRequestService,
            $transition$,
        ) {
            "ngInject";
            return SafetyMeetingRequestService.list({
                grower_membership__sid: $transition$.params().member_sid,
                safety_meeting__organizer__sid:
                    $transition$.params().grower_sid,
                status: SAFETY_PENDING,
            });
        },
        safetyMeetingTopics: function (
            SafetyMeetingTopicService,
            subjectIncompleteSafetyMeetingRequests,
        ) {
            "ngInject";
            return SafetyMeetingTopicService.several(
                _.map(subjectIncompleteSafetyMeetingRequests, "topic"),
            );
        },
        incompleteSafetyMeetings(
            subjectIncompleteSafetyMeetingRequests,
            SafetyMeetingService,
        ) {
            "ngInject";
            return SafetyMeetingService.several(
                _.map(subjectIncompleteSafetyMeetingRequests, "safety_meeting"),
            );
        },
        incompleteSafetyCourses: function (
            SafetyCourseService,
            incompleteSafetyMeetings,
        ) {
            "ngInject";
            return SafetyCourseService.several(
                _.flatten(_.map(incompleteSafetyMeetings, "courses")),
            );
        },
        credentials: function (CredentialService, $transition$) {
            "ngInject";
            return CredentialService.list({
                grower_membership__sid: $transition$.params().member_sid,
                archived: false,
            });
        },
        credentialTypes: function (CredentialTypeService) {
            "ngInject";
            return CredentialTypeService.list();
        },
        credentialsData: function (credentials, credentialTypes) {
            "ngInject";
            return _.map(credentials, function (credential) {
                return {
                    credential: credential,
                    credentialType: _.find(credentialTypes, [
                        SID_LOOKUP,
                        credential.credential_type,
                    ]),
                };
            });
        },
        identificationDocuments: function (
            IdentificationDocumentService,
            $transition$,
        ) {
            "ngInject";
            return IdentificationDocumentService.head(
                {
                    grower_membership__sid: $transition$.params().member_sid,
                    owner__sid: $transition$.params().grower_sid,
                    archived: false,
                },
                "created",
                4,
                false,
            );
        },
        identificationDocumentTypes: function (
            IdentificationDocumentTypeService,
        ) {
            "ngInject";
            return IdentificationDocumentTypeService.list();
        },
        identificationDocumentsData: function (
            identificationDocuments,
            identificationDocumentTypes,
        ) {
            "ngInject";
            return _.map(identificationDocuments, function (document) {
                return {
                    document: document,
                    documentType: _.find(identificationDocumentTypes, [
                        SID_LOOKUP,
                        document.document_type,
                    ]),
                };
            });
        },
        timeTrackingEmployeeConfigurations: function (
            TimeTrackingEmployeeConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return TimeTrackingEmployeeConfigurationService.list({
                grower_membership__sid: $transition$.params().member_sid,
            });
        },
        housingAssignments: function (HousingAssignmentService, $transition$) {
            "ngInject";
            return HousingAssignmentService.list({
                active: true,
                grower_membership__sid: $transition$.params().member_sid,
            });
        },
        housingSites: function (HousingSiteService, $transition$) {
            "ngInject";
            return HousingSiteService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        memberHousingAssignments: function (housingAssignments, housingSites) {
            "ngInject";
            return _.map(housingAssignments, function (assignment) {
                return {
                    assignment,
                    housingSite: _.find(housingSites, [
                        SID_LOOKUP,
                        assignment.housing_site,
                    ]),
                };
            });
        },
    },
    data: {
        title: "crumbs.MEMBER",
        mixpanelEvent: "Viewed employee",
        breadcrumb: "crumbs.MEMBER",
    },
};

export const manageCommunicationState = {
    name: "app.growers.grower.manageCommunication",
    url: "/communication",
    component: "manageCommunication",
    resolve: {
        alertThemes: function (AlertThemeService, $transition$) {
            "ngInject";
            return AlertThemeService.list({
                administrator__growerorganization__sid:
                    $transition$.params().grower_sid,
            });
        },
        alertSubscriptions: function (AlertSubscriptionService, $transition$) {
            "ngInject";
            return AlertSubscriptionService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        comments: function (CommentService, $transition$) {
            "ngInject";
            return CommentService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                category: WORKER_COMMENT,
                created__gte: moment().subtract(12, "months").toISOString(),
            });
        },
        communicationReportUsers: function (AccountService, $transition$) {
            "ngInject";
            return AccountService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        communicationReportGrowerMemberships: function (
            GrowerMembershipService,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        communicationReportRosterMembers: function (
            communicationReportUsers,
            communicationReportGrowerMemberships,
        ) {
            "ngInject";
            let collection = _.map(communicationReportUsers, function (user) {
                return {
                    user: user,
                    growerMembership: _.find(
                        communicationReportGrowerMemberships,
                        ["user", user.sid],
                    ),
                };
            });
            return _.filter(collection, "growerMembership");
        },
    },
    data: {
        title: "routes.MANAGE_COMMUNICATION_TITLE",
        permissions: {
            only: [MANAGE_COMMUNICATION],
            redirectTo: previousIfForbidden,
        },
        mixpanelEvent: "Viewed communication dashboard",
        breadcrumb: "crumbs.MANAGE_COMMUNICATION",
    },
};

export const communicationSettingsState = {
    name: "app.growers.grower.manageCommunication.communicationSettings",
    url: "/settings",
    component: "editCommunicationSettings",
    resolve: {
        communicationConfiguration: function (
            CommunicationConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return CommunicationConfigurationService.first({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.COMMUNICATION_SETTINGS_TITLE",
        mixpanelEvent: "Viewed communication settings",
        breadcrumb: "crumbs.COMMUNICATION_SETTINGS",
    },
};

const NEW_CONVERSATION = "new_conversation";

export const conversationListState = {
    name: "app.growers.grower.manageCommunication.conversations",
    url: "/conversations?state",
    component: "conversationList",
    resolve: {
        listState: function () {
            "ngInject";
            return conversationListState;
        },
        conversationState: function () {
            "ngInject";
            return conversationState;
        },
        createConversationState: function () {
            "ngInject";
            return conversationCreateState;
        },
        notificationSettingsState: function () {
            "ngInject";
            return conversationNotificationSettingsState;
        },
        listStateParams: function ($transition$) {
            "ngInject";
            return { grower_sid: $transition$.params().grower_sid };
        },
        createStateParams: function ($transition$) {
            "ngInject";
            return { grower_sid: $transition$.params().grower_sid };
        },
        conversationStateParams: function ($transition$) {
            "ngInject";
            return { grower_sid: $transition$.params().grower_sid };
        },
        listTabs: function () {
            "ngInject";
            return [
                {
                    labelKey: "conversationList.ACTIVE_TAB",
                    params: { state: ACTIVE },
                },
                {
                    labelKey: "conversationList.CLOSED_TAB",
                    params: { state: CLOSED },
                },
            ];
        },
        listLayout: function () {
            "ngInject";
            return "table";
        },
        conversations: function (ConversationService, $transition$) {
            "ngInject";
            let params = {
                grower_organization__sid: $transition$.params().grower_sid,
                state: $transition$.params().state,
            };
            return ConversationService.list(params);
        },
        growerMemberships: function (
            GrowerMembershipService,
            $transition$,
            conversations,
        ) {
            "ngInject";
            let ownerSids = _.map(conversations, "owner");
            let contactSids = _.map(conversations, "contact");
            let userSids = _.uniq(_.compact(_.concat(ownerSids, contactSids)));
            return GrowerMembershipService.severalByLookup(
                userSids,
                "user__sid__in",
                {
                    grower_organization__sid: $transition$.params().grower_sid,
                },
            );
        },
    },
    data: {
        title: "routes.CONVERSATION_LIST_TITLE",
        mixpanelEvent: "Viewed conversations list",
        breadcrumb: "crumbs.CONVERSATION_LIST",
    },
};

export const conversationNotificationSettingsState = {
    name: "app.growers.grower.manageCommunication.conversations.notifications",
    url: "/notifications",
    component: "notificationSettings",
    resolve: getNotificationSettingsResolves(NEW_CONVERSATION),
    data: {
        title: "routes.CONVERSATION_NOTIFICATIONS_TITLE",
        breadcrumb: "crumbs.CONVERSATION_NOTIFICATIONS",
        mixpanelEvent: "Viewed conversation notification settings",
    },
};

export const conversationCreateState = {
    name: "app.growers.grower.manageCommunication.conversations.create",
    url: "/create",
    params: {
        contact: {
            squash: true,
        },
        is_anonymous: {
            squash: true,
            value: false,
        },
        quote: {
            squash: true,
            value: false,
        },
    },
    component: "conversationCreate",
    resolve: {
        conversationState: function () {
            "ngInject";
            return conversationState;
        },
        conversationStateParams: function ($transition$) {
            "ngInject";
            return { grower_sid: $transition$.params().grower_sid };
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        employees: function (AccountService, $transition$) {
            "ngInject";
            return AccountService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        workers: function (employees, growerMemberships) {
            "ngInject";
            return _.compact(
                _.map(employees, function (user) {
                    return getLatestMembership(growerMemberships, user.sid);
                }),
            );
        },
        conversation: function ($transition$) {
            "ngInject";
            return {
                contact: $transition$.params().contact,
                is_anonymous: $transition$.params().is_anonymous,
            };
        },
        quote: function ($transition$) {
            "ngInject";
            return $transition$.params().quote;
        },
    },
    data: {
        title: "routes.CONVERSATION_CREATE_TITLE",
        mixpanelEvent: "Viewed create conversation",
        breadcrumb: "crumbs.CONVERSATION_CREATE",
    },
};

export const conversationState = {
    name: "app.growers.grower.manageCommunication.conversations.conversation",
    url: "/{conversation_sid}",
    params: {
        quote: {
            squash: true,
            value: false,
        },
    },
    component: "conversation",
    resolve: {
        listState: function () {
            "ngInject";
            return conversationListState;
        },
        listStateParams: function ($transition$) {
            "ngInject";
            return {
                grower_sid: $transition$.params().grower_sid,
                state: ACTIVE,
            };
        },
        editState: function () {
            "ngInject";
            return conversationEditState;
        },
        conversation: function (ConversationService, $transition$) {
            "ngInject";
            return ConversationService.retrieve(
                $transition$.params().conversation_sid,
            );
        },
        addedParticipant: function (conversation) {
            "ngInject";
            return conversation.addParticipant();
        },
        participants: function (AccountService, conversation) {
            "ngInject";
            return AccountService.several(
                _.uniq(_.compact([conversation.contact, conversation.owner])),
            );
        },
        contact: function (participants, conversation) {
            "ngInject";
            return _.find(participants, [SID_LOOKUP, conversation.contact]);
        },
        ownerMembership: function (
            GrowerMembershipService,
            $transition$,
            conversation,
        ) {
            "ngInject";
            if (!conversation.owner) {
                return null;
            }
            return GrowerMembershipService.first({
                grower_organization__sid: $transition$.params().grower_sid,
                user__sid: conversation.owner,
            }).then(
                function (membership) {
                    return membership;
                },
                function () {
                    return null;
                },
            );
        },
        owner: function (participants, conversation, ownerMembership) {
            "ngInject";
            return mapUserWithGrowerMembershipName(
                _.find(participants, [SID_LOOKUP, conversation.owner]),
                ownerMembership,
            );
        },
        messages: function () {
            "ngInject";
            return [];
        },
        growerMemberships: function (
            GrowerMembershipService,
            $transition$,
            conversation,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                user__sid: conversation.contact,
            });
        },
        contactWorker: function (growerMemberships, contact, conversation) {
            "ngInject";
            let growerMembership = getLatestMembership(
                growerMemberships,
                contact.sid,
            );
            return {
                user: mapUserWithGrowerMembershipName(
                    contact,
                    growerMembership,
                ),
                growerMembership,
                displayName: getContactWorkerDisplayName(
                    conversation,
                    growerMembership,
                ),
            };
        },
        newMessage: function ($transition$) {
            "ngInject";
            let quote = $transition$.params().quote;
            let newMessage = {};
            if (quote) {
                newMessage.raw = '"' + quote + '"';
            }
            return newMessage;
        },
    },
    data: {
        title: "routes.CONVERSATION_TITLE",
        mixpanelEvent: "Viewed conversation",
        breadcrumb: "crumbs.CONVERSATION",
    },
};

// TODO: OK to move to grower level
export const conversationEditState = {
    name: "app.growers.grower.manageCommunication.conversations.conversation.edit",
    url: "/edit",
    views: { conversationEdit: { component: "conversationEdit" } },
    resolve: {
        growerManagementMemberships: function (
            GrowerManagementMembershipService,
            $transition$,
        ) {
            "ngInject";
            return GrowerManagementMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        managerUsers: function (growerManagementMemberships, AccountService) {
            "ngInject";
            return AccountService.several(
                _.map(growerManagementMemberships, "user"),
            );
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        managers: function (
            growerManagementMemberships,
            managerUsers,
            growerMemberships,
        ) {
            "ngInject";
            return _.compact(
                _.map(managerUsers, function (user) {
                    return _.find(growerMemberships, ["user", user.sid]);
                }),
            );
        },
    },
    data: {
        title: "routes.CONVERSATION_EDIT_TITLE",
        mixpanelEvent: "Viewed edit conversation",
        breadcrumb: "crumbs.CONVERSATION_EDIT",
    },
};

// TODO: OK to move to grower level
export const communicationHistoryState = {
    name: "app.growers.grower.manageCommunication.history",
    url: "/messages?sender?status?timetable?sent_by_harvust",
    params: {
        status: {
            value: SENT_STATUS,
        },
        sent_by_harvust: {
            type: "bool",
            value: false,
            squash: true,
        },
    },
    component: "announcementList",
    resolve: {
        announcementObjects: function (AnnouncementService, $transition$) {
            "ngInject";
            let transitionParams = $transition$.params();
            let params = {
                active: true,
                grower_organization__sid: transitionParams.grower_sid,
                status: transitionParams.status,
                timetable: transitionParams.timetable,
                omit: "recipients",
                created__gt: moment().subtract(12, "months").toISOString(),
            };
            params.sender__isnull = transitionParams.sent_by_harvust;
            let senderSid = transitionParams.sender;
            if (!transitionParams.sent_by_harvust && !_.isNil(senderSid)) {
                params.sender__sid = senderSid;
            }
            return AnnouncementService.list(params);
        },
        status: function ($transition$) {
            "ngInject";
            return $transition$.params().status;
        },
        sentByHarvust: function ($transition$) {
            "ngInject";
            return $transition$.params().sent_by_harvust;
        },
    },
    data: {
        title: "routes.COMMUNICATION_HISTORY_TITLE",
        mixpanelEvent: "Viewed previous messages list",
        breadcrumb: "crumbs.COMMUNICATION_HISTORY",
    },
};

export const communicationReportState = {
    name: "app.growers.grower.manageCommunication.communicationReport",
    url: "/report?scrollTo",
    params: {
        scrollTo: {
            value: null,
            squash: true,
        },
    },
    component: "communicationReport",
    resolve: {
        rosterMembers: function (communicationReportRosterMembers) {
            "ngInject";
            return communicationReportRosterMembers;
        },
    },
    data: {
        title: "routes.COMMUNICATION_REPORT_TITLE",
        mixpanelEvent: "Created communications report",
        breadcrumb: "crumbs.COMMUNICATION_REPORT",
    },
};

export const bulkPreferredCommunicationChannelState = {
    name: "app.growers.grower.manageCommunication.bulkPreferredCommunicationChannel",
    url: "/bulk-preferred-communication-channel?grower_memberships",
    params: {
        grower_memberships: {
            array: true,
            value: [],
            squash: true,
            inherit: false,
        },
    },
    component: "bulkPreferredCommunicationChannel",
    resolve: {
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        preselectedUserSids: function ($transition$, growerMemberships) {
            "ngInject";
            let growerMembershipSids =
                $transition$.params().grower_memberships || [];
            if (!growerMembershipSids.length) {
                return [];
            }
            let growerMembershipBySid = _.keyBy(growerMemberships, "sid");
            return _.uniq(
                _.compact(
                    _.map(growerMembershipSids, function (sid) {
                        let growerMembership = growerMembershipBySid[sid];
                        return growerMembership ? growerMembership.user : null;
                    }),
                ),
            );
        },
    },
    data: {
        title: "routes.COMMUNICATION_REPORT_TITLE",
        mixpanelEvent: "Viewed bulk preferred communication channel update",
        breadcrumb: "crumbs.COMMUNICATION_REPORT",
    },
};

// TODO: OK to move to grower level
export const announcementDetailState = {
    name: "app.growers.grower.manageCommunication.history.view",
    url: "/{announcement_sid}",
    component: "announcementDetail",
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
                {},
                {
                    expand: ["translations"],
                },
            );
        },
        senderSid: function (announcement) {
            "ngInject";
            return _.get(announcement, "sender.sid", announcement.sender);
        },
        senderRaw: function (AccountService, senderSid) {
            "ngInject";
            if (!senderSid) {
                return null;
            }
            return AccountService.retrieve(senderSid);
        },
        senderGrowerMembership: function (
            GrowerMembershipService,
            $transition$,
            senderSid,
        ) {
            "ngInject";
            if (!senderSid) {
                return null;
            }
            return GrowerMembershipService.first({
                grower_organization__sid: $transition$.params().grower_sid,
                user__sid: senderSid,
            }).catch(function () {
                return null;
            });
        },
        sender: function (senderRaw, senderGrowerMembership) {
            "ngInject";
            return mapUserWithGrowerMembershipName(
                senderRaw,
                senderGrowerMembership,
            );
        },
        farm: function (FarmService, announcement) {
            "ngInject";
            return FarmService.retrieve(announcement.farm);
        },
        messages: function (MessageService, $transition$) {
            "ngInject";
            return MessageService.list({
                announcement__sid: $transition$.params().announcement_sid,
            });
        },
        recipients: function (
            AccountService,
            messages,
            recipientGrowerMemberships,
        ) {
            "ngInject";
            let recipientUserSids = _.uniq(
                _.compact(
                    _.concat(
                        _.map(recipientGrowerMemberships, "user"),
                        _.map(messages, "recipient"),
                    ),
                ),
            );
            if (!recipientUserSids.length) {
                return [];
            }
            return AccountService.several(recipientUserSids);
        },
        recipientGrowerMemberships: function (
            GrowerMembershipService,
            announcement,
            messages,
        ) {
            "ngInject";
            let recipientGrowerMembershipSids = _.uniq(
                _.compact(
                    _.concat(
                        announcement.recipient_grower_memberships || [],
                        _.map(messages, "grower_membership"),
                    ),
                ),
            );
            if (!recipientGrowerMembershipSids.length) {
                return [];
            }
            return GrowerMembershipService.several(
                recipientGrowerMembershipSids,
            ).then(
                function (recipientGrowerMemberships) {
                    return recipientGrowerMemberships;
                },
                function () {
                    return [];
                },
            );
        },
        messageItems: function (
            messages,
            recipients,
            recipientGrowerMemberships,
        ) {
            "ngInject";
            let recipientsBySid = _.keyBy(recipients, SID_LOOKUP);
            let recipientMembershipsBySid = _.keyBy(
                recipientGrowerMemberships,
                SID_LOOKUP,
            );
            return _.map(messages, function (message) {
                let recipient = recipientsBySid[message.recipient];
                let recipientMembership =
                    recipientMembershipsBySid[message.grower_membership];
                if (recipientMembership) {
                    recipient = mapUserWithGrowerMembershipName(
                        recipient || { sid: recipientMembership.user },
                        recipientMembership,
                    );
                }
                message.recipient = recipient || { sid: message.recipient };
                return message;
            });
        },
        attachments: function (AttachmentService, $transition$) {
            "ngInject";
            return AttachmentService.list({
                announcement__sid: $transition$.params().announcement_sid,
            }).then(function (results) {
                return _.groupBy(results, "language");
            });
        },
    },
    data: {
        title: "routes.ANNOUNCEMENT_DETAIL_TITLE",
        mixpanelEvent: "Viewed announcement detail",
        breadcrumb: "crumbs.ANNOUNCEMENT_DETAIL",
    },
};

export const manageSafetyState = {
    name: "app.growers.grower.manageSafety",
    url: "/safety",
    component: "manageSafety",
    resolve: {
        accidentReports: function (AccidentReportService, $transition$) {
            "ngInject";
            return AccidentReportService.list({
                farm__grower_organization__sid:
                    $transition$.params().grower_sid,
                archived: false,
            });
        },
        accidentReportEmployees: function (
            GrowerMembershipService,
            accidentReports,
            $transition$,
        ) {
            "ngInject";
            let employeeUserSids = _.uniq(
                _.compact(_.map(accidentReports, "employee")),
            );
            if (!employeeUserSids.length) {
                return [];
            }
            return GrowerMembershipService.severalByLookup(
                employeeUserSids,
                "user__sid__in",
                {
                    grower_organization__sid: $transition$.params().grower_sid,
                },
            );
        },
        recentAccidentReports: function (
            accidentReports,
            accidentReportEmployees,
            moment,
        ) {
            "ngInject";
            let recentReports = _.take(
                _.orderBy(accidentReports, "created", "desc"),
                5,
            );
            return _.map(recentReports, function (report) {
                return {
                    report: report,
                    employee: getLatestMembership(
                        accidentReportEmployees,
                        report.employee,
                    ),
                };
            });
        },
        cePrograms: function (SafetyMeetingProgramService, $transition$) {
            "ngInject";
            return SafetyMeetingProgramService.list({
                administrator__growerorganization__sid:
                    $transition$.params().grower_sid,
                program_type: FARM_CONTINUING_EDUCATION,
                active: true,
            });
        },
        safetyMeetingProgram: function (
            SafetyMeetingProgramService,
            $transition$,
        ) {
            "ngInject";
            return SafetyMeetingProgramService.first({
                program_type: DYNAMIC,
                active: true,
            });
        },
        crews: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        employees: function (AccountService, growerMemberships) {
            "ngInject";
            return AccountService.several(_.map(growerMemberships, "user"));
        },
        workers: function (employees, growerMemberships) {
            "ngInject";
            return _.compact(
                _.map(employees, function (user) {
                    return getLatestMembership(growerMemberships, user.sid);
                }),
            );
        },
        subscriptions: function (
            SafetyMeetingProgramSubscriptionService,
            $transition$,
        ) {
            "ngInject";
            return SafetyMeetingProgramSubscriptionService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            }).then(function (subscriptions) {
                return _.keyBy(subscriptions, "safety_meeting_program");
            });
        },
        alertThemes: function (AlertThemeService, $transition$) {
            "ngInject";
            return AlertThemeService.list({
                administrator__growerorganization__sid:
                    $transition$.params().grower_sid,
            });
        },
        alertSubscriptions: function (AlertSubscriptionService, $transition$) {
            "ngInject";
            return AlertSubscriptionService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        sprayConfiguration: function (SprayConfigurationService, $transition$) {
            "ngInject";
            return SprayConfigurationService.first({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        nextProgramElements: function (
            SafetyMeetingProgramElementService,
            $transition$,
            moment,
        ) {
            "ngInject";
            // Fetch upcoming safety meeting program elements (start_date >= today)
            return SafetyMeetingProgramElementService.list({
                start_date__gte: moment().format("YYYY-MM-DD"),
            }).then(function (elements) {
                // Group by program sid
                let grouped = _.groupBy(elements, "safety_meeting_program");
                // For each program, pick earliest element by start_date
                return _.mapValues(grouped, function (programElements) {
                    let nextEl = _.minBy(programElements, function (el) {
                        return el.start_date;
                    });
                    return nextEl;
                });
            });
        },
    },
    data: {
        title: "routes.MANAGE_SAFETY_TITLE",
        permissions: {
            only: [MANAGE_SAFETY],
            redirectTo: previousIfForbidden,
        },
        mixpanelEvent: "Viewed safety dashboard",
        breadcrumb: "crumbs.MANAGE_SAFETY",
    },
};

const NEW_ACCIDENT_REPORT = "new_accident_report";
const SUGGEST_TO_MOVE_TO_IN_PERSON = "suggest_to_move_to_in_person";
const SPRAY_LOG = "spray_log";
const HOUSING_RULES_REMINDER = "housing_rules_reminder";
const ACTION_APPROVAL_NEEDED = "action_approval_needed";

function getNotificationSettingsResolves(themeIdentifier) {
    return {
        themes: function (ThemeService) {
            "ngInject";
            return ThemeService.list({
                type: "realtime",
                theme_identifier: themeIdentifier,
            });
        },
        managementMemberships: function (
            GrowerManagementMembershipService,
            $transition$,
        ) {
            "ngInject";
            return GrowerManagementMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        growerMemberships: function (
            GrowerMembershipService,
            managementMemberships,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.list(
                {
                    grower_organization__sid: $transition$.params().grower_sid,
                    user__sid__in: _.uniq(
                        _.map(managementMemberships, "user"),
                    ).join(","),
                },
                { orderBy: "created", ascending: true },
            );
        },
        subscriptions: function (
            NoticeSubscriptionService,
            $transition$,
            themes,
        ) {
            "ngInject";
            if (!themes.length) {
                return [];
            }
            return NoticeSubscriptionService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                theme__sid__in: _.map(themes, SID_LOOKUP).join(","),
                active: true,
            });
        },
        farms: function (FarmService, $transition$) {
            "ngInject";
            return FarmService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    };
}

export const accidentReportNotificationSettingsState = {
    name: "app.growers.grower.manageSafety.accidentLog.notifications",
    url: "/notifications",
    component: "notificationSettings",
    resolve: getNotificationSettingsResolves(NEW_ACCIDENT_REPORT),
    data: {
        title: "routes.ACCIDENT_REPORT_NOTIFICATIONS_TITLE",
        breadcrumb: "crumbs.ACCIDENT_REPORT_NOTIFICATIONS",
        mixpanelEvent: "Viewed accident report notification settings",
        permissions: { only: [MANAGE_SAFETY], redirectTo: previousIfForbidden },
    },
};

export const safetyMeetingProgramsState = {
    name: "app.growers.grower.manageSafety.programs",
    url: "/safety-meeting-programs",
    views: { manageSafetyView: { component: "safetyMeetingProgramList" } },
    resolve: {
        safetyMeetingPrograms: function (
            SafetyMeetingProgramService,
            $transition$,
        ) {
            "ngInject";
            return SafetyMeetingProgramService.list({
                active: true,
                administrator__growerorganization__sid:
                    $transition$.params().grower_sid,
            });
        },
        safetyMeetingProgramElements: function (
            SafetyMeetingProgramElementService,
        ) {
            "ngInject";
            return SafetyMeetingProgramElementService.list().then(
                function (elements) {
                    return _.groupBy(elements, "safety_meeting_program");
                },
            );
        },
        crews: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        subscriptions: function (
            SafetyMeetingProgramSubscriptionService,
            $transition$,
        ) {
            "ngInject";
            return SafetyMeetingProgramSubscriptionService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            }).then(function (subscriptions) {
                return _.keyBy(subscriptions, "safety_meeting_program");
            });
        },
    },
    data: {
        title: "routes.SAFETY_MEETING_PROGRAM_LIST_TITLE",
        mixpanelEvent: "Viewed safety meeting program list",
        breadcrumb: "crumbs.SAFETY_MEETING_PROGRAM_LIST",
    },
};

export const hazComState = {
    name: "app.growers.grower.manageSafety.hazcom",
    url: "/hazcom",
    views: { manageSafetyView: { component: "hazcom" } },
    resolve: {
        inventoryItems: function (
            SDSProductInventoryItemService,
            $transition$,
        ) {
            "ngInject";
            return SDSProductInventoryItemService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        sdsProducts: function (inventoryItems, SDSProductService) {
            "ngInject";
            let productSids = _.uniq(_.map(inventoryItems, "sds_product"));
            if (!productSids.length) {
                return [];
            }
            return SDSProductService.several(productSids);
        },
        chemicalInventoryRows: function (inventoryItems, sdsProducts) {
            "ngInject";
            return buildChemicalInventoryRows(inventoryItems, sdsProducts);
        },
        hazComConfiguration: function (
            HazComConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return HazComConfigurationService.first({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.HAZCOM_TITLE",
        mixpanelEvent: "Viewed HazCom dashboard",
        breadcrumb: "crumbs.HAZCOM",
    },
};

export const hazComConfigurationState = {
    name: "app.growers.grower.manageSafety.hazcom.hazcomConfiguration",
    url: "/configuration",
    component: "editHazcomConfiguration",
    resolve: {
        hazComConfiguration: function (
            HazComConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return HazComConfigurationService.first({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.EDIT_HAZCOM_CONFIGURATION_TITLE",
        mixpanelEvent: "Viewed edit HazCom configuration",
        breadcrumb: "crumbs.EDIT_HAZCOM_CONFIGURATION",
    },
};

export const hazComAddChemicalsState = {
    name: "app.growers.grower.manageSafety.hazcom.addChemicals",
    url: "/add-chemicals",
    component: "hazcomAddChemicals",
    data: {
        title: "routes.HAZCOM_ADD_CHEMICALS_TITLE",
        mixpanelEvent: "Viewed HazCom add chemicals",
        breadcrumb: "crumbs.HAZCOM_ADD_CHEMICALS",
    },
};

export const hazComGettingStartedChemicalsState = {
    name: "app.growers.grower.manageSafety.hazcom.gettingStartedChemicals",
    url: "/getting-started/chemicals",
    component: "hazcomGettingStartedChemicals",
    data: {
        title: "routes.HAZCOM_GETTING_STARTED_CHEMICALS_TITLE",
        mixpanelEvent: "Viewed HazCom getting started chemicals",
        breadcrumb: "crumbs.HAZCOM_GETTING_STARTED_CHEMICALS",
    },
};

export const hazComGettingStartedWrittenProgramConfigState = {
    name: "app.growers.grower.manageSafety.hazcom.gettingStartedWrittenProgramConfig",
    url: "/getting-started/written-program",
    component: "hazcomGettingStartedWrittenProgramConfig",
    resolve: {
        hazComConfiguration: function (
            HazComConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return HazComConfigurationService.first({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.HAZCOM_GETTING_STARTED_WRITTEN_PROGRAM_CONFIG_TITLE",
        mixpanelEvent: "Viewed HazCom getting started written program config",
        breadcrumb: "crumbs.HAZCOM_GETTING_STARTED_WRITTEN_PROGRAM_CONFIG",
    },
};

export const hazComGettingStartedPosterState = {
    name: "app.growers.grower.manageSafety.hazcom.gettingStartedPoster",
    url: "/getting-started/poster",
    component: "hazcomGettingStartedPoster",
    data: {
        title: "routes.HAZCOM_GETTING_STARTED_POSTER_TITLE",
        mixpanelEvent: "Viewed HazCom getting started poster",
        breadcrumb: "crumbs.HAZCOM_GETTING_STARTED_POSTER",
    },
};

export const hazComGettingStartedActivateState = {
    name: "app.growers.grower.manageSafety.hazcom.gettingStartedActivate",
    url: "/getting-started/activate",
    component: "hazcomGettingStartedActivate",
    resolve: {
        hazComConfiguration: function (
            HazComConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return HazComConfigurationService.first({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.HAZCOM_GETTING_STARTED_ACTIVATE_TITLE",
        mixpanelEvent: "Viewed HazCom getting started activate",
        breadcrumb: "crumbs.HAZCOM_GETTING_STARTED_ACTIVATE",
    },
};

export const removeSelectedChemicalInventoryItemsState = {
    name: "app.growers.grower.manageSafety.hazcom.removeSelectedChemicalInventoryItems",
    url: "/remove-selected",
    params: {
        actionItems: {
            array: true,
        },
    },
    component: "bulkRemoveChemicalInventoryItems",
    resolve: {
        inventoryItems: function (
            $transition$,
            SDSProductInventoryItemService,
        ) {
            "ngInject";
            let actionItems = $transition$.params().actionItems || [];
            if (!actionItems.length) {
                return [];
            }
            return SDSProductInventoryItemService.list({
                sid__in: _.join(actionItems),
                active: true,
            });
        },
        sdsProducts: function (inventoryItems, SDSProductService) {
            "ngInject";
            let productSids = _.uniq(_.map(inventoryItems, "sds_product"));
            if (!productSids.length) {
                return [];
            }
            return SDSProductService.several(productSids);
        },
        chemicalInventoryRows: function (inventoryItems, sdsProducts) {
            "ngInject";
            return buildChemicalInventoryRows(inventoryItems, sdsProducts);
        },
    },
    data: {
        title: "routes.REMOVE_CHEMICAL_INVENTORY_ITEMS_TITLE",
        mixpanelEvent: "Viewed remove selected chemical inventory items",
        breadcrumb: "crumbs.REMOVE_CHEMICAL_INVENTORY_ITEMS",
    },
    redirectTo: parentIfEmpty,
};

export const sprayDashboardState = {
    name: "app.growers.grower.manageSafety.spray",
    url: "/spray",
    views: { manageSafetyView: { component: "sprayDashboard" } },
    resolve: {
        sprayConfiguration: function (SprayConfigurationService, $transition$) {
            "ngInject";
            return SprayConfigurationService.first({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        neighbors: function (NeighborService, $transition$) {
            "ngInject";
            return NeighborService.list({
                spray_configuration__grower_organization__sid:
                    $transition$.params().grower_sid,
                active: true,
            });
        },
        applicationRecords: function (ApplicationRecordService, $transition$) {
            "ngInject";
            return ApplicationRecordService.list(
                {
                    active: true,
                    grower_organization__sid: $transition$.params().grower_sid,
                },
                {
                    orderBy: "created",
                    ascending: false,
                },
            );
        },
        fieldsBySid: function (FieldService, applicationRecords) {
            "ngInject";
            let fieldSids = _.uniq(
                _.flatten(
                    _.map(applicationRecords || [], function (record) {
                        return record.fields;
                    }),
                ),
            );
            if (!fieldSids.length) {
                return {};
            }
            return FieldService.several(fieldSids).then(function (fields) {
                return _.keyBy(fields, SID_LOOKUP);
            });
        },
        applicatorGrowerMemberships: function (
            GrowerMembershipService,
            $transition$,
            applicationRecords,
        ) {
            "ngInject";
            let applicatorGrowerMembershipSids = _.uniq(
                _.compact(
                    _.flatten(
                        _.map(applicationRecords || [], function (record) {
                            return [
                                record.applicator,
                                record.assigned_applicator,
                            ];
                        }),
                    ),
                ),
            );
            if (!applicatorGrowerMembershipSids.length) {
                return [];
            }

            return GrowerMembershipService.several(
                applicatorGrowerMembershipSids,
            );
        },
    },
    data: {
        title: "routes.SPRAY_DASHBOARD_TITLE",
        mixpanelEvent: "Viewed spray dashboard",
        breadcrumb: "crumbs.SPRAY_DASHBOARD",
    },
};

export const sprayNotificationSettingsState = {
    name: "app.growers.grower.manageSafety.spray.notifications",
    url: "/notifications",
    component: "notificationSettings",
    resolve: getNotificationSettingsResolves(SPRAY_LOG),
    data: {
        title: "routes.SPRAY_NOTIFICATIONS_TITLE",
        breadcrumb: "crumbs.SPRAY_NOTIFICATIONS",
        mixpanelEvent: "Viewed spray notification settings",
        permissions: { only: [MANAGE_SAFETY], redirectTo: previousIfForbidden },
    },
};

export const sprayNeighborsState = {
    name: "app.growers.grower.manageSafety.spray.neighbors",
    url: "/neighbors",
    component: "sprayNeighbors",
    resolve: {
        sprayConfiguration: function (SprayConfigurationService, $transition$) {
            "ngInject";
            return SprayConfigurationService.first({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        neighbors: function (NeighborService, sprayConfiguration) {
            "ngInject";
            return NeighborService.list(
                {
                    spray_configuration__sid: sprayConfiguration.sid,
                    active: true,
                },
                { orderBy: "created", ascending: false },
            );
        },
        fields: function (FieldService, $transition$) {
            "ngInject";
            return FieldService.list({
                active: true,
                farm__grower_organization__sid:
                    $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.SPRAY_NEIGHBORS_TITLE",
        mixpanelEvent: "Viewed spray neighbors",
        breadcrumb: "crumbs.SPRAY_NEIGHBORS",
    },
};

export const sprayGetLogState = {
    name: "app.growers.grower.manageSafety.spray.getLog",
    url: "/get-log",
    component: "sprayGetLog",
    data: {
        title: "routes.SPRAY_GET_LOG_TITLE",
        mixpanelEvent: "Viewed spray get log",
        breadcrumb: "crumbs.SPRAY_GET_LOG",
    },
};

export const sprayGetLogPosterState = {
    name: "app.growers.grower.manageSafety.spray.getLogPoster",
    url: "/get-log-poster",
    component: "sprayGetLogPoster",
    data: {
        title: "routes.SPRAY_GET_LOG_POSTER_TITLE",
        mixpanelEvent: "Viewed spray get log poster",
        breadcrumb: "crumbs.SPRAY_GET_LOG_POSTER",
    },
};

export const growerSprayLogState = {
    name: "app.growers.grower.manageSafety.spray.log",
    url: "/log-full?test",
    component: "growerSprayLog",
    params: {
        test: {
            type: "bool",
            value: false,
        },
    },
    resolve: {
        applicationRecords: function (ApplicationRecordService, $transition$) {
            "ngInject";
            return ApplicationRecordService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
                created__gt: moment().subtract(30, "days").toISOString(),
            });
        },
        crops: function (CropService) {
            "ngInject";
            return CropService.list();
        },
        resolvedFields: function (FieldService, $transition$) {
            "ngInject";
            return FieldService.list({
                active: true,
                farm__grower_organization__sid:
                    $transition$.params().grower_sid,
            });
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        sprayerWorkers: function (AccountService, growerMemberships) {
            "ngInject";
            let userSids = _.uniq(_.map(growerMemberships || [], "user"));
            if (!userSids.length) {
                return [];
            }
            return AccountService.several(userSids).then(function (users) {
                return _.compact(
                    _.map(users, function (user) {
                        return getLatestMembership(growerMemberships, user.sid);
                    }),
                );
            });
        },
    },
    data: {
        title: "routes.LOG_FULL_SPRAY_TITLE",
        mixpanelEvent: "Viewed grower full spray log",
        breadcrumb: "crumbs.LOG_FULL_SPRAY",
    },
};

export const growerSprayApplicationRecordState = {
    name: "app.growers.grower.manageSafety.spray.applicationRecord",
    url: "/application-records/{application_record_sid}",
    component: "growerSprayApplicationRecord",
    resolve: {
        applicationRecord: function (ApplicationRecordService, $transition$) {
            "ngInject";
            return ApplicationRecordService.retrieve(
                $transition$.params().application_record_sid,
            );
        },
        pesticideProducts: function (
            PesticideProductService,
            applicationRecord,
        ) {
            "ngInject";

            return PesticideProductService.several(
                applicationRecord.pesticides,
            );
        },
        fields: function (FieldService, applicationRecord) {
            "ngInject";
            let fieldSids = applicationRecord.fields;
            if (!fieldSids.length) {
                return [];
            }
            return FieldService.several(fieldSids);
        },
        crops: function (CropService, fields) {
            "ngInject";
            let cropSids = getFieldCropSids(fields);
            if (!cropSids.length) {
                return [];
            }
            return CropService.several(cropSids);
        },
        announcements: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.list(
                {
                    grower_organization__sid: $transition$.params().grower_sid,
                    originating_application_record__sid:
                        $transition$.params().application_record_sid,
                },
                {
                    orderBy: "send_date",
                    ascending: false,
                },
            );
        },
        applicatorMembership: function (
            GrowerMembershipService,
            applicationRecord,
        ) {
            "ngInject";
            let applicatorMembershipSid =
                applicationRecord.applicator ||
                applicationRecord.assigned_applicator;
            if (!applicatorMembershipSid) {
                return null;
            }

            return GrowerMembershipService.retrieve(applicatorMembershipSid);
        },
    },
    data: {
        title: "routes.SPRAY_APPLICATION_RECORD_TITLE",
        mixpanelEvent: "Viewed grower spray application record",
        breadcrumb: "crumbs.SPRAY_APPLICATION_RECORD",
    },
};

export const growerSprayEndSprayState = {
    name: "app.growers.grower.manageSafety.spray.applicationRecord.endSpray",
    url: "/end",
    component: "growerSprayEndSpray",
    data: {
        title: "routes.SPRAY_APPLICATION_RECORD_END_TITLE",
        mixpanelEvent: "Viewed grower spray end spray",
        breadcrumb: "crumbs.SPRAY_APPLICATION_RECORD_END",
    },
};

export const growerSprayPostSprayState = {
    name: "app.growers.grower.manageSafety.spray.postSpray",
    url: "/application-records/{application_record_sid}/post",
    component: "sprayPostSpray",
    resolve: {
        applicationRecord: function (ApplicationRecordService, $transition$) {
            "ngInject";
            return ApplicationRecordService.retrieve(
                $transition$.params().application_record_sid,
            );
        },
        redirectState: function () {
            "ngInject";
            return growerSprayApplicationRecordState;
        },
        redirectFailedState: function () {
            "ngInject";
            return sprayDashboardState;
        },
    },
    data: {
        title: "routes.SPRAY_POST_SPRAY_TITLE",
        mixpanelEvent: "Viewed grower spray post spray",
        breadcrumb: "crumbs.SPRAY_POST_SPRAY",
    },
};

export const safetyReportListState = {
    name: TRAINING_REPORT_LIST_STATE,
    url: "/training-reports",
    views: { manageSafetyView: { component: "trainingReportList" } },
    resolve: {
        savedReports: function (TrainingReportService, grower) {
            "ngInject";
            return TrainingReportService.list({
                grower_organization__sid: grower.sid,
            });
        },
    },
    redirectTo: (transition) => {
        if (
            transition.from().name === TRAINING_REPORT_STATE &&
            !transition.options().reload
        ) {
            return transition
                .injector()
                .get("$state")
                .target(TRAINING_REPORT_LIST_STATE, transition.params(), {
                    reload: TRAINING_REPORT_LIST_STATE,
                });
        }
    },
    data: {
        title: "routes.SAFETY_REPORT_TITLE",
        permissions: {
            only: [MANAGE_SAFETY],
            redirectTo: previousIfForbidden,
        },
        mixpanelEvent: "Viewed training reports",
        breadcrumb: "crumbs.SAFETY_REPORT",
    },
};

export const safetyReportState = {
    name: TRAINING_REPORT_STATE,
    url: "/{report_sid}",
    params: { report_sid: { value: "new", dynamic: true } },
    views: {
        "manageSafetyView@app.growers.grower.manageSafety": {
            component: "trainingReport",
        },
    },
    resolve: {
        reportSid: function ($transition$) {
            "ngInject";
            return $transition$.params().report_sid;
        },
        reportContext: function (savedReports, $transition$, $translate) {
            "ngInject";
            const saved = savedReports.find(
                (report) => report.sid === $transition$.params().report_sid,
            );
            return {
                savedReports,
                name: saved
                    ? saved.name
                    : $translate.instant("trainingReport.NEW_REPORT"),
            };
        },
    },
    data: {
        title: "routes.SAFETY_REPORT_DETAIL_TITLE",
        permissions: {
            only: [MANAGE_SAFETY],
            redirectTo: previousIfForbidden,
        },
        mixpanelEvent: "Viewed safety report",
        breadcrumb: "crumbs.SAFETY_REPORT_DETAIL",
    },
};

export const legacySafetyReportState = {
    name: "app.growers.grower.manageSafety.trainingReport",
    url: "/training-report?report",
    redirectTo: (transition) => {
        const { report, grower_sid } = transition.params();
        return report
            ? {
                  state: TRAINING_REPORT_STATE,
                  params: { grower_sid, report_sid: report },
              }
            : {
                  state: TRAINING_REPORT_LIST_STATE,
                  params: { grower_sid },
              };
    },
};

export const knowledgeCheckReportState = {
    name: "app.growers.grower.manageSafety.knowledgeCheckReport",
    url: "/knowledge-check-report",
    views: { manageSafetyView: { component: "knowledgeCheckReport" } },
    resolve: {
        topics: function (TrainingReportService, grower, $translate) {
            "ngInject";
            return TrainingReportService.options(
                grower.sid,
                $translate.use(),
            ).then(function (catalog) {
                return catalog.topics;
            });
        },
    },
    data: {
        title: "knowledgeCheckReport.HEADLINE",
        permissions: { only: [MANAGE_SAFETY], redirectTo: previousIfForbidden },
        mixpanelEvent: "Viewed knowledge check report",
        breadcrumb: "knowledgeCheckReport.HEADLINE",
    },
};

export const credentialReportState = {
    name: "app.growers.grower.manageSafety.credentialReport",
    url: "/credential-report",
    views: { manageSafetyView: { component: "credentialReport" } },
    resolve: {
        credentialTypes: function (CredentialTypeService) {
            "ngInject";
            return CredentialTypeService.list({ active: true });
        },
    },
    data: {
        title: "routes.CREDENTIAL_REPORT_TITLE",
        permissions: {
            only: [MANAGE_SAFETY],
            redirectTo: previousIfForbidden,
        },
        mixpanelEvent: "Viewed credential report",
        breadcrumb: "crumbs.CREDENTIAL_REPORT",
    },
};

export const trainingTimeReportState = {
    name: "app.growers.grower.manageSafety.trainingTime",
    url: "/time-report",
    views: { manageSafetyView: { component: "trainingTime" } },
    data: {
        title: "routes.TRAINING_TIME_REPORT_TITLE",
        permissions: {
            only: [MANAGE_SAFETY],
            redirectTo: previousIfForbidden,
        },
        mixpanelEvent: "Viewed training time report",
        breadcrumb: "crumbs.TRAINING_TIME_REPORT",
    },
};

export const downloadWPSAuditExportState = {
    name: "app.growers.grower.manageSafety.downloadWPSAuditExport",
    url: "/download-wps-audit-export",
    views: {
        manageSafetyView: { component: "downloadWPSAuditExport" },
    },
    data: {
        title: "routes.DOWNLOAD_WPS_AUDIT_EXPORT_TITLE",
        permissions: {
            only: [MANAGE_SAFETY],
            redirectTo: previousIfForbidden,
        },
        mixpanelEvent: "Viewed download WPS audit export",
        breadcrumb: "crumbs.DOWNLOAD_WPS_AUDIT_EXPORT",
    },
};

export const commentsState = {
    name: "app.growers.grower.manageCommunication.comments",
    url: "/feedback?important",
    params: {
        important: {
            type: "bool",
            value: true,
        },
    },
    component: "feedbackList",
    resolve: {
        comments: function (CommentService, $transition$) {
            "ngInject";
            return CommentService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                category: WORKER_COMMENT,
                created__gte: moment().subtract(12, "months").toISOString(),
            });
        },
        translations: function (CommentTRANSLATEDService, comments) {
            "ngInject";
            let translationSids = _.flatten(_.map(comments, "translations"));
            return CommentTRANSLATEDService.several(translationSids);
        },
        filteredComments: function (comments, $transition$) {
            "ngInject";
            return _.filter(comments, {
                important: $transition$.params().important,
            });
        },
        feedback: function (filteredComments, translations, $translate) {
            "ngInject";
            return _.map(filteredComments, function (comment) {
                return {
                    comment: comment,
                    toShow: _.find(translations, {
                        comment: comment.sid,
                        language: $translate.use(),
                    }),
                    original: _.find(translations, {
                        comment: comment.sid,
                        original_language: true,
                    }),
                };
            });
        },
    },
    data: {
        title: "routes.SAFETY_COMMENTS_TITLE",
        mixpanelEvent: "Viewed safety comments",
        breadcrumb: "crumbs.SAFETY_COMMENTS",
    },
};

export const accidentLogState = {
    name: "app.growers.grower.manageSafety.accidentLog",
    url: "/accident-log",
    views: { manageSafetyView: { component: "accidentLog" } },
    resolve: {
        reports: function (AccidentReportService, $transition$) {
            "ngInject";
            return AccidentReportService.list({
                farm__grower_organization__sid:
                    $transition$.params().grower_sid,
                archived: false,
            });
        },
        employees: function (GrowerMembershipService, reports, $transition$) {
            "ngInject";
            let employeeUserSids = _.uniq(
                _.compact(_.map(reports, "employee")),
            );
            if (!employeeUserSids.length) {
                return [];
            }
            return GrowerMembershipService.severalByLookup(
                employeeUserSids,
                "user__sid__in",
                {
                    grower_organization__sid: $transition$.params().grower_sid,
                },
            );
        },
        formattedAccidentReports: function (reports, employees) {
            "ngInject";
            return _.map(reports, function (report) {
                return {
                    report: report,
                    employee: getLatestMembership(employees, report.employee),
                };
            });
        },
    },
    data: {
        title: "routes.ACCIDENT_LOG_TITLE",
        mixpanelEvent: "Viewed accident log",
        breadcrumb: "crumbs.ACCIDENT_LOG",
    },
};

export const downloadOSHALogState = {
    name: "app.growers.grower.manageSafety.accidentLog.download",
    url: "/download-osha-log",
    component: "downloadOSHALog",
    data: {
        title: "routes.DOWNLOAD_OSHA_LOG_TITLE",
        mixpanelEvent: "Viewed download accident log",
        breadcrumb: "crumbs.DOWNLOAD_OSHA_LOG",
    },
};

export const viewAccidentReportState = {
    name: "app.growers.grower.manageSafety.accidentLog.view",
    url: "/{report_sid}",
    component: "accidentReport",
    resolve: {
        report: function (AccidentReportService, $transition$) {
            "ngInject";
            return AccidentReportService.retrieve(
                $transition$.params().report_sid,
            );
        },
        translations: function (
            AccidentReportTranslatedService,
            $translate,
            $transition$,
        ) {
            "ngInject";
            return AccidentReportTranslatedService.list({
                report__sid: $transition$.params().report_sid,
            }).then(function (translations) {
                return {
                    display: _.find(translations, [
                        "language",
                        $translate.use(),
                    ]),
                    original: _.find(translations, "original_language"),
                };
            });
        },
        employeeGrowerMembership: function (
            GrowerMembershipService,
            report,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                grower_organization__sid: $transition$.params().grower_sid,
                user__sid: report.employee,
            }).then(
                function (membership) {
                    return membership;
                },
                function () {
                    return null;
                },
            );
        },
        employee: function (employeeGrowerMembership) {
            "ngInject";
            return employeeGrowerMembership;
        },
        authorRaw: function (AccountService, report) {
            "ngInject";
            return AccountService.retrieve(report.author);
        },
        authorGrowerMembership: function (
            GrowerMembershipService,
            report,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                grower_organization__sid: $transition$.params().grower_sid,
                user__sid: report.author,
            }).catch(function () {
                return null;
            });
        },
        author: function (authorRaw, authorGrowerMembership) {
            "ngInject";
            return mapUserWithGrowerMembershipName(
                authorRaw,
                authorGrowerMembership,
            );
        },
        attachments: function (AccidentReportAttachmentService, $transition$) {
            "ngInject";
            return AccidentReportAttachmentService.list({
                accident_report__sid: $transition$.params().report_sid,
            });
        },
        accidentReportNotes: function (
            AccidentReportNoteService,
            $transition$,
        ) {
            "ngInject";
            return AccidentReportNoteService.list({
                accident_report__sid: $transition$.params().report_sid,
            });
        },
        noteTranslations: function (
            AccidentReportNoteTranslatedService,
            accidentReportNotes,
        ) {
            "ngInject";
            let translationSids = _.flatten(
                _.map(accidentReportNotes, "translations"),
            );
            if (_.isEmpty(translationSids)) {
                return [];
            }
            return AccidentReportNoteTranslatedService.several(translationSids);
        },
        noteAuthors: function (AccountService, accidentReportNotes) {
            "ngInject";
            let authorSids = _.map(accidentReportNotes, "author");
            if (_.isEmpty(authorSids)) {
                return [];
            }
            return AccountService.several(authorSids);
        },
        notes: function (
            accidentReportNotes,
            noteTranslations,
            $translate,
            noteAuthors,
        ) {
            "ngInject";
            return _.map(accidentReportNotes, function (note) {
                let noteTranslationSids = _.get(note, "translations", []);
                return {
                    note,
                    toShow: _.find(noteTranslations, function (translation) {
                        return (
                            _.includes(noteTranslationSids, translation.sid) &&
                            translation.language === $translate.use()
                        );
                    }),
                    original: _.find(noteTranslations, function (translation) {
                        return (
                            _.includes(noteTranslationSids, translation.sid) &&
                            translation.original_language
                        );
                    }),
                    author: _.find(noteAuthors, [SID_LOOKUP, note.author]),
                };
            });
        },
        editState: function () {
            "ngInject";
            return accidentReportMetaInformationState;
        },
        notesState: function () {
            "ngInject";
            return accidentReportNoteCreateState;
        },
        archiveRedirectState: function () {
            "ngInject";
            return accidentLogState;
        },
        memberState: function () {
            "ngInject";
            return memberState;
        },
    },
    data: {
        title: "routes.VIEW_ACCIDENT_REPORT_TITLE",
        mixpanelEvent: "Viewed accident report",
        breadcrumb: "crumbs.VIEW_ACCIDENT_REPORT",
    },
};

export const uploadAccidentReportAttachmentsState = {
    name: "app.growers.grower.manageSafety.accidentLog.view.uploadAttachments",
    url: "/upload-attachments",
    component: "uploadAccidentReportAttachments",
    resolve: {
        redirectState: function () {
            "ngInject";
            return viewAccidentReportState;
        },
    },
    data: {
        title: "routes.UPLOAD_ACCIDENT_REPORT_ATTACHMENTS_TITLE",
        mixpanelEvent: "Viewed upload accident report attachments",
        breadcrumb: "crumbs.UPLOAD_ACCIDENT_REPORT_ATTACHMENTS",
    },
};

export const accidentReportNoteCreateState = {
    name: "app.growers.grower.manageSafety.accidentLog.view.note",
    url: "/add-note",
    component: "accidentReportNoteCreate",
    data: {
        title: "routes.ACCIDENT_REPORT_NOTE_CREATE_TITLE",
        mixpanelEvent: "Viewed accident report note create",
        breadcrumb: "crumbs.ACCIDENT_REPORT_NOTE_CREATE",
    },
};

export const createAccidentReportState = {
    name: "app.growers.grower.manageSafety.accidentLog.new",
    url: "/new-accident-report",
    component: "createAccidentReport",
    resolve: {
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        users: function (AccountService, growerMemberships) {
            "ngInject";
            return AccountService.several(_.map(growerMemberships, "user"));
        },
        employees: function (growerMemberships, users) {
            "ngInject";
            return _.compact(
                _.map(users, function (user) {
                    return getLatestMembership(growerMemberships, user.sid);
                }),
            );
        },
        nextState: function () {
            "ngInject";
            return accidentReportMetaInformationState;
        },
    },
    data: {
        title: "routes.CREATE_ACCIDENT_REPORT_TITLE",
        mixpanelEvent: "Viewed new accident report",
        breadcrumb: "crumbs.CREATE_ACCIDENT_REPORT",
    },
};

export const createAccidentReportBaseState = {
    name: "app.growers.grower.manageSafety.accidentLog.view.base",
    abstract: true,
    component: "accidentReportEditBase",
    redirectTo: viewAccidentReportState,
};

export const accidentReportMetaInformationState = {
    name: "app.growers.grower.manageSafety.accidentLog.view.base.metaInformation",
    url: "/basic-information",
    component: "accidentReportMetaInformation",
    resolve: {
        prefillData: function (report) {
            "ngInject";
            return report.prefill();
        },
        nextState: function () {
            "ngInject";
            return accidentReportEmployeeInformationState;
        },
    },
    data: {
        title: "routes.ACCIDENT_REPORT_META_INFORMATION_TITLE",
        mixpanelEvent: "Viewed accident report basic information",
    },
};

export const accidentReportEmployeeInformationState = {
    name: "app.growers.grower.manageSafety.accidentLog.view.base.employeeInformation",
    url: "/employee-information",
    component: "accidentReportEmployeeInformation",
    resolve: {
        prefillData: function (report) {
            "ngInject";
            return report.prefill();
        },
        nextState: function () {
            "ngInject";
            return accidentReportAccidentInformationState;
        },
        reloadState: function () {
            "ngInject";
            return viewAccidentReportState;
        },
    },
    data: {
        title: "routes.ACCIDENT_REPORT_EMPLOYEE_INFORMATION_TITLE",
        mixpanelEvent: "Viewed accident report employee information",
    },
};

export const accidentReportAccidentInformationState = {
    name: "app.growers.grower.manageSafety.accidentLog.view.base.accidentInformation",
    url: "/accident-information",
    component: "accidentReportAccidentInformation",
    resolve: {
        nextState: function () {
            "ngInject";
            return accidentReportHealthCareInformationState;
        },
        reloadState: function () {
            "ngInject";
            return viewAccidentReportState;
        },
    },
    data: {
        title: "routes.ACCIDENT_REPORT_ACCIDENT_INFORMATION_TITLE",
        mixpanelEvent: "Viewed accident report accident information",
    },
};

export const accidentReportHealthCareInformationState = {
    name: "app.growers.grower.manageSafety.accidentLog.view.base.healthcareInformation",
    url: "/healthcare-information",
    component: "accidentReportHealthCareInformation",
    resolve: {
        nextState: function () {
            "ngInject";
            return viewAccidentReportState;
        },
        reloadState: function () {
            "ngInject";
            return viewAccidentReportState;
        },
    },
    data: {
        title: "routes.ACCIDENT_REPORT_HEALTH_CARE_INFORMATION_TITLE",
        mixpanelEvent: "Viewed accident report health care information",
    },
};

export const safetyMeetingsState = {
    name: "app.growers.grower.manageSafety.meetings",
    url: "/safety-meetings?search?category?delivery_type?status?crew",
    params: {
        order_by: {
            value: "start_date",
            squash: true,
        },
        search: {
            dynamic: true,
        },
        status: {
            value: IN_PROGRESS_MEETING_STATUS,
        },
        crew: {
            array: true,
        },
    },
    views: { manageSafetyView: { component: "meetingList" } },
    resolve: {
        safetyMeetingTopics: function (
            SafetyMeetingTopicService,
            $transition$,
        ) {
            "ngInject";
            return SafetyMeetingTopicService.list({
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        courses: function (SafetyCourseService, $transition$) {
            "ngInject";
            return SafetyCourseService.list({
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        meetingsRaw: function (SafetyMeetingService, $transition$) {
            "ngInject";
            return SafetyMeetingService.list({
                organizer__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        groups: function (GroupService, meetingsRaw) {
            "ngInject";
            return GroupService.several(_.flatten(_.map(meetingsRaw, "crews")));
        },
        meetings: function (
            SafetyMeetingService,
            moment,
            $transition$,
            safetyMeetingTopics,
            courses,
            meetingsRaw,
            groups,
        ) {
            "ngInject";
            let meetings = _.map(meetingsRaw, function (meeting) {
                return {
                    topics: _.filter(safetyMeetingTopics, function (topic) {
                        return _.includes(meeting.topics, topic.sid);
                    }),
                    meeting: meeting,
                    courses: _.filter(courses, function (course) {
                        return _.includes(meeting.courses, course.sid);
                    }),
                    crews: _.filter(groups, function (group) {
                        return _.includes(meeting.crews, group.sid);
                    }),
                };
            });
            return _.map(meetings, function (meeting) {
                meeting.meeting.is_future = moment(
                    meeting.meeting.start_date,
                ).isAfter(moment());
                return meeting;
            });
        },
    },
    data: {
        title: "routes.SAFETY_MEETINGS_TITLE",
        mixpanelEvent: "Viewed safety meeting list",
        breadcrumb: "crumbs.SAFETY_MEETINGS",
    },
};

export const safetyMeetingNotificationSettingsState = {
    name: "app.growers.grower.manageSafety.meetings.notifications",
    url: "/notifications",
    component: "notificationSettings",
    resolve: getNotificationSettingsResolves(SUGGEST_TO_MOVE_TO_IN_PERSON),
    data: {
        title: "routes.SAFETY_MEETING_NOTIFICATIONS_TITLE",
        breadcrumb: "crumbs.SAFETY_MEETING_NOTIFICATIONS",
        mixpanelEvent: "Viewed safety meeting notification settings",
        permissions: { only: [MANAGE_SAFETY], redirectTo: previousIfForbidden },
    },
};

export const topicsState = {
    name: "app.growers.grower.manageSafety.topics",
    url: "/topics",
    views: { manageSafetyView: { component: "topicList" } },
    resolve: {
        administrators: function (TradeOrganizationService, $transition$) {
            "ngInject";
            return TradeOrganizationService.list({
                growerorganization__sid: $transition$.params().grower_sid,
            });
        },
        topics: function (SafetyMeetingTopicService, $transition$) {
            "ngInject";
            return SafetyMeetingTopicService.list({
                active: true,
                accessor__sid: $transition$.params().grower_sid,
                calculate_popularity: true,
            });
        },
        subjects: function (SubjectService, $transition$) {
            "ngInject";
            return SubjectService.list();
        },
    },
    data: {
        title: "routes.TOPICS_TITLE",
        mixpanelEvent: "Viewed topics list",
        breadcrumb: "crumbs.TOPICS",
    },
};

export const viewTopicState = {
    name: "app.growers.grower.manageSafety.topics.view",
    url: "/{topic_sid}",
    component: "safetyMeetingTopic",
    resolve: {
        topic: function (SafetyMeetingTopicService, $transition$) {
            "ngInject";
            return SafetyMeetingTopicService.retrieve(
                $transition$.params().topic_sid,
            );
        },
        translateds: function (
            SafetyMeetingTopicTRANSLATEDService,
            $transition$,
        ) {
            "ngInject";
            return SafetyMeetingTopicTRANSLATEDService.list({
                safety_meeting_topic__sid: $transition$.params().topic_sid,
            });
        },
        courseMembers: function (CourseMemberService, $transition$) {
            "ngInject";
            return CourseMemberService.list({
                active: true,
                topic__sid: $transition$.params().topic_sid,
            });
        },
        courses: function (SafetyCourseService, courseMembers, $transition$) {
            "ngInject";
            return SafetyCourseService.list({
                active: true,
                accessor__sid: $transition$.params().grower_sid,
            }).then(function (courses) {
                let courseMembersByCourseSid = _.keyBy(courseMembers, "course");
                return _.filter(courses, function (course) {
                    return !!courseMembersByCourseSid[course.sid];
                });
            });
        },
        administrator: function (TradeOrganizationService, topic) {
            "ngInject";
            if (topic.administrator) {
                return TradeOrganizationService.retrieve(topic.administrator);
            }
        },
    },
    data: {
        title: "crumbs.VIEW_TOPIC",
        mixpanelEvent: "Viewed topic",
        breadcrumb: "crumbs.VIEW_TOPIC",
    },
};

export const addToCourseState = {
    name: "app.growers.grower.manageSafety.topics.addToCourse",
    url: "/add-to-course?topic_sid",
    component: "addToCourse",
    resolve: {
        topic: function (SafetyMeetingTopicService, $transition$) {
            "ngInject";
            return SafetyMeetingTopicService.retrieve(
                $transition$.params().topic_sid,
            );
        },
        topics: function (SafetyMeetingTopicService, $transition$) {
            "ngInject";
            return SafetyMeetingTopicService.list({
                active: true,
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        courses: function (SafetyCourseService, $transition$) {
            "ngInject";
            return SafetyCourseService.list({
                active: true,
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        courseMembers: function (CourseMemberService, courses) {
            "ngInject";
            return CourseMemberService.list({
                active: true,
                course__sid__in: _.map(courses, "sid").join(","),
            });
        },
    },
    data: {
        title: "routes.ADD_TO_COURSE_TITLE",
        mixpanelEvent: "Viewed add to course",
        breadcrumb: "crumbs.ADD_TO_COURSE",
    },
};

export const topicCreateState = {
    name: "app.growers.grower.manageSafety.topics.create",
    url: "/create",
    component: "topicCreate",
    data: {
        title: "routes.TOPIC_CREATE_TITLE",
        mixpanelEvent: "Viewed training topic create",
        breadcrumb: "crumbs.TOPIC_CREATE",
    },
};

export const topicEditState = {
    name: "app.growers.grower.manageSafety.topics.view.edit",
    url: "/edit",
    component: "topicEdit",
    resolve: {
        topic: function ($transition$, SafetyMeetingTopicService) {
            "ngInject";
            return SafetyMeetingTopicService.retrieve(
                $transition$.params().topic_sid,
            );
        },
        translateds: function (
            $transition$,
            SafetyMeetingTopicTRANSLATEDService,
        ) {
            "ngInject";
            return SafetyMeetingTopicTRANSLATEDService.list({
                safety_meeting_topic__sid: $transition$.params().topic_sid,
            });
        },
    },
    data: {
        title: "routes.TOPIC_EDIT_TITLE",
        mixpanelEvent: "Viewed topic edit",
        breadcrumb: "crumbs.TOPIC_EDIT",
    },
};

export const assessmentObjectivesState = {
    name: "app.growers.grower.manageSafety.topics.view.objectives",
    url: "/objectives",
    component: "assessmentObjectives",
    resolve: {
        topic: function ($transition$, SafetyMeetingTopicService) {
            "ngInject";
            return SafetyMeetingTopicService.retrieve(
                $transition$.params().topic_sid,
            );
        },
        editable: function () {
            return false;
        },
    },
    data: {
        title: "routes.ASSESSMENT_OBJECTIVES_TITLE",
        breadcrumb: "crumbs.ASSESSMENT_OBJECTIVES",
        mixpanelEvent: "Viewed knowledge check objectives",
        permissions: { only: [MANAGE_SAFETY], redirectTo: previousIfForbidden },
    },
};

export const assessmentObjectiveCreateState = {
    name: "app.growers.grower.manageSafety.topics.view.createObjective",
    url: "/objectives/add",
    component: "assessmentObjectives",
    resolve: {
        ...assessmentObjectivesState.resolve,
        editable: function () {
            return true;
        },
        objective: function () {
            return null;
        },
    },
    data: {
        ...assessmentObjectivesState.data,
        title: "routes.CREATE_ASSESSMENT_OBJECTIVE_TITLE",
        breadcrumb: "crumbs.CREATE_ASSESSMENT_OBJECTIVE",
        mixpanelEvent: "Viewed knowledge check objective create",
    },
};

export const assessmentObjectiveEditState = {
    name: "app.growers.grower.manageSafety.topics.view.editObjective",
    url: "/objectives/:objective_sid/edit",
    component: "assessmentObjectives",
    resolve: {
        ...assessmentObjectivesState.resolve,
        editable: function () {
            return true;
        },
        objective: function ($transition$, topic, AssessmentObjectiveService) {
            "ngInject";
            return AssessmentObjectiveService.retrieve(
                $transition$.params().objective_sid,
                { training__sid: topic.sid },
            );
        },
    },
    data: {
        ...assessmentObjectivesState.data,
        title: "routes.EDIT_ASSESSMENT_OBJECTIVE_TITLE",
        breadcrumb: "crumbs.EDIT_ASSESSMENT_OBJECTIVE",
        mixpanelEvent: "Viewed knowledge check objective edit",
    },
};

export const courseListState = {
    name: "app.growers.grower.manageSafety.courses",
    url: "/courses",
    views: { manageSafetyView: { component: "courseList" } },
    resolve: {
        topics: function (SafetyMeetingTopicService, $transition$) {
            "ngInject";
            return SafetyMeetingTopicService.list({
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        administrators: function (TradeOrganizationService, $transition$) {
            "ngInject";
            return TradeOrganizationService.list({
                growerorganization__sid: $transition$.params().grower_sid,
            });
        },
        courses: function (SafetyCourseService, $transition$) {
            "ngInject";
            return SafetyCourseService.list({
                active: true,
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        courseMembers: function (CourseMemberService, courses) {
            "ngInject";
            return CourseMemberService.list({
                active: true,
                course__sid__in: _.map(courses, "sid").join(","),
            });
        },
    },
    data: {
        title: "routes.COURSE_LIST_TITLE",
        mixpanelEvent: "Viewed courses list",
        breadcrumb: "crumbs.COURSE_LIST",
    },
};

export const safetyMeetingCreateState = {
    name: "app.growers.grower.manageSafety.createMeeting",
    url: "/create?category?courses?topics",
    params: {
        additional_participants: {
            array: true,
            value: [],
        },
        courses: {
            array: true,
            value: [],
        },
        topics: {
            array: true,
            value: [],
        },
        //     category: {
        //         value: SAFE,
        //     },
    },
    views: { manageSafetyView: { component: "createSafetyMeeting" } },
    resolve: {
        groups: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        topics: function (SafetyMeetingTopicService, $transition$) {
            "ngInject";
            return SafetyMeetingTopicService.list({
                active: true,
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        administrators: function (TradeOrganizationService, $transition$) {
            "ngInject";
            return TradeOrganizationService.list({
                growerorganization__sid: $transition$.params().grower_sid,
            });
        },
        topicData: function (topics, administrators) {
            "ngInject";
            return _.map(topics, function (topic) {
                return {
                    topic: topic,
                    administrator: _.find(administrators, [
                        SID_LOOKUP,
                        topic.administrator,
                    ]),
                };
            });
        },
        learningGoals: function (topics, AssessmentObjectiveService) {
            "ngInject";
            return AssessmentObjectiveService.list({
                training__sid__in: _.map(topics, SID_LOOKUP).join(","),
                active: true,
                status: OBJECTIVES_READY,
            });
        },
        courses: function (SafetyCourseService, $transition$) {
            "ngInject";
            return SafetyCourseService.list({
                active: true,
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        courseMembers: function (CourseMemberService, courses) {
            "ngInject";
            return CourseMemberService.list({
                active: true,
                course__sid__in: _.map(courses, "sid").join(","),
            });
        },
        courseData: function (courses, administrators, topics, courseMembers) {
            "ngInject";
            let courseMembersByCourse = _.groupBy(courseMembers, "course");
            let topicsBySid = _.keyBy(topics, "sid");
            return _.map(courses, function (course) {
                let members = _.orderBy(
                    courseMembersByCourse[course.sid] || [],
                    "order",
                );
                return {
                    course: course,
                    courseMembers: members,
                    administrator: _.find(administrators, [
                        SID_LOOKUP,
                        course.administrator,
                    ]),
                    topics: _.compact(
                        _.map(members, function (member) {
                            return topicsBySid[member.topic];
                        }),
                    ),
                };
            });
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        employees: function (AccountService, growerMemberships) {
            "ngInject";
            return AccountService.several(_.map(growerMemberships, "user"));
        },
        workers: function (employees, growerMemberships) {
            "ngInject";
            return _.compact(
                _.map(employees, function (user) {
                    return getLatestMembership(growerMemberships, user.sid);
                }),
            );
        },
        newMeeting: function ($transition$) {
            "ngInject";
            return {
                timetable: NOW,
                delivery_type: REMOTE,
                category: $transition$.params().category || GENERAL_TRAINING,
                additional_participants:
                    $transition$.params().additional_participants,
                crews: [],
                topics: $transition$.params().topics,
                courses: $transition$.params().courses,
            };
        },
        wpsCredentials: function ($transition$, CredentialService) {
            "ngInject";
            return CredentialService.list({
                archived: false,
                credential_type__wps_qualified: true,
                expiration_date__gt: moment().format(DATE_FORMAT),
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        wpsCredentialedUsers: function (wpsCredentials, AccountService) {
            "ngInject";
            let wpsTrainerUserSids = _.map(wpsCredentials, "subject");
            return AccountService.several(wpsTrainerUserSids);
        },
        wpsCredentialedGrowerMemberships: function (
            $transition$,
            wpsCredentials,
            GrowerMembershipService,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                user__sid__in: _.join(_.map(wpsCredentials, "subject")),
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        wpsTrainers: function (
            wpsCredentialedUsers,
            wpsCredentialedGrowerMemberships,
        ) {
            "ngInject";
            return _.compact(
                _.map(wpsCredentialedUsers, function (user) {
                    return _.find(wpsCredentialedGrowerMemberships, [
                        "user",
                        user.sid,
                    ]);
                }),
            );
        },
    },
    data: {
        title: "routes.SAFETY_MEETING_CREATE_TITLE",
        mixpanelEvent: "Viewed safety meeting create",
        breadcrumb: "crumbs.SAFETY_MEETING_CREATE",
    },
};

export const safetyMeetingEditState = {
    name: "app.growers.grower.manageSafety.editMeeting",
    url: "/{meeting_sid}/edit",
    views: { manageSafetyView: { component: "editSafetyMeeting" } },
    resolve: {
        groups: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        meeting: function ($transition$, SafetyMeetingService) {
            "ngInject";
            return SafetyMeetingService.retrieve(
                $transition$.params().meeting_sid,
            );
        },
        topics: function (SafetyMeetingTopicService, $transition$) {
            "ngInject";
            return SafetyMeetingTopicService.list({
                active: true,
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        administrators: function (TradeOrganizationService, $transition$) {
            "ngInject";
            return TradeOrganizationService.list({
                growerorganization__sid: $transition$.params().grower_sid,
            });
        },
        topicData: function (topics, administrators) {
            "ngInject";
            return _.map(topics, function (topic) {
                return {
                    topic: topic,
                    administrator: _.find(administrators, [
                        SID_LOOKUP,
                        topic.administrator,
                    ]),
                };
            });
        },
        learningGoals: function (topics, AssessmentObjectiveService) {
            "ngInject";
            return AssessmentObjectiveService.list({
                training__sid__in: _.map(topics, SID_LOOKUP).join(","),
                active: true,
                status: OBJECTIVES_READY,
            });
        },
        courses: function (SafetyCourseService, $transition$) {
            "ngInject";
            return SafetyCourseService.list({
                active: true,
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        courseMembers: function (CourseMemberService, courses) {
            "ngInject";
            return CourseMemberService.list({
                active: true,
                course__sid__in: _.map(courses, "sid").join(","),
            });
        },
        courseData: function (courses, administrators, topics, courseMembers) {
            "ngInject";
            let courseMembersByCourse = _.groupBy(courseMembers, "course");
            let topicsBySid = _.keyBy(topics, "sid");
            return _.map(courses, function (course) {
                let members = _.orderBy(
                    courseMembersByCourse[course.sid] || [],
                    "order",
                );
                return {
                    course: course,
                    courseMembers: members,
                    administrator: _.find(administrators, [
                        SID_LOOKUP,
                        course.administrator,
                    ]),
                    topics: _.compact(
                        _.map(members, function (member) {
                            return topicsBySid[member.topic];
                        }),
                    ),
                };
            });
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        employees: function (AccountService, growerMemberships) {
            "ngInject";
            return AccountService.several(_.map(growerMemberships, "user"));
        },
        workers: function (employees, growerMemberships) {
            "ngInject";
            return _.compact(
                _.map(employees, function (user) {
                    return getLatestMembership(growerMemberships, user.sid);
                }),
            );
        },
        wpsCredentials: function ($transition$, CredentialService) {
            "ngInject";
            return CredentialService.list({
                archived: false,
                credential_type__wps_qualified: true,
                expiration_date__gt: moment().format(DATE_FORMAT),
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        wpsCredentialedUsers: function (
            $transition$,
            wpsCredentials,
            AccountService,
        ) {
            "ngInject";
            let wpsTrainerUserSids = _.map(wpsCredentials, "subject");
            return AccountService.several(wpsTrainerUserSids);
        },
        wpsCredentialedGrowerMemberships: function (
            $transition$,
            wpsCredentials,
            GrowerMembershipService,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                user__sid__in: _.join(_.map(wpsCredentials, "subject")),
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        wpsTrainers: function (
            wpsCredentialedUsers,
            wpsCredentialedGrowerMemberships,
        ) {
            "ngInject";
            return _.compact(
                _.map(wpsCredentialedUsers, function (user) {
                    return _.find(wpsCredentialedGrowerMemberships, [
                        "user",
                        user.sid,
                    ]);
                }),
            );
        },
    },
    redirectTo: (trans) => {
        let meetingResolve = trans.injector().getAsync("meeting");
        return meetingResolve.then((meeting) => {
            if (moment(meeting.start_date) < moment()) {
                let $state = trans.injector().get("$state");
                return $state.target(
                    safetyMeetingsState,
                    { grower_sid: meeting.organizer },
                    { reload: safetyMeetingsState },
                );
            }
        });
    },
    data: {
        title: "routes.SAFETY_MEETING_EDIT_TITLE",
        mixpanelEvent: "Viewed safety meeting edit",
        breadcrumb: "crumbs.SAFETY_MEETING_EDIT",
    },
};

export const manageSafetyMeetingState = {
    name: "app.growers.grower.manageSafety.meetings.manage",
    url: "/{meeting_sid}",
    component: "manageMeeting",
    resolve: {
        meeting: function ($transition$, SafetyMeetingService) {
            "ngInject";
            return SafetyMeetingService.retrieve(
                $transition$.params().meeting_sid,
            );
        },
        topics: function (SafetyMeetingTopicService, meeting) {
            "ngInject";
            return SafetyMeetingTopicService.several(meeting.topics);
        },
        courses: function (SafetyCourseService, meeting) {
            "ngInject";
            if (!meeting.courses || !meeting.courses.length) {
                return [];
            }
            return SafetyCourseService.several(meeting.courses);
        },
        requests: function (SafetyMeetingRequestService, $transition$) {
            "ngInject";
            return SafetyMeetingRequestService.list({
                safety_meeting__sid: $transition$.params().meeting_sid,
            });
        },
        assessmentResults: function (
            SafetyMeetingRequestService,
            grower,
            meeting,
            requests,
        ) {
            "ngInject";
            if (!requests.length) {
                return [];
            }
            let results = [];
            function loadPage(page) {
                return SafetyMeetingRequestService.knowledgeCheckReport({
                    requester__sid: grower.sid,
                    safety_meeting__sid: meeting.sid,
                    page: page,
                }).then(function (report) {
                    results.push(...report.results);
                    return report.next ? loadPage(page + 1) : results;
                });
            }
            return loadPage(1);
        },
        participants: function (requests, AccountService) {
            "ngInject";
            return AccountService.several(_.map(requests, "subject"));
        },
        crews: function (GroupService, meeting) {
            "ngInject";
            return GroupService.several(meeting.crews);
        },
        targetParticipants: function (
            meeting,
            MembershipService,
            crews,
            $q,
            AccountService,
        ) {
            "ngInject";
            let pastMembers = MembershipService.list({
                group__sid__in: meeting.crews,
                created_before: meeting.start_date,
                end_date_after: meeting.start_date,
                active: false,
            });
            let currentMembers = MembershipService.list({
                group__sid__in: meeting.crews,
                created_before: meeting.start_date,
                end_date__isnull: true,
                active: true,
            });
            return $q
                .all([pastMembers, currentMembers])
                .then(_.flatten)
                .then(function (responses) {
                    return _.map(responses, "user");
                })
                .then(_.uniq)
                .then(function (userSids) {
                    return AccountService.several(userSids);
                });
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        trainers: function (meeting, growerMemberships) {
            "ngInject";
            return _.filter(growerMemberships, function (membership) {
                return _.includes(meeting.trainers, membership.sid);
            });
        },
        scheduler: function (meeting, growerMemberships) {
            "ngInject";
            return _.find(growerMemberships, ["user", meeting.scheduler]);
        },
        nonParticipants: function (
            participants,
            targetParticipants,
            growerMemberships,
        ) {
            "ngInject";
            let users = _.differenceBy(targetParticipants, participants, "sid");
            return _.map(users, function (user) {
                return {
                    user: user,
                    growerMembership: getLatestMembership(
                        growerMemberships,
                        user.sid,
                    ),
                };
            });
        },
        signatures: function (SignatureService, requests) {
            "ngInject";
            return SignatureService.several(_.map(requests, "signature"));
        },
        requestStatuses: function (
            requests,
            participants,
            crews,
            growerMemberships,
            signatures,
        ) {
            "ngInject";
            return _.map(participants, function (participant) {
                let participantsRequests = _.filter(requests, [
                    "subject",
                    participant.sid,
                ]);
                for (let request of participantsRequests) {
                    request.signature = _.find(signatures, [
                        SID_LOOKUP,
                        request.signature,
                    ]);
                }
                return {
                    requests: participantsRequests,
                    participant: participant,
                    growerMembership: getLatestMembership(
                        growerMemberships,
                        participant.sid,
                    ),
                    pending: _.some(participantsRequests, [
                        "status",
                        SAFETY_PENDING,
                    ]),
                    complete: _.every(participantsRequests, [
                        "status",
                        COMPLETE,
                    ]),
                    cancelled: _.every(participantsRequests, [
                        "status",
                        CANCELLED,
                    ]),
                };
            });
        },
        pending: function (requestStatuses) {
            "ngInject";
            return _.filter(requestStatuses, "pending");
        },
        completed: function (requestStatuses) {
            "ngInject";
            return _.filter(requestStatuses, "complete");
        },
        cancelled: function (requestStatuses) {
            "ngInject";
            return _.filter(requestStatuses, "cancelled");
        },
        allComments: function (CommentService, $transition$) {
            "ngInject";
            return CommentService.list({
                safety_meeting__sid: $transition$.params().meeting_sid,
            });
        },
        comments: function (allComments) {
            "ngInject";
            return _.filter(allComments, ["category", WORKER_COMMENT]);
        },
        translations: function (CommentTRANSLATEDService, comments) {
            "ngInject";
            let translationSids = _.flatten(_.map(comments, "translations"));
            return CommentTRANSLATEDService.several(translationSids);
        },
        feedback: function (comments, translations, $translate) {
            "ngInject";
            let feedback = {
                important: [],
                hidden: [],
            };
            for (let comment of comments) {
                let reformattedComment = {
                    comment: comment,
                    toShow: _.find(translations, {
                        comment: comment.sid,
                        language: $translate.use(),
                    }),
                    original: _.find(translations, {
                        comment: comment.sid,
                        original_language: true,
                    }),
                };
                if (comment.important) {
                    feedback.important.push(reformattedComment);
                } else {
                    feedback.hidden.push(reformattedComment);
                }
            }
            return feedback;
        },
        notesRaw: function (allComments) {
            "ngInject";
            return _.filter(allComments, ["category", NOTE]);
        },
        notesTranslations: function (CommentTRANSLATEDService, notesRaw) {
            "ngInject";
            let translationSids = _.flatten(_.map(notesRaw, "translations"));
            return CommentTRANSLATEDService.several(translationSids);
        },
        notesData: function (notesRaw, notesTranslations, $translate) {
            "ngInject";
            return _.map(notesRaw, function (comment) {
                return {
                    comment: comment,
                    toShow: _.find(notesTranslations, {
                        comment: comment.sid,
                        language: $translate.use(),
                    }),
                    original: _.find(notesTranslations, {
                        comment: comment.sid,
                        original_language: true,
                    }),
                };
            });
        },
        wpsTrainer: function (meeting, growerMemberships) {
            "ngInject";
            return _.find(growerMemberships, ["user", meeting.wps_trainer]);
        },
    },
    data: {
        title: "routes.MANAGE_SAFETY_MEETING_TITLE",
        mixpanelEvent: "Viewed manage safety meeting",
        breadcrumb: "crumbs.MANAGE_SAFETY_MEETING",
    },
};

export const safetyMeetingDownloadState = {
    name: "app.growers.grower.manageSafety.meetings.manage.download",
    url: "/download",
    component: "downloadMeetingReport",
    data: {
        title: "routes.SAFETY_MEETING_DOWNLOAD_TITLE",
        mixpanelEvent: "Viewed safety meeting download",
        breadcrumb: "crumbs.SAFETY_MEETING_DOWNLOAD",
    },
};

export const safetyMeetingFinishState = {
    name: "app.growers.grower.manageSafety.meetings.manage.finish",
    url: "/finish",
    component: "safetyMeetingFinish",
    data: {
        title: "routes.SAFETY_MEETING_FINISH_TITLE",
        mixpanelEvent: "Viewed safety meeting finish",
        breadcrumb: "crumbs.SAFETY_MEETING_FINISH",
    },
};

export const safetyMeetingMessageState = {
    name: "app.growers.grower.manageSafety.meetings.manage.message",
    url: "/message",
    component: "safetyMeetingMessage",
    data: {
        title: "routes.SAFETY_MEETING_MESSAGE_TITLE",
        mixpanelEvent: "Viewed safety meeting message",
        breadcrumb: "crumbs.SAFETY_MEETING_MESSAGE",
    },
};

export const sendCrewBossAttendanceLinkState = {
    name: "app.growers.grower.manageSafety.meetings.manage.sendCrewBossAttendanceLink",
    url: "/crew-boss-attendance",
    component: "sendCrewBossAttendanceLink",
    resolve: {
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        employees: function (AccountService, growerMemberships) {
            "ngInject";
            return AccountService.several(_.map(growerMemberships, "user"));
        },
        workers: function (employees, growerMemberships) {
            "ngInject";
            return _.compact(
                _.map(employees, function (user) {
                    return getLatestMembership(growerMemberships, user.sid);
                }),
            );
        },
    },
    data: {
        title: "routes.SAFETY_MEETING_SEND_CREW_BOSS_ATTENDANCE_TITLE",
        mixpanelEvent: "Viewed send attendance link to supervisors",
    },
};

export const safetyMeetingAddParticipantState = {
    name: "app.growers.grower.manageSafety.meetings.manage.addParticipant",
    url: "/add-participant",
    component: "addParticipant",
    resolve: {
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        employees: function (AccountService, growerMemberships) {
            "ngInject";
            return AccountService.several(_.map(growerMemberships, "user"));
        },
        workers: function (employees, growerMemberships) {
            "ngInject";
            return _.compact(
                _.map(employees, function (user) {
                    return getLatestMembership(growerMemberships, user.sid);
                }),
            );
        },
    },
    data: {
        title: "routes.SAFETY_MEETING_ADD_PARTICIPANT_TITLE",
        mixpanelEvent: "Viewed add participant to safety meeting",
        breadcrumb: "crumbs.SAFETY_MEETING_ADD_PARTICIPANT",
    },
};

export const safetyMeetingScanAttendanceState = {
    name: "app.growers.grower.manageSafety.meetings.manage.scanAttendance",
    url: "/scan-attendance",
    component: "scanAttendance",
    resolve: {
        redirectState: function () {
            "ngInject";
            return manageSafetyMeetingState;
        },
        manualState: function () {
            "ngInject";
            return safetyMeetingManualAttendanceState;
        },
    },
    data: {
        title: "routes.SAFETY_MEETING_TAKE_ATTENDANCE_TITLE",
        mixpanelEvent: "Viewed take attendance to safety meeting",
        breadcrumb: "crumbs.SAFETY_MEETING_TAKE_ATTENDANCE",
    },
};

export const safetyMeetingManualAttendanceState = {
    name: "app.growers.grower.manageSafety.meetings.manage.manualAttendance",
    url: "/manual-attendance",
    component: "manualAttendance",
    resolve: {
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        employees: function (AccountService, growerMemberships) {
            "ngInject";
            return AccountService.several(_.map(growerMemberships, "user"));
        },
        workers: function (employees, growerMemberships) {
            "ngInject";
            return _.compact(
                _.map(employees, function (user) {
                    return getLatestMembership(growerMemberships, user.sid);
                }),
            );
        },
        redirectState: function () {
            "ngInject";
            return manageSafetyMeetingState;
        },
        searchState: function () {
            "ngInject";
            return safetyMeetingScanAttendanceState;
        },
    },
    data: {
        title: "routes.SAFETY_MEETING_BACKUP_SEARCH_TITLE",
        mixpanelEvent: "Viewed safety meeting manual attendance",
        breadcrumb: "crumbs.SAFETY_MEETING_BACKUP_SEARCH",
    },
};

export const safetyMeetingRequestSignState = {
    name: "app.growers.grower.manageSafety.meetings.manage.sign",
    url: "/{participant_sid}/sign",
    component: "safetyMeetingSign",
    resolve: {
        requests: function ($transition$, SafetyMeetingRequestService) {
            "ngInject";
            return SafetyMeetingRequestService.list({
                subject__sid: $transition$.params().participant_sid,
                safety_meeting__sid: $transition$.params().meeting_sid,
            });
        },
        participant: function ($transition$, AccountService) {
            "ngInject";
            return AccountService.retrieve(
                $transition$.params().participant_sid,
            );
        },
        participantGrowerMembership: function (
            $transition$,
            GrowerMembershipService,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                grower_organization__sid: $transition$.params().grower_sid,
                user__sid: $transition$.params().participant_sid,
            }).then(
                function (membership) {
                    return membership;
                },
                function () {
                    return null;
                },
            );
        },
    },
    data: {
        title: "routes.SAFETY_MEETING_REQUEST_SIGN_TITLE",
        mixpanelEvent: "Viewed safety meeting sign",
        breadcrumb: "crumbs.SAFETY_MEETING_REQUEST_SIGN",
    },
};

export const bulkCompleteSafetyMeetingRequestsState = {
    name: "app.growers.grower.manageSafety.meetings.manage.bulkCompleteRequests",
    url: "/bulk-complete-requests",
    params: {
        actionItems: {
            array: true,
        },
    },
    component: "bulkCompleteSafetyMeetingRequests",
    resolve: {
        requests: function ($transition$, SafetyMeetingRequestService) {
            "ngInject";
            let actionItems = $transition$.params().actionItems || [];
            if (!actionItems.length) {
                return [];
            }
            return SafetyMeetingRequestService.list({
                safety_meeting__sid: $transition$.params().meeting_sid,
                sid__in: _.join(actionItems),
            });
        },
        participants: function (
            requests,
            AccountService,
            GrowerMembershipService,
            $transition$,
        ) {
            "ngInject";
            let participantSids = _.uniq(_.compact(_.map(requests, "subject")));
            if (!participantSids.length) {
                return [];
            }
            return AccountService.several(participantSids).then(
                function (participantUsers) {
                    return GrowerMembershipService.list({
                        grower_organization__sid:
                            $transition$.params().grower_sid,
                        user__sid__in: _.join(participantSids),
                    }).then(
                        function (participantMemberships) {
                            return mapUsersWithGrowerMembershipNames(
                                participantUsers,
                                participantMemberships,
                            );
                        },
                        function () {
                            return participantUsers;
                        },
                    );
                },
            );
        },
        topics: function (requests, SafetyMeetingTopicService) {
            "ngInject";
            return SafetyMeetingTopicService.several(
                _.uniq(_.map(requests, "topic")),
            );
        },
    },
    data: {
        title: "routes.SAFETY_MEETING_BULK_COMPLETE_REQUESTS_TITLE",
        mixpanelEvent: "Viewed bulk complete selected safety meeting requests",
        breadcrumb: "crumbs.SAFETY_MEETING_BULK_COMPLETE_REQUESTS",
    },
    redirectTo: parentIfEmpty,
};

export const bulkCancelSafetyMeetingRequestsState = {
    name: "app.growers.grower.manageSafety.meetings.manage.bulkCancelRequests",
    url: "/bulk-cancel-requests",
    params: {
        actionItems: {
            array: true,
        },
    },
    component: "bulkCancelSafetyMeetingRequests",
    resolve: {
        requests: function ($transition$, SafetyMeetingRequestService) {
            "ngInject";
            let actionItems = $transition$.params().actionItems || [];
            if (!actionItems.length) {
                return [];
            }
            return SafetyMeetingRequestService.list({
                safety_meeting__sid: $transition$.params().meeting_sid,
                sid__in: _.join(actionItems),
            });
        },
        participants: function (
            requests,
            AccountService,
            GrowerMembershipService,
            $transition$,
        ) {
            "ngInject";
            let participantSids = _.uniq(_.compact(_.map(requests, "subject")));
            if (!participantSids.length) {
                return [];
            }
            return AccountService.several(participantSids).then(
                function (participantUsers) {
                    return GrowerMembershipService.list({
                        grower_organization__sid:
                            $transition$.params().grower_sid,
                        user__sid__in: _.join(participantSids),
                    }).then(
                        function (participantMemberships) {
                            return mapUsersWithGrowerMembershipNames(
                                participantUsers,
                                participantMemberships,
                            );
                        },
                        function () {
                            return participantUsers;
                        },
                    );
                },
            );
        },
        topics: function (requests, SafetyMeetingTopicService) {
            "ngInject";
            return SafetyMeetingTopicService.several(
                _.uniq(_.map(requests, "topic")),
            );
        },
    },
    data: {
        title: "routes.SAFETY_MEETING_BULK_CANCEL_REQUESTS_TITLE",
        mixpanelEvent: "Viewed bulk cancel selected safety meeting requests",
        breadcrumb: "crumbs.SAFETY_MEETING_BULK_CANCEL_REQUESTS",
    },
    redirectTo: parentIfEmpty,
};

export const packetCreateState = {
    name: "app.growers.grower.manageOnboarding.createPacket",
    url: "/packets/create",
    component: "packetCreate",
    resolve: {
        documents: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.list({
                accessor__sid: $transition$.params().grower_sid,
                active: true,
                status: DocumentPackageService.LIVE,
            });
        },
        administrators: function (TradeOrganizationService, $transition$) {
            "ngInject";
            return TradeOrganizationService.list({
                growerorganization__sid: $transition$.params().grower_sid,
            });
        },
        documentData: function (documents, administrators) {
            "ngInject";
            return _.map(documents, function (document) {
                return {
                    document: document,
                    administrator: _.find(administrators, [
                        SID_LOOKUP,
                        document.administrator,
                    ]),
                };
            });
        },
    },
    data: {
        title: "routes.PACKET_CREATE_TITLE",
        mixpanelEvent: "Viewed packet create",
        breadcrumb: "crumbs.PACKET_CREATE",
    },
};

export const packetEditState = {
    name: "app.growers.grower.manageOnboarding.packetEdit",
    url: "/packets/{packet_sid}/edit",
    component: "packetEdit",
    resolve: {
        packet: function ($transition$, PacketService) {
            "ngInject";
            return PacketService.retrieve($transition$.params().packet_sid);
        },
        documents: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.list({
                accessor__sid: $transition$.params().grower_sid,
                active: true,
                status: DocumentPackageService.LIVE,
            });
        },
        documentPackageTranslateds: function (
            DocumentPackageTranslatedService,
            documents,
        ) {
            "ngInject";
            return DocumentPackageTranslatedService.list({
                document_package__sid__in: _.join(
                    _.uniq(_.map(documents, SID_LOOKUP)),
                ),
            });
        },
        administrators: function (TradeOrganizationService, $transition$) {
            "ngInject";
            return TradeOrganizationService.list({
                growerorganization__sid: $transition$.params().grower_sid,
            });
        },
        customizations: function (
            DocumentPackageCustomizationService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageCustomizationService.list({
                owner__sid: $transition$.params().grower_sid,
            });
        },
        customizationTranslateds: function (
            DocumentPackageCustomizationTranslatedService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageCustomizationTranslatedService.list({
                document_package_customization__owner__sid:
                    $transition$.params().grower_sid,
            });
        },
        packetMembers: function (PacketMemberService, $transition$) {
            "ngInject";
            return PacketMemberService.list({
                packet__sid: $transition$.params().packet_sid,
                active: true,
            });
        },
    },
    data: {
        title: "routes.PACKET_EDIT_TITLE",
        mixpanelEvent: "Viewed packet edit",
        breadcrumb: "crumbs.PACKET_EDIT",
    },
};

export const packetMemberEditState = {
    name: "app.growers.grower.manageOnboarding.packetEdit.packetMemberEdit",
    url: "/{packet_member_sid}/edit",
    component: "packetMemberEdit",
    resolve: {
        packetMember: function ($transition$, PacketMemberService) {
            "ngInject";
            return PacketMemberService.retrieve(
                $transition$.params().packet_member_sid,
            );
        },
        documentPackage: function (DocumentPackageService, packetMember) {
            "ngInject";
            return DocumentPackageService.retrieve(
                packetMember.document_package,
            );
        },
        documentPackageTranslated: function (
            documentPackage,
            documentPackageTranslateds,
        ) {
            "ngInject";
            return documentPackage.getDocumentPackageTranslated(
                documentPackageTranslateds,
            );
        },
        customization: function (
            DocumentPackageCustomizationService,
            packetMember,
        ) {
            "ngInject";
            if (packetMember.document_package_customization) {
                return DocumentPackageCustomizationService.retrieve(
                    packetMember.document_package_customization,
                );
            }
        },
    },
    data: {
        title: "routes.PACKET_MEMBER_EDIT_TITLE",
        mixpanelEvent: "Viewed packet member edit",
        breadcrumb: "crumbs.PACKET_MEMBER_EDIT",
    },
};

export const courseCreateState = {
    name: "app.growers.grower.manageSafety.courses.createCourse",
    url: "/courses/create",
    component: "courseCreate",
    resolve: {
        topicData: function (topics, administrators) {
            "ngInject";
            return _.map(topics, function (topic) {
                return {
                    topic: topic,
                    administrator: _.find(administrators, [
                        SID_LOOKUP,
                        topic.administrator,
                    ]),
                };
            });
        },
    },
    data: {
        title: "routes.COURSE_CREATE_TITLE",
        mixpanelEvent: "Viewed course create",
        breadcrumb: "crumbs.COURSE_CREATE",
    },
};

export const courseEditState = {
    name: "app.growers.grower.manageSafety.courses.courseEdit",
    url: "/courses/{course_sid}/edit",
    component: "courseEdit",
    resolve: {
        course: function ($transition$, SafetyCourseService) {
            "ngInject";
            return SafetyCourseService.retrieve(
                $transition$.params().course_sid,
            );
        },
        courseMembers: function (CourseMemberService, $transition$) {
            "ngInject";
            return CourseMemberService.list({
                course__sid: $transition$.params().course_sid,
                active: true,
            });
        },
        topicData: function (topics, administrators) {
            "ngInject";
            return _.map(topics, function (topic) {
                return {
                    topic: topic,
                    administrator: _.find(administrators, [
                        SID_LOOKUP,
                        topic.administrator,
                    ]),
                };
            });
        },
    },
    data: {
        title: "routes.COURSE_EDIT_TITLE",
        mixpanelEvent: "Viewed course edit",
        breadcrumb: "crumbs.COURSE_EDIT",
    },
};

export const manageCrewsState = {
    name: "app.growers.grower.manageCrews",
    url: "/crews",
    component: "manageCrews",
    resolve: {
        crews: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        farms: function (FarmService, $transition$) {
            "ngInject";
            return FarmService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.MANAGE_CREWS_TITLE",
        mixpanelEvent: "Viewed crews dashboard",
        breadcrumb: "crumbs.MANAGE_CREWS",
    },
};

export const bulkCrewAssignmentState = {
    name: "app.growers.grower.manageCrews.bulkCrewAssignment",
    url: "/bulk-crew-assignment?grower_memberships",
    params: {
        grower_memberships: {
            array: true,
            value: [],
            squash: true,
            inherit: false,
        },
    },
    component: "bulkCrewAssignment",
    resolve: {
        crews: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        farms: function (FarmService, $transition$) {
            "ngInject";
            return FarmService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        users: function (AccountService, growerMemberships) {
            "ngInject";
            return AccountService.several(_.map(growerMemberships, "user"));
        },
        workers: function (growerMemberships, users) {
            "ngInject";
            return _.map(users, function (user) {
                return {
                    user: user,
                    growerMembership: getLatestMembership(
                        growerMemberships,
                        user.sid,
                    ),
                };
            });
        },
        memberships: function (MembershipService, $transition$) {
            "ngInject";
            return MembershipService.list({
                active: true,
                group__grower_organization__sid:
                    $transition$.params().grower_sid,
            });
        },
        preselectedUserSids: function ($transition$, growerMemberships) {
            "ngInject";
            const paramSids = $transition$.params().grower_memberships || [];
            if (!paramSids.length) return [];
            const gmBySid = _.keyBy(growerMemberships, "sid");
            return _.uniq(
                _.compact(
                    paramSids.map((sid) =>
                        gmBySid[sid] ? gmBySid[sid].user : null,
                    ),
                ),
            );
        },
        disciplineReports: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.head(
                {
                    grower_organization__sid: $transition$.params().grower_sid,
                },
                "created",
                25,
                false,
            );
        },
    },
    data: {
        title: "routes.BULK_CREW_ASSIGNMENT_TITLE",
        mixpanelEvent: "Viewed bulk crew assignment",
        breadcrumb: "crumbs.BULK_CREW_ASSIGNMENT",
    },
};

export const crewCreateState = {
    name: "app.growers.grower.manageCrews.createCrew",
    url: "/create",
    component: "crewCreate",
    data: {
        title: "routes.CREW_CREATE_TITLE",
        mixpanelEvent: "Viewed crew create",
        breadcrumb: "crumbs.CREW_CREATE",
    },
};

export const crewEditState = {
    name: "app.growers.grower.manageCrews.crewEdit",
    url: "/{crew_sid}/edit",
    component: "crewEdit",
    resolve: {
        group: function ($transition$, GroupService) {
            "ngInject";
            return GroupService.retrieve($transition$.params().crew_sid);
        },
    },
    data: {
        title: "routes.CREW_EDIT_TITLE",
        mixpanelEvent: "Viewed crew edit",
        breadcrumb: "crumbs.CREW_EDIT",
    },
};

export const crewManagementState = {
    name: "app.growers.grower.manageCrews.crewManagement",
    url: "/{crew_sid}/management",
    component: "crewManagement",
    resolve: {
        group: function ($transition$, GroupService) {
            "ngInject";
            return GroupService.retrieve($transition$.params().crew_sid);
        },
        permissions: function (GroupManagementPermissionService) {
            "ngInject";
            return GroupManagementPermissionService.list({});
        },
        managementMemberships: function (
            $transition$,
            GroupManagementMembershipService,
        ) {
            "ngInject";
            return GroupManagementMembershipService.list({
                active: true,
                group__sid: $transition$.params().crew_sid,
            });
        },
        managerUsers: function (managementMemberships, AccountService) {
            "ngInject";
            if (managementMemberships.length) {
                return AccountService.several(
                    _.uniq(_.map(managementMemberships, "user")),
                );
            } else {
                return [];
            }
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        management: function (
            managerUsers,
            managementMemberships,
            growerMemberships,
        ) {
            "ngInject";
            return _.map(managerUsers, function (user) {
                return {
                    user: user,
                    membership: _.find(managementMemberships, [
                        "user",
                        user.sid,
                    ]),
                    growerMembership: _.find(growerMemberships, [
                        "user",
                        user.sid,
                    ]),
                };
            });
        },
        users: function (AccountService, $transition$) {
            "ngInject";
            return AccountService.list({
                growermembership__grower_organization__sid:
                    $transition$.params().grower_sid,
            });
        },
        selectableUsers: function (managerUsers, users) {
            "ngInject";
            return _.differenceBy(users, managerUsers, SID_LOOKUP);
        },
        workers: function (selectableUsers, growerMemberships) {
            "ngInject";
            return _.compact(
                _.map(selectableUsers, function (user) {
                    return _.find(growerMemberships, ["user", user.sid]);
                }),
            );
        },
    },
    data: {
        title: "routes.CREW_MANAGEMENT_TITLE",
        mixpanelEvent: "Viewed crew managers",
        breadcrumb: "crumbs.CREW_MANAGEMENT",
    },
};

export const crewManagerEditState = {
    name: "app.growers.grower.manageCrews.crewManagement.edit",
    url: "/{group_management_membership_sid}/edit",
    component: "crewManagerEdit",
    resolve: {
        membership: function ($transition$, GroupManagementMembershipService) {
            "ngInject";
            return GroupManagementMembershipService.retrieve(
                $transition$.params().group_management_membership_sid,
            );
        },
        user: function (membership, AccountService) {
            "ngInject";
            return AccountService.retrieve(membership.user);
        },
        permissions: function (GroupManagementPermissionService) {
            "ngInject";
            return GroupManagementPermissionService.list({});
        },
    },
    data: {
        title: "routes.CREW_MANAGER_EDIT_TITLE",
        mixpanelEvent: "Viewed edit crew manager",
        breadcrumb: "crumbs.CREW_MANAGER_EDIT",
    },
};

export const farmState = {
    name: "app.growers.grower.farms.farm",
    url: "/{farm_sid}",
    component: "farmDetail",
    resolve: {
        farm: function ($transition$, FarmService) {
            "ngInject";
            return FarmService.retrieve($transition$.params().farm_sid);
        },
        crops: function (CropService) {
            "ngInject";
            return CropService.list();
        },
        fields: function (farm, FieldService) {
            "ngInject";
            return FieldService.list(
                {
                    active: true,
                    farm__sid: farm.sid,
                },
                {
                    orderBy: "name",
                },
            );
        },
    },
    data: {
        title: "routes.FARM_TITLE",
        breadcrumb: "crumbs.FARM",
    },
};

export const farmEditState = {
    name: "app.growers.grower.farms.farm.edit",
    url: "/edit",
    component: "farmEdit",
    data: {
        title: "routes.FARM_EDIT_TITLE",
        mixpanelEvent: "Viewed farm edit",
        breadcrumb: "crumbs.FARM_EDIT",
        permissions: {
            only: [EDIT_FARM],
            redirectTo: previousIfForbidden,
        },
    },
};

export const farmFieldsCreateState = {
    name: "app.growers.grower.farms.farm.fieldsCreate",
    url: "/fields/create",
    component: "fieldCreate",
    resolve: {
        farms: function (farm) {
            "ngInject";
            return [farm];
        },
        crops: function (CropService) {
            "ngInject";
            return CropService.list();
        },
        successRedirectState: function () {
            "ngInject";
            return "app.growers.grower.farms.farm";
        },
        successRedirectParams: function ($transition$) {
            "ngInject";
            return {
                grower_sid: $transition$.params().grower_sid,
                farm_sid: $transition$.params().farm_sid,
            };
        },
        cancelRedirectState: function () {
            "ngInject";
            return "app.growers.grower.farms.farm";
        },
        cancelRedirectParams: function ($transition$) {
            "ngInject";
            return {
                grower_sid: $transition$.params().grower_sid,
                farm_sid: $transition$.params().farm_sid,
            };
        },
    },
    data: {
        title: "routes.FARM_EDIT_TITLE",
        mixpanelEvent: "Viewed farm field create",
        breadcrumb: "crumbs.FARM_FIELDS_CREATE",
        permissions: {
            only: [EDIT_FARM],
            redirectTo: previousIfForbidden,
        },
    },
};

export const farmFieldsEditState = {
    name: "app.growers.grower.farms.farm.fieldsEdit",
    url: "/fields/{field_sid}/edit",
    component: "fieldEdit",
    resolve: {
        farms: function (farm) {
            "ngInject";
            return [farm];
        },
        field: function (FieldService, $transition$) {
            "ngInject";
            return FieldService.retrieve($transition$.params().field_sid);
        },
        crops: function (CropService) {
            "ngInject";
            return CropService.list();
        },
    },
    data: {
        title: "routes.FARM_FIELDS_EDIT_TITLE",
        mixpanelEvent: "Viewed farm field edit",
        breadcrumb: "crumbs.FARM_FIELDS_EDIT",
        permissions: {
            only: [EDIT_FARM],
            redirectTo: previousIfForbidden,
        },
    },
};

export const farmFieldsUploadState = {
    name: "app.growers.grower.farms.farm.fieldsUpload",
    url: "/fields/upload",
    component: "fieldUpload",
    data: {
        title: "routes.FARM_FIELDS_UPLOAD_TITLE",
        mixpanelEvent: "Viewed farm fields upload",
        breadcrumb: "crumbs.FARM_FIELDS_UPLOAD",
        permissions: {
            only: [EDIT_FARM],
            redirectTo: previousIfForbidden,
        },
    },
};

export const newWorkerState = {
    name: "app.growers.grower.manage.addWorker",
    url: "/add-worker?mobile_phone",
    component: "newWorkerStart",
    resolve: {
        mobile: function ($transition$) {
            "ngInject";
            return $transition$.params().mobile_phone;
        },
    },
    data: {
        title: "routes.NEW_WORKER_TITLE",
        permissions: {
            only: [ADD_TERMINATE_EMPLOYEES],
            redirectTo: previousIfForbidden,
        },
        mixpanelEvent: "Viewed new employee start",
    },
};

export const newWorkerCreateState = {
    name: "app.growers.grower.manage.addWorker.new",
    url: "/new?mobile",
    component: "newWorkerCreate",
    resolve: {
        newMember: function ($transition$) {
            "ngInject";
            return {
                mobile: $transition$.params().mobile,
                preferred_language: SPANISH,
            };
        },
    },
    data: {
        title: "routes.NEW_WORKER_CREATE_TITLE",
        mixpanelEvent: "Viewed create new employee account",
    },
};

export const newWorkerBaseState = {
    name: "app.growers.grower.manage.addWorker.base",
    url: "/{grower_membership_sid}",
    abstract: true,
    component: "newWorkerBase",
    resolve: {
        growerMembership: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.retrieve(
                $transition$.params().grower_membership_sid,
            );
        },
    },
};
export const newWorkerPhotoPromptState = {
    name: "app.growers.grower.manage.addWorker.base.photo",
    url: "/photo",
    component: "newWorkerPhoto",
    resolve: {
        user: function (AccountService, growerMembership) {
            "ngInject";
            return AccountService.retrieve(growerMembership.user);
        },
        nextState: function () {
            "ngInject";
            return newWorkerAssignCrewsState;
        },
        reloadState: function () {
            "ngInject";
            return newWorkerBaseState;
        },
    },
    data: {
        title: "routes.NEW_WORKER_PHOTO_TITLE",
        mixpanelEvent: "Viewed new employee take photo",
    },
};

export const newWorkerIdentificationDocumentsState = {
    name: "app.growers.grower.manage.addWorker.base.identificationDocuments",
    url: "/identification-documents",
    component: "newWorkerIdentificationDocuments",
    resolve: {
        user: function (AccountService, growerMembership) {
            "ngInject";
            return AccountService.retrieve(growerMembership.user);
        },
        nextState: function () {
            "ngInject";
            return newWorkerPaperworkState;
        },
        reloadState: function () {
            "ngInject";
            return newWorkerBaseState;
        },
    },
    data: {
        title: "routes.NEW_WORKER_IDENITIFCATION_DOCUMENTS_TITLE",
        mixpanelEvent: "Viewed new employee scan identification documents",
    },
};

export const newWorkerAssignCrewsState = {
    name: "app.growers.grower.manage.addWorker.base.crews",
    url: "/crews",
    component: "newWorkerAssignCrews",
    resolve: {
        crews: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.NEW_WORKER_ASSIGN_CREWS_TITLE",
        mixpanelEvent: "Viewed new employee assign crews",
    },
};

export const newWorkerPaperworkState = {
    name: "app.growers.grower.manage.addWorker.base.paperwork",
    url: "/documents",
    component: "newWorkerPaperwork",
    resolve: {
        growerMembership: function (GrowerMembershipService, growerMembership) {
            "ngInject";
            return GrowerMembershipService.retrieve(growerMembership.sid);
        },
        packets: function (PacketService, $transition$) {
            "ngInject";
            return PacketService.list({
                active: true,
                owner__sid: $transition$.params().grower_sid,
            });
        },
        packetMembers: function (PacketMemberService, packets) {
            "ngInject";
            return PacketMemberService.list({
                active: true,
                packet__sid__in: _.join(_.map(packets, SID_LOOKUP)),
            });
        },
    },
    data: {
        title: "routes.NEW_WORKER_PAPERWORK_TITLE",
        mixpanelEvent: "Viewed new employee paperwork",
    },
};

export const newWorkerTrainingState = {
    name: "app.growers.grower.manage.addWorker.base.training",
    url: "/training",
    component: "newWorkerTraining",
    resolve: {
        growerMembership: function (GrowerMembershipService, growerMembership) {
            "ngInject";
            return GrowerMembershipService.retrieve(growerMembership.sid);
        },
        courses: function (SafetyCourseService, $transition$) {
            "ngInject";
            return SafetyCourseService.list({
                active: true,
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        wpsCredentials: function ($transition$, CredentialService) {
            "ngInject";
            return CredentialService.list({
                archived: false,
                credential_type__wps_qualified: true,
                expiration_date__gt: moment().format(DATE_FORMAT),
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        wpsCredentialedUsers: function (wpsCredentials, AccountService) {
            "ngInject";
            let wpsTrainerUserSids = _.map(wpsCredentials, "subject");
            return AccountService.several(wpsTrainerUserSids);
        },
        wpsCredentialedGrowerMemberships: function (
            $transition$,
            wpsCredentials,
            GrowerMembershipService,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                user__sid__in: _.join(_.map(wpsCredentials, "subject")),
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        wpsTrainers: function (
            wpsCredentialedUsers,
            wpsCredentialedGrowerMemberships,
        ) {
            "ngInject";
            return _.compact(
                _.map(wpsCredentialedUsers, function (user) {
                    return _.find(wpsCredentialedGrowerMemberships, [
                        "user",
                        user.sid,
                    ]);
                }),
            );
        },
        timeTrackingVendorConfigurations: function (
            TimeTrackingVendorConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return TimeTrackingVendorConfigurationService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
    },
    data: {
        title: "routes.NEW_WORKER_TRAINING_TITLE",
        mixpanelEvent: "Viewed new employee training",
    },
};

export const newWorkerDoneState = {
    name: "app.growers.grower.manage.addWorker.base.done",
    url: "/done",
    component: "newWorkerDone",
    resolve: {
        growerMembership: function (GrowerMembershipService, growerMembership) {
            "ngInject";
            return GrowerMembershipService.retrieve(growerMembership.sid);
        },
    },
    data: {
        title: "routes.NEW_WORKER_DONE_TITLE",
        mixpanelEvent: "Viewed new employee done",
    },
};

export const newWorkerTimeTrackingState = {
    name: "app.growers.grower.manage.addWorker.base.timetracking",
    url: "/time-tracking",
    component: "newWorkerTimeTracking",
    resolve: {
        growerMembership: function (GrowerMembershipService, growerMembership) {
            "ngInject";
            return GrowerMembershipService.retrieve(growerMembership.sid);
        },
        timeTrackingVendorConfigurations: function (
            TimeTrackingVendorConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return TimeTrackingVendorConfigurationService.list(
                {
                    grower_organization__sid: $transition$.params().grower_sid,
                    active: true,
                },
                {
                    expand: ["time_tracking_vendor"],
                },
            );
        },
        timeTrackingEmployeeConfigurations: function (
            TimeTrackingEmployeeConfigurationService,
            growerMembership,
        ) {
            "ngInject";
            return TimeTrackingEmployeeConfigurationService.list({
                grower_membership__sid: growerMembership.sid,
            });
        },
        redirectState: function () {
            "ngInject";
            return "app.growers.grower.manage.addWorker.base.timetracking"; // Have to reference the string since reload state is itself
        },
    },
    data: {
        title: "routes.NEW_WORKER_TIME_TRACKING",
        mixpanelEvent: "Viewed new employee time tracking",
    },
};

function parentIfEmpty(transition, selectedItemsParam = "actionItems") {
    let targetState = transition.targetState();
    let targetParams = targetState.params();
    if (_.isEmpty(targetParams[selectedItemsParam])) {
        return targetState.state().$$state().parent.name;
    }
}

function buildChemicalInventoryRows(inventoryItems, sdsProducts) {
    let productBySid = _.keyBy(sdsProducts, SID_LOOKUP);
    let rows = _.map(inventoryItems, function (inventoryItem) {
        let product = productBySid[inventoryItem.sds_product];
        return {
            inventoryItem: inventoryItem,
            productName: product ? product.product_name : null,
            manufacturer: product
                ? product.manufacturer || product.manufacturer_name
                : null,
            sdsUrl: product ? product.sds_url : null,
        };
    });
    return _.orderBy(rows, ["inventoryItem.created"], ["desc"]);
}

function parentIfNoUser(transition) {
    let targetState = transition.targetState();
    if (!targetState.params().user) {
        return targetState.state().$$state().parent.name;
    }
}

export const sendPaperworkSelectedState = {
    name: "app.growers.grower.manage.sendPaperwork",
    url: "/send-paperwork?document_package_sid?document_package_customization_sid?initiator_fill_first",
    params: {
        actionItems: {
            array: true,
        },
        grower_memberships: {
            array: true,
            value: [],
            squash: true,
            inherit: false,
        },
        document_package_sid: {
            value: null,
            squash: true,
        },
        document_package_customization_sid: {
            value: null,
            squash: true,
        },
        initiator_fill_first: {
            value: null,
            squash: true,
            type: "bool",
        },
    },
    component: "sendPaperworkSelected",
    resolve: {
        recipients: function (AccountService, $transition$, growerMemberships) {
            "ngInject";
            return AccountService.several(
                $transition$.params().actionItems,
            ).then(function (recipients) {
                return mapUsersWithGrowerMembershipNames(
                    recipients,
                    growerMemberships,
                );
            });
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            let growerMembershipSids =
                $transition$.params().grower_memberships || [];
            if (growerMembershipSids.length) {
                return GrowerMembershipService.several(growerMembershipSids);
            }
            let selectedUsers = $transition$.params().actionItems || [];
            if (!selectedUsers.length) {
                return [];
            }
            return GrowerMembershipService.severalByLookup(
                selectedUsers,
                "user__sid__in",
                {
                    grower_organization__sid: $transition$.params().grower_sid,
                },
            );
        },
        documents: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.list({
                accessor__sid: $transition$.params().grower_sid,
                active: true,
                status: DocumentPackageService.LIVE,
            });
        },
        documentPackageTranslateds: function (
            DocumentPackageTranslatedService,
            documents,
        ) {
            "ngInject";
            return DocumentPackageTranslatedService.list({
                document_package__sid__in: _.join(
                    _.uniq(_.map(documents, SID_LOOKUP)),
                ),
            });
        },
        packets: function (PacketService, $transition$) {
            "ngInject";
            return PacketService.list({
                active: true,
                owner__sid: $transition$.params().grower_sid,
            });
        },
        packetMembers: function (PacketMemberService, packets) {
            "ngInject";
            return PacketMemberService.list({
                active: true,
                packet__sid__in: _.join(_.map(packets, SID_LOOKUP)),
            });
        },
        administrators: function (TradeOrganizationService, $transition$) {
            "ngInject";
            return TradeOrganizationService.list({
                growerorganization__sid: $transition$.params().grower_sid,
            });
        },
        customizations: function (
            DocumentPackageCustomizationService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageCustomizationService.list({
                owner__sid: $transition$.params().grower_sid,
            });
        },
        customizationTranslateds: function (
            DocumentPackageCustomizationTranslatedService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageCustomizationTranslatedService.list({
                document_package_customization__owner__sid:
                    $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.SEND_PAPERWORK_SELECTED_TITLE",
        mixpanelEvent: "Viewed send paperwork selected",
        breadcrumb: "crumbs.SEND_PAPERWORK_SELECTED",
    },
    redirectTo: parentIfEmpty,
};

export const bulkSendScanLinksState = {
    name: "app.growers.grower.manage.bulkSendScanLinks",
    url: "/send-scan-links",
    params: {
        actionItems: {
            array: true,
        },
        grower_memberships: {
            array: true,
        },
    },
    component: "bulkSendScanLinks",
    resolve: {
        subjects: function (AccountService, $transition$, growerMemberships) {
            "ngInject";
            return AccountService.several(
                $transition$.params().actionItems,
            ).then(function (subjects) {
                return mapUsersWithGrowerMembershipNames(
                    subjects,
                    growerMemberships,
                );
            });
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            let membershipSids = $transition$.params().grower_memberships || [];
            return GrowerMembershipService.several(membershipSids);
        },
    },
    data: {
        title: "routes.BULK_SEND_SCAN_LINKS_TITLE",
        mixpanelEvent: "Viewed bulk send scan links",
        breadcrumb: "crumbs.BULK_SEND_SCAN_LINKS",
    },
    redirectTo: parentIfEmpty,
};

export const bulkSendSelfieLinksState = {
    name: "app.growers.grower.manage.bulkSendSelfieLinks",
    url: "/send-selfie-links",
    params: {
        actionItems: {
            array: true,
        },
        grower_memberships: {
            array: true,
        },
    },
    component: "bulkSendSelfieLinks",
    resolve: {
        subjects: function (AccountService, $transition$, growerMemberships) {
            "ngInject";
            return AccountService.several(
                $transition$.params().actionItems,
            ).then(function (subjects) {
                return mapUsersWithGrowerMembershipNames(
                    subjects,
                    growerMemberships,
                );
            });
        },
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            let membershipSids = $transition$.params().grower_memberships || [];
            return GrowerMembershipService.several(membershipSids);
        },
    },
    data: {
        title: "routes.BULK_SEND_SELFIE_LINKS_TITLE",
        mixpanelEvent: "Viewed bulk send selfie links",
        breadcrumb: "crumbs.BULK_SEND_SELFIE_LINKS",
    },
    redirectTo: parentIfEmpty,
};

export const editMemberAccountState = {
    name: "app.growers.grower.manage.member.editMemberAccount",
    url: "/edit-account",
    component: "editMemberAccount",
    resolve: {
        user: function (AccountService, user) {
            "ngInject";
            return AccountService.retrieve(user.sid);
        },
    },
    data: {
        title: "routes.EDIT_MEMBER_ACCOUNT_TITLE",
        mixpanelEvent: "Viewed employee account edit",
        breadcrumb: "crumbs.EDIT_MEMBER_ACCOUNT",
    },
};

export const createMemberCredentialState = {
    name: "app.growers.grower.manage.member.createCredential",
    url: "/add-credential",
    component: "createCredential",
    resolve: {
        credentialTypes: function (CredentialTypeService) {
            "ngInject";
            return CredentialTypeService.list({ active: true });
        },
    },
    data: {
        title: "routes.CREATE_MEMBER_CREDENTIAL_TITLE",
        mixpanelEvent: "Viewed add employee credential or license",
        breadcrumb: "crumbs.CREATE_MEMBER_CREDENTIAL",
    },
};

export const memberEditProductsState = {
    name: "app.growers.grower.manage.member.editProducts",
    url: "/edit-details",
    component: "memberEditProducts",
    resolve: {
        growerMembership: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.retrieve(
                $transition$.params().member_sid,
            );
        },
    },
    data: {
        title: "routes.MEMBER_EDIT_PRODUCT_TITLE",
        mixpanelEvent: "Viewed employee details edit",
        breadcrumb: "crumbs.MEMBER_EDIT_PRODUCT",
    },
};

export const memberEditPhotoState = {
    name: "app.growers.grower.manage.member.editProducts.editPhoto",
    url: "/photo",
    component: "memberEditPhoto",
    resolve: {
        nextState: function () {
            "ngInject";
            return memberState;
        },
    },
    data: {
        title: "routes.MEMBER_EDIT_PHOTO_TITLE",
        breadcrumb: "crumbs.MEMBER_EDIT_PHOTO",
    },
};

export const memberMoveState = {
    name: "app.growers.grower.manage.member.move",
    url: "/move",
    component: "moveWorker",
    resolve: {
        groupMemberships: function (MembershipService, $transition$, user) {
            "ngInject";
            return MembershipService.list({
                active: true,
                group__grower_organization__sid:
                    $transition$.params().grower_sid,
                user__sid: user.sid,
            });
        },
        groups: function ($transition$, GroupService) {
            "ngInject";
            return GroupService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        assignments: function (groupMemberships) {
            "ngInject";
            return _.map(groupMemberships, "group");
        },
        originalAssignments: function (assignments) {
            "ngInject";
            return _.clone(assignments);
        },
        originalMemberships: function (groupMemberships) {
            "ngInject";
            return groupMemberships;
        },
    },
    data: {
        title: "routes.MEMBER_MOVE_TITLE",
        mixpanelEvent: "Viewed move worker",
        breadcrumb: "crumbs.MEMBER_MOVE",
    },
};

export const bulkAssignHousingState = {
    name: "app.growers.grower.housing.bulkAssignHousing",
    url: "/bulk-assign-housing?grower_memberships",
    params: {
        grower_memberships: {
            array: true,
            value: [],
            squash: true,
            inherit: false,
        },
    },
    component: "bulkAssignHousing",
    resolve: {
        housingSites: function (HousingSiteService, $transition$) {
            "ngInject";
            return HousingSiteService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        assignments: function (HousingAssignmentService, $transition$) {
            "ngInject";
            return HousingAssignmentService.list({
                active: true,
                housing_site__grower_organization__sid:
                    $transition$.params().grower_sid,
            });
        },
        activeGrowerMemberships: function (
            GrowerMembershipService,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        housingAssignmentGrowerMemberships: function (
            GrowerMembershipService,
            assignments,
        ) {
            "ngInject";
            let membershipSids = _.uniq(
                _.map(assignments, "grower_membership"),
            );
            if (!membershipSids.length) {
                return [];
            }
            return GrowerMembershipService.several(membershipSids);
        },
        growerMemberships: function (
            activeGrowerMemberships,
            housingAssignmentGrowerMemberships,
        ) {
            "ngInject";
            return _.uniqBy(
                _.concat(
                    activeGrowerMemberships,
                    housingAssignmentGrowerMemberships,
                ),
                SID_LOOKUP,
            );
        },
        preselectedUserSids: function ($transition$, growerMemberships) {
            "ngInject";
            const paramSids = $transition$.params().grower_memberships || [];
            if (!paramSids.length) return [];
            const gmBySid = _.keyBy(growerMemberships, "sid");
            return _.uniq(
                _.compact(
                    paramSids.map((sid) =>
                        gmBySid[sid] ? gmBySid[sid].user : null,
                    ),
                ),
            );
        },
    },
    data: {
        title: "routes.BULK_ASSIGN_HOUSING_TITLE",
        mixpanelEvent: "Viewed bulk assign housing",
        breadcrumb: "crumbs.BULK_ASSIGN_HOUSING",
    },
};

export const memberPaperworkHistoryState = {
    name: "app.growers.grower.manage.member.paperworkHistory",
    url: "/paperwork-history",
    component: "paperworkHistory",
    resolve: {
        completedPaperworkRecords: function (
            DocumentPackageRecordService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageRecordService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                grower_membership__sid: $transition$.params().member_sid,
                completion_date__isnull: false,
                archived: false,
            });
        },
        documentPackages: function (
            DocumentPackageService,
            completedPaperworkRecords,
        ) {
            "ngInject";
            let recordPackageSids = _.map(
                completedPaperworkRecords,
                "document_package",
            );
            let packageSids = _.uniq(recordPackageSids);
            return DocumentPackageService.several(packageSids);
        },
        documentPackageTranslateds: function (
            DocumentPackageTranslatedService,
            documentPackages,
        ) {
            "ngInject";
            return DocumentPackageTranslatedService.list({
                document_package__sid__in: _.join(
                    _.map(documentPackages, SID_LOOKUP),
                ),
            });
        },
        paperworks: function (
            completedPaperworkRecords,
            documentPackages,
            documentPackageTranslateds,
            user,
        ) {
            "ngInject";
            return _.map(completedPaperworkRecords, function (record) {
                let documentPackage = _.find(documentPackages, [
                    SID_LOOKUP,
                    record.document_package,
                ]);
                return {
                    record: record,
                    documentPackage,
                    documentPackageTranslated: documentPackage
                        ? documentPackage.getDocumentPackageTranslated(
                              documentPackageTranslateds,
                          )
                        : null,
                    user: user,
                };
            });
        },
    },
    data: {
        title: "routes.MEMBER_PAPERWORK_HISTORY_TITLE",
        mixpanelEvent: "Viewed paperwork history",
        breadcrumb: "crumbs.MEMBER_PAPERWORK_HISTORY",
    },
};

export const memberUploadExternalDocumentsState = {
    name: "app.growers.grower.manage.member.paperworkHistory.uploadExternalDocuments",
    url: "/upload",
    component: "uploadExternalDocuments",
    resolve: {},
    data: {
        title: "routes.MEMBER_UPLOAD_EXTERNAL_DOCUMENTS_TITLE",
        mixpanelEvent: "Viewed upload external documents",
        breadcrumb: "crumbs.MEMBER_UPLOAD_EXTERNAL_DOCUMENTS",
    },
};

export const memberTrainingHistoryState = {
    name: "app.growers.grower.manage.member.trainingHistory",
    url: "/training-history",
    component: "traineeHistory",
    resolve: {
        completedRequests: function (
            SafetyMeetingRequestService,
            $transition$,
            user,
        ) {
            "ngInject";
            return SafetyMeetingRequestService.list({
                subject__sid: user.sid,
                status: COMPLETE,
                requester: $transition$.params().grower_sid,
            });
        },
        topics: function (SafetyMeetingTopicService, completedRequests) {
            "ngInject";
            let topicsSids = _.map(completedRequests, "topic");
            return SafetyMeetingTopicService.several(topicsSids);
        },
        records: function (completedRequests, topics) {
            "ngInject";
            return _.map(completedRequests, function (request) {
                return {
                    request: request,
                    topic: _.find(topics, [SID_LOOKUP, request.topic]),
                };
            });
        },
    },
    data: {
        title: "routes.MEMBER_TRAINING_HISTORY_TITLE",
        mixpanelEvent: "Viewed training history",
        breadcrumb: "crumbs.MEMBER_TRAINING_HISTORY",
    },
};

export const memberAddTrainingHistoryState = {
    name: "app.growers.grower.manage.member.trainingHistory.add",
    url: "/add",
    component: "addTrainingHistory",
    resolve: {
        topics: function (SafetyMeetingTopicService, $transition$) {
            "ngInject";
            return SafetyMeetingTopicService.list({
                active: true,
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        administrators: function (TradeOrganizationService, $transition$) {
            "ngInject";
            return TradeOrganizationService.list({
                growerorganization__sid: $transition$.params().grower_sid,
            });
        },
        topicData: function (topics, administrators) {
            "ngInject";
            return _.map(topics, function (topic) {
                return {
                    topic: topic,
                    administrator: _.find(administrators, [
                        SID_LOOKUP,
                        topic.administrator,
                    ]),
                };
            });
        },
        request: function (user, grower, account) {
            "ngInject";
            return {
                subject: user.sid,
                requester: grower.sid,
                status: COMPLETE,
                certifier: account.sid,
                certification_type: GROWER_CERTIFIED,
            };
        },
    },
    data: {
        title: "routes.MEMBER_ADD_TRAINING_HISTORY_TITLE",
        mixpanelEvent: "Viewed add training history",
        breadcrumb: "crumbs.MEMBER_ADD_TRAINING_HISTORY",
    },
};

export const memberPaperworkState = {
    name: "app.growers.grower.manage.member.paperwork",
    url: "/paperwork",
    component: "memberPaperwork",
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(/* webpackChunkName: "lazy.paperwork" */ "./lazy/paperwork"),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    resolve: {
        memberIncompleteDocumentRequests: function (
            DocumentPackageRequestService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageRequestService.list({
                grower_membership__sid: $transition$.params().member_sid,
                completed: false,
                cancelled: false,
                requester__sid: $transition$.params().grower_sid,
            });
        },
        signatureRequests: function (SignatureRequestService, $transition$) {
            "ngInject";
            return SignatureRequestService.list({
                document_package_request__completed: false,
                document_package_request__cancelled: false,
                document_package_request__grower_membership__sid:
                    $transition$.params().member_sid,
                document_package_request__requester__sid:
                    $transition$.params().grower_sid,
            });
        },
        documentPackages: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.list({
                pending_document_package_request__requester__sid:
                    $transition$.params().grower_sid,
            });
        },
        paperworkToDo: function (
            memberIncompleteDocumentRequests,
            signatureRequests,
            documentPackages,
        ) {
            "ngInject";
            return _.map(
                memberIncompleteDocumentRequests,
                function (documentRequest) {
                    return {
                        documentRequest: documentRequest,
                        signatureRequests: _.filter(signatureRequests, [
                            "document_package_request",
                            documentRequest.sid,
                        ]),
                        nextRole: getNextRole(
                            documentRequest,
                            _.find(documentPackages, [
                                SID_LOOKUP,
                                documentRequest.document_package,
                            ]),
                        ),
                    };
                },
            );
        },
        paperworkToStart: function (paperworkToDo) {
            "ngInject";
            return _.filter(paperworkToDo, function (paperwork) {
                return paperwork.documentRequest.isInPrefill();
            });
        },
        paperworkV3ToSign: function (paperworkToDo, account) {
            "ngInject";
            return _.filter(paperworkToDo, function (paperwork) {
                return !_.isEmpty(
                    _.filter(paperwork.signatureRequests, {
                        role: INITIATOR,
                        status: PAPERWORK_PENDING,
                        signer: account.sid,
                    }),
                );
            });
        },
        paperworkWithEmployerPartToDo: function (paperworkToDo) {
            "ngInject";
            return _.filter(paperworkToDo, function (paperwork) {
                return (
                    paperwork.nextRole === INITIATOR &&
                    _.isEmpty(
                        _.reject(paperwork.signatureRequests, {
                            role: SIGNER,
                            status: PAPERWORK_COMPLETE,
                        }),
                    )
                );
            });
        },
        redirect: function (
            paperworkToStart,
            paperworkV3ToSign,
            paperworkWithEmployerPartToDo,
            $transition$,
        ) {
            "ngInject";
            console.log({ paperworkToStart });
            console.log({ paperworkV3ToSign });
            console.log({ paperworkWithEmployerPartToDo });

            let state;
            let opts = { reload: memberState };
            let params = {
                grower_sid: $transition$.params().grower_sid,
                member_sid: $transition$.params().member_sid,
            };
            if (paperworkToStart.length) {
                let nextDocument = _.first(paperworkToStart);
                state = prefillPaperworkState;
                params.document_package_request_sid =
                    nextDocument.documentRequest.sid;
            } else if (paperworkWithEmployerPartToDo.length) {
                let nextDocument = _.first(paperworkWithEmployerPartToDo);
                state = memberV3InitiatorSignaturePaperwork;
                params.document_package_request_sid =
                    nextDocument.documentRequest.sid;
            } else if (paperworkV3ToSign.length) {
                state = memberV3SignState;
                params.document_package_requests = _.map(
                    paperworkV3ToSign,
                    "documentRequest.sid",
                );
            } else {
                state = memberState;
            }
            return $transition$.router.stateService.target(state, params, opts);
        },
    },
    redirectTo: (trans) => {
        return trans.injector().getAsync("redirect");
    },
};

export const memberRequestPaperworkState = {
    name: "app.growers.grower.manage.member.send-paperwork",
    url: "/send-paperwork",
    component: "memberRequestPaperwork",
    resolve: {
        documents: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.list({
                accessor__sid: $transition$.params().grower_sid,
                active: true,
                status: DocumentPackageService.LIVE,
            });
        },
        documentPackageTranslateds: function (
            DocumentPackageTranslatedService,
            documents,
        ) {
            "ngInject";
            return DocumentPackageTranslatedService.list({
                document_package__sid__in: _.join(
                    _.uniq(_.map(documents, SID_LOOKUP)),
                ),
            });
        },
        customizations: function (
            DocumentPackageCustomizationService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageCustomizationService.list({
                owner__sid: $transition$.params().grower_sid,
            });
        },
        customizationTranslateds: function (
            DocumentPackageCustomizationTranslatedService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageCustomizationTranslatedService.list({
                document_package_customization__owner__sid:
                    $transition$.params().grower_sid,
            });
        },
        administrators: function (TradeOrganizationService, $transition$) {
            "ngInject";
            return TradeOrganizationService.list({
                growerorganization__sid: $transition$.params().grower_sid,
            });
        },
        packets: function (PacketService, $transition$) {
            "ngInject";
            return PacketService.list({
                active: true,
                owner__sid: $transition$.params().grower_sid,
            });
        },
        packetMembers: function (PacketMemberService, packets) {
            "ngInject";
            return PacketMemberService.list({
                active: true,
                packet__sid__in: _.join(_.map(packets, SID_LOOKUP)),
            });
        },
    },
    data: {
        title: "routes.MEMBER_REQUEST_PAPERWORK_TITLE",
        mixpanelEvent: "Viewed send paperwork individual",
        breadcrumb: "crumbs.MEMBER_REQUEST_PAPERWORK",
    },
};

export const prefillPaperworkState = {
    name: "app.growers.grower.manage.member.paperwork.prefill",
    url: "/{document_package_request_sid}/prefill",
    component: "prefill",
    resolve: {
        documentPackageRequest: function (
            DocumentPackageRequestService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageRequestService.retrieve(
                $transition$.params().document_package_request_sid,
            );
        },
        documentPackage: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.first({
                documentpackagerequest__sid:
                    $transition$.params().document_package_request_sid,
            });
        },
        documentPackageTranslateds: function (
            DocumentPackageTranslatedService,
            documentPackage,
        ) {
            "ngInject";
            return DocumentPackageTranslatedService.list({
                document_package__sid: documentPackage.sid,
            });
        },
        documentPackageTranslated: function (
            documentPackage,
            documentPackageTranslateds,
        ) {
            "ngInject";
            return documentPackage.getDocumentPackageTranslated(
                documentPackageTranslateds,
            );
        },
        prefillData: function (
            DocumentPackageRequestService,
            $transition$,
            account,
        ) {
            "ngInject";
            return DocumentPackageRequestService.prefillRequest(
                $transition$.params().document_package_request_sid,
                account.sid,
            );
        },
    },
    data: {
        title: "routes.MEMBER_START_SIGNATURE_PAPERWORK_TITLE",
        mixpanelEvent: "Grower viewed paperwork simple form",
        breadcrumb: "crumbs.MEMBER_START_SIGNATURE_PAPERWORK",
    },
    redirectTo: (trans) => {
        return trans
            .injector()
            .getAsync("documentPackageRequest")
            .then(function (request) {
                if (
                    request.completed ||
                    request.cancelled ||
                    !request.isInPrefill()
                ) {
                    return trans.router.stateService.target(
                        memberPaperworkState,
                        {
                            grower_sid: trans.params().grower_sid,
                            member_sid: trans.params().member_sid,
                        },
                        { reload: memberPaperworkState },
                    );
                }
            });
    },
};

export const memberV3SignState = {
    name: "app.growers.grower.manage.member.paperwork.memberV3Sign",
    url: "/v3-sign?document_package_requests",
    params: {
        document_package_requests: {
            array: true,
        },
    },
    component: "newSignPaperwork",
    resolve: {
        documentPackageRequests: function (
            DocumentPackageRequestService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageRequestService.list({
                completed: false,
                cancelled: false,
                sid__in: _.join(
                    $transition$.params().document_package_requests,
                ),
            });
        },
        documentPackages: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.list({
                documentpackagerequest__sid__in: _.join(
                    $transition$.params().document_package_requests,
                ),
            });
        },
        signers: function (AccountService, documentPackageRequests) {
            "ngInject";
            return AccountService.several(
                _.uniq(_.map(documentPackageRequests, "signer")),
            );
        },
        signatureRequests: function (
            $transition$,
            SignatureRequestService,
            accountSid,
        ) {
            "ngInject";
            return SignatureRequestService.list({
                signer__sid: accountSid,
                status: PENDING,
                role: INITIATOR,
                document_package_request__sid__in: _.join(
                    $transition$.params().document_package_requests,
                ),
            });
        },
        documentPackageTranslateds: function (
            DocumentPackageTranslatedService,
            documentPackages,
        ) {
            "ngInject";
            return DocumentPackageTranslatedService.list({
                document_package__sid__in: _.join(
                    _.uniq(_.map(documentPackages, SID_LOOKUP)),
                ),
            });
        },
        redirectState: function () {
            "ngInject";
            return memberState;
        },
        reloadState: function () {
            "ngInject";
            return memberState;
        },
        initialSignature: function (
            PaperworkSignatureService,
            accountSid,
            $transition$,
        ) {
            "ngInject";
            return PaperworkSignatureService.get(
                accountSid,
                $transition$.params().grower_sid,
            );
        },
        requests: function (
            signatureRequests,
            documentPackages,
            documentPackageRequests,
            documentPackageTranslateds,
        ) {
            "ngInject";
            return _.map(signatureRequests, function (signatureRequest) {
                let documentPackageRequest = _.find(documentPackageRequests, [
                    SID_LOOKUP,
                    signatureRequest.document_package_request,
                ]);
                let documentPackage = _.find(documentPackages, [
                    SID_LOOKUP,
                    signatureRequest.document_package,
                ]);
                return {
                    signatureRequest,
                    documentPackageRequest,
                    documentPackage,
                    documentPackageTranslated:
                        documentPackage.getDocumentPackageTranslated(
                            documentPackageTranslateds,
                        ),
                };
            });
        },
    },
    data: {
        title: "routes.V3_SIGN_TITLE",
        mixpanelEvent: "Grower viewed paperwork for signature",
        breadcrumb: "crumbs.V3_SIGN",
    },
};

export const memberV3InitiatorSignaturePaperwork = {
    name: "app.growers.grower.manage.member.paperwork.initiatorStartPaperwork",
    url: "/{document_package_request_sid}/v3-start-signature",
    component: "startPaperwork",
    resolve: {
        documentPackageRequest: function (
            DocumentPackageRequestService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageRequestService.retrieve(
                $transition$.params().document_package_request_sid,
            );
        },
        documentPackage: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.first({
                documentpackagerequest__sid:
                    $transition$.params().document_package_request_sid,
            });
        },
        documentPackageTranslateds: function (
            DocumentPackageTranslatedService,
            documentPackage,
        ) {
            "ngInject";
            return DocumentPackageTranslatedService.list({
                document_package__sid: documentPackage.sid,
            });
        },
        documentPackageTranslated: function (
            documentPackage,
            documentPackageTranslateds,
        ) {
            "ngInject";
            return documentPackage.getDocumentPackageTranslated(
                documentPackageTranslateds,
            );
        },
        prefillData: function (
            DocumentPackageRequestService,
            $transition$,
            account,
        ) {
            "ngInject";
            return DocumentPackageRequestService.prefillRequest(
                $transition$.params().document_package_request_sid,
                account.sid,
            );
        },
        redirectState: function () {
            "ngInject";
            return memberPaperworkState;
        },
        backState: function () {
            "ngInject";
            return memberState;
        },
        role: function () {
            "ngInject";
            return INITIATOR;
        },
        autoSubmit: function (documentPackageRequest) {
            "ngInject";
            return false;
        },
    },
    data: {
        title: "routes.V3_EMPLOYER_FORM_TITLE",
        mixpanelEvent: "Employer viewed employer part of paperwork simple form",
        breadcrumb: "crumbs.V3_EMPLOYER_FORM",
    },
    redirectTo: (trans) => {
        let requestResolve = trans
            .injector()
            .getAsync("documentPackageRequest");
        let redirectStateResolve = trans.injector().getAsync("redirectState");
        let $transition$ = trans.injector().get("$transition$");
        let $q = trans.injector().get("$q");
        return $q
            .all([requestResolve, redirectStateResolve])
            .then(function (resolves) {
                let [request, redirectState] = resolves;
                let targetRedirectState =
                    $transition$.router.stateService.target(
                        redirectState,
                        { member_sid: request.grower_membership },
                        { reload: redirectState },
                    );
                if (
                    request.completed ||
                    request.cancelled ||
                    request.next_pending_signature_request
                ) {
                    return targetRedirectState;
                }
            });
    },
};

//  Stage manager paperwork states

export const stageManagerManagerSignatureState = {
    name: "app.growers.grower.manageOnboarding.stageManager.memberV3Sign",
    url: "/v3-sign",
    params: {
        document_package_requests: {
            array: true,
        },
    },
    component: "newSignPaperwork",
    resolve: {
        documentPackageRequests: function (
            DocumentPackageRequestService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageRequestService.list({
                completed: false,
                cancelled: false,
                sid__in: _.join(
                    $transition$.params().document_package_requests,
                ),
            });
        },
        documentPackages: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.list({
                documentpackagerequest__sid__in: _.join(
                    $transition$.params().document_package_requests,
                ),
            });
        },
        growerMemberships: function (
            GrowerMembershipService,
            documentPackageRequests,
        ) {
            "ngInject";
            if (!documentPackageRequests.length) {
                return [];
            }
            return GrowerMembershipService.several(
                _.uniq(_.map(documentPackageRequests, "grower_membership")),
            );
        },
        signers: function (AccountService, documentPackageRequests) {
            "ngInject";
            return AccountService.several(
                _.uniq(_.map(documentPackageRequests, "signer")),
            );
        },
        signatureRequests: function (
            $transition$,
            SignatureRequestService,
            accountSid,
        ) {
            "ngInject";
            return SignatureRequestService.list({
                signer__sid: accountSid,
                status: PENDING,
                role: INITIATOR,
                document_package_request__sid__in: _.join(
                    $transition$.params().document_package_requests,
                ),
            });
        },
        documentPackageTranslateds: function (
            DocumentPackageTranslatedService,
            documentPackages,
        ) {
            "ngInject";
            return DocumentPackageTranslatedService.list({
                document_package__sid__in: _.join(
                    _.uniq(_.map(documentPackages, SID_LOOKUP)),
                ),
            });
        },
        redirectState: function () {
            "ngInject";
            return paperworkStageManagerState;
        },
        successToastKey: function () {
            "ngInject";
            return "newSignPaperwork.INITIATOR_SIGN_SUCCESS_TOAST";
        },
        initialSignature: function (
            PaperworkSignatureService,
            accountSid,
            $transition$,
        ) {
            "ngInject";
            return PaperworkSignatureService.get(
                accountSid,
                $transition$.params().grower_sid,
            );
        },
        requests: function (
            signatureRequests,
            documentPackages,
            documentPackageRequests,
            documentPackageTranslateds,
        ) {
            "ngInject";
            return _.map(signatureRequests, function (signatureRequest) {
                let documentPackageRequest = _.find(documentPackageRequests, [
                    SID_LOOKUP,
                    signatureRequest.document_package_request,
                ]);
                let documentPackage = _.find(documentPackages, [
                    SID_LOOKUP,
                    signatureRequest.document_package,
                ]);
                return {
                    signatureRequest,
                    documentPackageRequest,
                    documentPackage,
                    documentPackageTranslated:
                        documentPackage.getDocumentPackageTranslated(
                            documentPackageTranslateds,
                        ),
                };
            });
        },
    },
    data: {
        title: "routes.V3_SIGN_TITLE",
        mixpanelEvent: "Grower viewed paperwork for signature",
        breadcrumb: "crumbs.V3_SIGN",
    },
    redirectTo: _.partialRight(parentIfEmpty, "document_package_requests"),
};

export const stageManagerPrefillBulkState = {
    name: "app.growers.grower.manageOnboarding.stageManager.prefillBulk",
    url: "/bulk-prefill",
    params: {
        document_package_requests: {
            array: true,
            value: [],
        },
    },
    component: "bulkPrefill",
    resolve: {
        documentPackageRequests: function (
            DocumentPackageRequestService,
            $transition$,
        ) {
            "ngInject";
            let requestSids = _.uniq(
                $transition$.params().document_package_requests || [],
            );
            if (!requestSids.length) {
                return [];
            }
            return DocumentPackageRequestService.list({
                completed: false,
                cancelled: false,
                sid__in: _.join(requestSids),
            });
        },
        documentPackages: function (
            DocumentPackageService,
            documentPackageRequests,
        ) {
            "ngInject";
            if (!documentPackageRequests.length) {
                return [];
            }
            return DocumentPackageService.list({
                documentpackagerequest__sid__in: _.join(
                    _.map(documentPackageRequests, "sid"),
                ),
            });
        },
        documentPackageTranslateds: function (
            DocumentPackageTranslatedService,
            documentPackageRequests,
        ) {
            "ngInject";
            if (!documentPackageRequests.length) {
                return [];
            }
            return DocumentPackageTranslatedService.list({
                document_package__sid__in: _.join(
                    _.uniq(_.map(documentPackageRequests, "document_package")),
                ),
            });
        },
        prefillAttemptsByDocumentPackageRequestSid: function (
            DocumentPackageRequestPrefillAttemptService,
            documentPackageRequests,
        ) {
            "ngInject";
            if (!documentPackageRequests.length) {
                return {};
            }
            return DocumentPackageRequestPrefillAttemptService.list(
                {
                    document_package_request__sid__in: _.join(
                        _.map(documentPackageRequests, SID_LOOKUP),
                    ),
                    status: "resolved",
                },
                {
                    orderBy: "created",
                    ascending: false,
                },
            ).then(getLatestPrefillAttemptByRequestSid);
        },
        growerMemberships: function (
            GrowerMembershipService,
            documentPackageRequests,
        ) {
            "ngInject";
            if (!documentPackageRequests.length) {
                return [];
            }
            return GrowerMembershipService.several(
                _.map(documentPackageRequests, "grower_membership"),
            );
        },
    },
    data: {
        title: "routes.STAGE_MANAGER_PREFILL_TITLE",
        mixpanelEvent: "Grower viewed paperwork simple form",
        breadcrumb: "crumbs.STAGE_MANAGER_PREFILL_TITLE",
    },
    redirectTo: _.partialRight(parentIfEmpty, "document_package_requests"),
};

export const stageManagerManagerStartPaperworkState = {
    name: "app.growers.grower.manageOnboarding.stageManager.managerStartPaperwork",
    url: "/{document_package_request_sid}/v3-start-signature",
    params: {
        document_package_requests: {
            array: true,
            value: [],
            squash: true,
            inherit: false,
        },
    },
    component: "bulkManagerStartPaperwork",
    resolve: {
        documentPackageRequestSids: function ($transition$) {
            "ngInject";
            return _.uniq(
                _.compact(
                    _.concat(
                        $transition$.params().document_package_requests || [],
                        [$transition$.params().document_package_request_sid],
                    ),
                ),
            );
        },
        documentPackageRequests: function (
            DocumentPackageRequestService,
            documentPackageRequestSids,
        ) {
            "ngInject";
            if (!documentPackageRequestSids.length) {
                return [];
            }
            return DocumentPackageRequestService.list({
                completed: false,
                cancelled: false,
                sid__in: _.join(documentPackageRequestSids),
            }).then(function (requests) {
                return _.filter(requests, function (request) {
                    return !request.next_pending_signature_request;
                });
            });
        },
        growerMemberships: function (
            GrowerMembershipService,
            documentPackageRequests,
        ) {
            "ngInject";
            if (!documentPackageRequests.length) {
                return [];
            }
            return GrowerMembershipService.several(
                _.uniq(_.map(documentPackageRequests, "grower_membership")),
            );
        },
        documentPackages: function (
            DocumentPackageService,
            documentPackageRequests,
        ) {
            "ngInject";
            if (!documentPackageRequests.length) {
                return [];
            }
            return DocumentPackageService.list({
                documentpackagerequest__sid__in: _.join(
                    _.map(documentPackageRequests, "sid"),
                ),
            });
        },
        documentPackageTranslateds: function (
            DocumentPackageTranslatedService,
            documentPackageRequests,
        ) {
            "ngInject";
            if (!documentPackageRequests.length) {
                return [];
            }
            return DocumentPackageTranslatedService.list({
                document_package__sid__in: _.join(
                    _.uniq(_.map(documentPackageRequests, "document_package")),
                ),
            });
        },
    },
    data: {
        title: "routes.V3_EMPLOYER_FORM_TITLE",
        mixpanelEvent: "Employer viewed employer part of paperwork simple form",
        breadcrumb: "crumbs.V3_EMPLOYER_FORM",
    },
    redirectTo: (trans) => {
        let requestsResolve = trans
            .injector()
            .getAsync("documentPackageRequests");
        let $transition$ = trans.injector().get("$transition$");
        return requestsResolve.then(function (requests) {
            if (!requests.length) {
                return $transition$.router.stateService.target(
                    paperworkStageManagerState,
                    null,
                );
            }
            if (
                _.every(requests, function (request) {
                    return (
                        request.completed ||
                        request.cancelled ||
                        request.next_pending_signature_request
                    );
                })
            ) {
                return $transition$.router.stateService.target(
                    paperworkStageManagerState,
                    null,
                );
            }
            return;
        });
    },
};

export const bulkFinishSafetyMeetingState = {
    name: "app.growers.grower.manageSafety.meetings.bulkFinish",
    url: "/finish",
    params: {
        actionItems: {
            array: true,
        },
    },
    component: "bulkSafetyMeetingFinish",
    resolve: {
        meetings: function (SafetyMeetingService, $transition$) {
            "ngInject";
            return SafetyMeetingService.several(
                $transition$.params().actionItems,
            );
        },
    },
    data: {
        title: "routes.BULK_FINISH_SAFETY_MEETING_TITLE",
        mixpanelEvent: "Viewed bulk finish safety meeting",
        breadcrumb: "crumbs.BULK_FINISH_SAFETY_MEETING",
    },
    redirectTo: parentIfEmpty,
};

export const bulkDownloadSafetyMeetingState = {
    name: "app.growers.grower.manageSafety.meetings.bulkDownload",
    url: "/download",
    params: {
        actionItems: {
            array: true,
        },
    },
    component: "bulkDownloadMeetingReport",
    resolve: {
        meetings: function (SafetyMeetingService, $transition$) {
            "ngInject";
            return SafetyMeetingService.several(
                $transition$.params().actionItems,
            );
        },
    },
    data: {
        title: "routes.BULK_DOWNLOAD_SAFETY_MEETING_TITLE",
        mixpanelEvent: "Viewed bulk download safety meeting",
        breadcrumb: "crumbs.BULK_DOWNLOAD_SAFETY_MEETING",
    },
    redirectTo: parentIfEmpty,
};

export const bulkMessageSafetyMeetingState = {
    name: "app.growers.grower.manageSafety.meetings.bulkMessage",
    url: "/message",
    params: {
        actionItems: {
            array: true,
        },
    },
    component: "bulkSafetyMeetingMessage",
    resolve: {
        meetings: function (SafetyMeetingService, $transition$) {
            "ngInject";
            return SafetyMeetingService.several(
                $transition$.params().actionItems,
            );
        },
    },
    data: {
        title: "routes.BULK_MESSAGE_SAFETY_MEETING_TITLE",
        mixpanelEvent: "Viewed bulk finish safety message",
        breadcrumb: "crumbs.BULK_MESSAGE_SAFETY_MEETING",
    },
    redirectTo: parentIfEmpty,
};
// TODO: OK to move to grower level
export const newAnnouncementBaseState = {
    name: "app.growers.grower.manageCommunication.base",
    url: "/{announcement_sid}",
    abstract: true,
    component: "newAnnouncementBase",
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
                {},
            );
        },
        deleteRedirectState: function () {
            "ngInject";
            return communicationHistoryState;
        },
        progressStates: function () {
            "ngInject";
            return [
                addRecipientsState,
                addContentsState,
                addAttachmentsState,
                addLocationState,
                reviewAnnouncementState,
            ];
        },
    },
};

// TODO: OK to move to grower level
export const addRecipientsState = {
    name: "app.growers.grower.manageCommunication.base.new",
    url: "/select-recipients",
    component: "addRecipients",
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
            );
        },
        nextState: function () {
            "ngInject";
            return addContentsState;
        },
        reloadState: function () {
            "ngInject";
            return newAnnouncementBaseState;
        },
    },
    data: {
        title: "routes.ADD_RECIPIENTS_TITLE",
        mixpanelEvent: "Viewed new announcement add recipients",
    },
};

// TODO: OK to move to grower level
export const addContentsState = {
    name: "app.growers.grower.manageCommunication.base.addContents",
    url: "/write-message",
    component: "addContents",
    resolve: {
        announcementTranslateds: function (
            AnnouncementTRANSLATEDService,
            $transition$,
        ) {
            "ngInject";
            return AnnouncementTRANSLATEDService.list({
                announcement__sid: $transition$.params().announcement_sid,
            });
        },
        previousState: function () {
            "ngInject";
            return addRecipientsState;
        },
        nextState: function () {
            "ngInject";
            return addAttachmentsState;
        },
        reloadState: function () {
            "ngInject";
            return newAnnouncementBaseState;
        },
    },
    data: {
        title: "routes.ADD_CONTENTS_TITLE",
        mixpanelEvent: "Viewed new announcement write message",
    },
};

// TODO: OK to move to grower level
export const addAttachmentsState = {
    name: "app.growers.grower.manageCommunication.base.addAttachments",
    url: "/add-attachments",
    component: "addAttachments",
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
            );
        },
        currentAttachments: function (AttachmentService, $transition$) {
            "ngInject";
            return AttachmentService.list({
                announcement__sid: $transition$.params().announcement_sid,
            });
        },
        previousState: function () {
            "ngInject";
            return addContentsState;
        },
        nextState: function () {
            "ngInject";
            return addLocationState;
        },
        reloadState: function () {
            "ngInject";
            return newAnnouncementBaseState;
        },
    },
    data: {
        title: "routes.ADD_ATTACHMENTS_TITLE",
        mixpanelEvent: "Viewed new announcement add attachments",
    },
};

// TODO: OK to move to grower level
export const addLocationState = {
    name: "app.growers.grower.manageCommunication.base.addLocation",
    url: "/add-location",
    component: "addLocation",
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
            );
        },
        previousState: function () {
            "ngInject";
            return addAttachmentsState;
        },
        nextState: function () {
            "ngInject";
            return reviewAnnouncementState;
        },
        reloadState: function () {
            "ngInject";
            return newAnnouncementBaseState;
        },
        farm: function (FarmService, $transition$) {
            "ngInject";
            return FarmService.first({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.ADD_LOCATION_TITLE",
        mixpanelEvent: "Viewed new announcement add location",
    },
};

// TODO: OK to move to grower level
export const reviewAnnouncementState = {
    name: "app.growers.grower.manageCommunication.base.reviewAnnouncement",
    url: "/review",
    component: "reviewAnnouncement",
    resolve: {
        announcement: function (AnnouncementService, $transition$) {
            "ngInject";
            return AnnouncementService.retrieve(
                $transition$.params().announcement_sid,
                {},
                {
                    expand: ["translations"],
                },
            );
        },
        senderSid: function (announcement) {
            "ngInject";
            return announcement.sender || null;
        },
        senderRaw: function (AccountService, senderSid) {
            "ngInject";
            if (!senderSid) {
                return null;
            }
            return AccountService.retrieve(senderSid);
        },
        senderGrowerMembership: function (
            GrowerMembershipService,
            senderSid,
            $transition$,
        ) {
            "ngInject";
            if (!senderSid) {
                return null;
            }
            return GrowerMembershipService.first({
                grower_organization__sid: $transition$.params().grower_sid,
                user__sid: senderSid,
            }).catch(function () {
                return null;
            });
        },
        sender: function (senderRaw, senderGrowerMembership) {
            "ngInject";
            return mapUserWithGrowerMembershipName(
                senderRaw,
                senderGrowerMembership,
            );
        },
        attachments: function ($transition$, AttachmentService) {
            "ngInject";
            return AttachmentService.list({
                announcement__sid: $transition$.params().announcement_sid,
            }).then(function (results) {
                return _.groupBy(results, "language");
            });
        },
        previousState: function () {
            "ngInject";
            return addLocationState;
        },
        nextState: function () {
            "ngInject";
            return announcementDetailState;
        },
        reloadState: function () {
            "ngInject";
            return newAnnouncementBaseState;
        },
    },
    data: {
        title: "routes.REVIEW_ANNOUNCEMENT_TITLE",
        mixpanelEvent: "Viewed new announcement review",
    },
};

export const waflaContractListState = {
    name: "app.growers.grower.wafla",
    url: "/wafla",
    component: "contractList",
    resolve: {
        contractsData: function (WAFLAContractService, grower) {
            "ngInject";
            return WAFLAContractService.retrieve(grower.wafla_id);
        },
        activeContracts: function (contractsData) {
            "ngInject";
            return _.filter(contractsData.contracts, function (contract) {
                return _.includes(
                    [
                        "Active",
                        "Crossing Completed",
                        "Transfer",
                        "Processing Outbound",
                    ],
                    contract.status,
                );
            });
        },
        doneContracts: function (contractsData) {
            "ngInject";
            return _.filter(contractsData.contracts, ["status", "Done"]);
        },
    },
    data: {
        title: "routes.WAFLA_DASHBOARD",
        breadcrumb: "crumbs.WAFLA_DASHBOARD",
        mixpanelEvent: "Viewed wafla dashboard",
    },
};

export const waflaContractDetailState = {
    name: "app.growers.grower.wafla.contractDetail",
    url: "/{contract_airtable_id}",
    component: "contractDetail",
    resolve: {
        contract: function (contractsData, $transition$) {
            "ngInject";
            return _.find(contractsData.contracts, {
                id: $transition$.params().contract_airtable_id,
            });
        },
        candidates: function (H2ACandidateService, $transition$) {
            "ngInject";
            return H2ACandidateService.list({
                contract_airtable_id:
                    $transition$.params().contract_airtable_id,
            });
        },
        growerMemberships: function (GrowerMembershipService, candidates) {
            "ngInject";
            return GrowerMembershipService.several(
                _.map(candidates, "grower_membership"),
            );
        },
    },
    data: {
        title: "routes.WAFLA_CONTRACT",
        breadcrumb: "crumbs.WAFLA_CONTRACT",
        mixpanelEvent: "Viewed wafla contract detail",
    },
};

export const waflaContractOnboardingState = {
    name: "app.growers.grower.wafla.contractDetail.onboard",
    url: "/onboard",
    params: {
        actionItems: {
            squash: true,
            inherit: false,
        },
    },
    component: "contractOnboarding",
    resolve: {
        selectedWorkers: function ($transition$, GrowerMembershipService) {
            "ngInject";
            return GrowerMembershipService.several(
                $transition$.params().actionItems,
            );
        },
        contract: function (contractsData, $transition$) {
            "ngInject";
            return _.find(contractsData.contracts, {
                id: $transition$.params().contract_airtable_id,
            });
        },
        housingSites: function (HousingSiteService, $transition$) {
            "ngInject";
            return HousingSiteService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        courses: function (SafetyCourseService, $transition$) {
            "ngInject";
            return SafetyCourseService.list({
                active: true,
                accessor__sid: $transition$.params().grower_sid,
            });
        },
        groups: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        wpsCredentials: function ($transition$, CredentialService) {
            "ngInject";
            return CredentialService.list({
                archived: false,
                credential_type__wps_qualified: true,
                expiration_date__gt: moment().format(DATE_FORMAT),
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        wpsCredentialedUsers: function (wpsCredentials, AccountService) {
            "ngInject";
            let wpsTrainerUserSids = _.map(wpsCredentials, "subject");
            return AccountService.several(wpsTrainerUserSids);
        },
        wpsCredentialedGrowerMemberships: function (
            $transition$,
            wpsCredentials,
            GrowerMembershipService,
        ) {
            "ngInject";
            return GrowerMembershipService.list({
                user__sid__in: _.join(_.map(wpsCredentials, "subject")),
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        wpsTrainers: function (
            wpsCredentialedUsers,
            wpsCredentialedGrowerMemberships,
        ) {
            "ngInject";
            return _.compact(
                _.map(wpsCredentialedUsers, function (user) {
                    return _.find(wpsCredentialedGrowerMemberships, [
                        "user",
                        user.sid,
                    ]);
                }),
            );
        },
        packets: function (PacketService, $transition$) {
            "ngInject";
            return PacketService.list({
                active: true,
                owner__sid: $transition$.params().grower_sid,
                delivery_type_fully_specified: true,
            });
        },
        packetMembers: function (PacketMemberService, packets) {
            "ngInject";
            return PacketMemberService.list({
                active: true,
                packet__sid__in: _.join(_.map(packets, SID_LOOKUP)),
            });
        },
        documentPackages: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.list({
                active: true,
                status: DocumentPackageService.LIVE,
                accessor__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.WAFLA_CONTRACT_ONBOARDING",
        breadcrumb: "crumbs.WAFLA_CONTRACT_ONBOARDING",
        mixpanelEvent: "Viewed wafla contract onboarding",
    },
    redirectTo: parentIfEmpty,
};

export const waflaContractAddEmployeeState = {
    name: "app.growers.grower.wafla.contractDetail.addEmployee",
    url: "/add?candidate_sid",
    component: "contractAddEmployee",
    resolve: {
        candidate: function (H2ACandidateService, $transition$) {
            "ngInject";
            return H2ACandidateService.retrieve(
                $transition$.params().candidate_sid,
            );
        },
        crew: function (GroupService, $transition$, contract) {
            "ngInject";
            return GroupService.first({
                grower_organization__sid: $transition$.params().grower_sid,
                name: contract.name,
            });
        },
    },
    data: {
        title: "routes.WAFLA_CONTRACT_ADD_EMPLOYEE",
        breadcrumb: "crumbs.WAFLA_CONTRACT_ADD_EMPLOYEE",
        mixpanelEvent: "Viewed wafla contract add employee",
    },
};

export const fieldClockLinkRedirectState = {
    name: "app.linkFieldClock",
    url: "/link-fieldclock",
    resolve: {
        growerManagementMemberships: function (
            GrowerManagementMembershipService,
            accountSid,
        ) {
            "ngInject";
            return GrowerManagementMembershipService.list({
                active: true,
                user__sid: accountSid,
            });
        },
        redirect: function ($transition$, growerManagementMemberships) {
            "ngInject";
            let params = {
                grower_sid: _.first(growerManagementMemberships)
                    .grower_organization,
            };
            return $transition$.router.stateService.target(
                growerEditState,
                params,
            );
        },
    },
    data: {
        mixpanelEvent: "Viewed FieldClock link redirect",
    },
    redirectTo: (trans) => {
        return trans.injector().getAsync("redirect");
    },
};

export const publicListTrainingState = {
    name: "publicListTraining",
    url: "/trainings",
    abstract: true,
};

function getIdFromSlug(slug) {
    const sep = "--";
    const i = slug.lastIndexOf(sep);
    return i === -1 ? null : slug.slice(i + sep.length);
}

export const publicTrainingState = {
    name: "publicListTraining.publicTraining",
    url: "/{slug}",
    component: "publicSafetyMeetingTopic",
    lazyLoad: function ($transition$) {
        return Promise.all([
            import(/* webpackChunkName: "common.safety" */ "./common/safety"),
        ]).then(function (modules) {
            $transition$
                .injector()
                .get("$ocLazyLoad")
                .load(_.map(modules, "default"));
        });
    },
    resolve: {
        topic: function (PublicTrainingService, $transition$) {
            "ngInject";
            return PublicTrainingService.retrievePublic(
                $transition$.params().slug,
            );
        },
        defaultTranslation: function (topic) {
            "ngInject";
            return _.find(topic.translations, ["language", ENGLISH]);
        },
    },
    data: {
        title: "routes.VIEW_PUBLIC_TRAINING_TITLE",
        mixpanelEvent: "Viewed public training",
        description: "routes.VIEW_PUBLIC_TRAINING_DESCRIPTION",
    },
    redirectTo: function (trans) {
        let AuthService = trans.injector().get("AuthService");
        let GrowerManagementMembershipService = trans
            .injector()
            .get("GrowerManagementMembershipService");

        return AuthService.getAuthenticatedUserSid().then(
            function (userSid) {
                return GrowerManagementMembershipService.list({
                    active: true,
                    user__sid: userSid,
                }).then(
                    function (memberships) {
                        let firstMembership = _.first(memberships);
                        if (firstMembership) {
                            let topicSid = getIdFromSlug(trans.params().slug);
                            return trans.router.stateService.target(
                                viewTopicState,
                                {
                                    grower_sid:
                                        firstMembership.grower_organization,
                                    topic_sid: topicSid,
                                },
                            );
                        } else {
                            console.log("logged in but no managements");
                            return;
                        }
                    },
                    function () {
                        console.log("problem getting managements");
                        return;
                    },
                );
            },
            function () {
                console.log("not logged in!");
                return;
            },
        );
    },
};

export const growerHousingSitesState = {
    name: "app.growers.grower.housing",
    url: "/housing",
    component: "housingSiteList",
    resolve: {
        housingSites: function ($transition$, HousingSiteService) {
            "ngInject";
            return HousingSiteService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            }).then(function (housingSites) {
                if (housingSites.length) {
                    return housingSites;
                }
                return [MOCK_HOUSING_SITE];
            });
        },
        alertThemes: function (AlertThemeService, $transition$) {
            "ngInject";
            return AlertThemeService.list({
                category: HOUSING_CATEGORY,
                administrator__growerorganization__sid:
                    $transition$.params().grower_sid,
            });
        },
        alertSubscriptions: function (AlertSubscriptionService, $transition$) {
            "ngInject";
            return AlertSubscriptionService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.GROWER_HOUSING_SITES_TITLE",
        mixpanelEvent: "Viewed grower housing sites",
        breadcrumb: "crumbs.GROWER_HOUSING_SITES",
        permissions: {
            only: [MANAGE_HOUSING],
            redirectTo: previousIfForbidden,
        },
    },
};

export const housingNotificationSettingsState = {
    name: "app.growers.grower.housing.notifications",
    url: "/notifications",
    component: "notificationSettings",
    resolve: getNotificationSettingsResolves(HOUSING_RULES_REMINDER),
    data: {
        title: "routes.HOUSING_NOTIFICATIONS_TITLE",
        breadcrumb: "crumbs.HOUSING_NOTIFICATIONS",
        mixpanelEvent: "Viewed housing notification settings",
        permissions: {
            only: [MANAGE_HOUSING],
            redirectTo: previousIfForbidden,
        },
    },
};

export const growerHousingReportState = {
    name: "app.growers.grower.housing.report",
    url: "/report?from?to?crews?housing_site?employees",
    params: {
        from: {
            value: () => moment().startOf("year").toISOString(),
            squash: true,
            inherit: false,
        },
        to: {
            value: () => moment().toISOString(),
            squash: true,
            inherit: false,
        },
        crews: {
            array: true,
            value: [],
        },
        housing_site: {
            array: true,
            value: [],
        },
        employees: {
            array: true,
            value: [],
        },
    },
    component: "housingReport",
    resolve: {
        housingSites: function ($transition$, HousingSiteService) {
            "ngInject";
            return HousingSiteService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            }).then(function (housingSites) {
                if (housingSites.length) {
                    return housingSites;
                }
                return [MOCK_HOUSING_SITE];
            });
        },
        housingAssignments: function ($transition$, HousingAssignmentService) {
            "ngInject";
            return HousingAssignmentService.list({
                housing_site__grower_organization__sid:
                    $transition$.params().grower_sid,
                move_out_date__gt_or_null: $transition$.params().from,
            });
        },
        growerMemberships: function (
            $transition$,
            GrowerMembershipService,
            housingAssignments,
        ) {
            "ngInject";
            let growerMembershipSids = _.map(
                housingAssignments,
                "grower_membership",
            );
            return GrowerMembershipService.several(
                _.concat($transition$.params().employees, growerMembershipSids),
            );
        },
        groups: function (GroupService, $transition$) {
            "ngInject";
            return GroupService.list({
                active: true,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        query: function ($transition$) {
            "ngInject";
            let x = {
                fromDate: $transition$.params().from,
                toDate: $transition$.params().to,
                crews: $transition$.params().crews,
                housing_site: $transition$.params().housing_site,
                employees: $transition$.params().employees,
            };
            console.log(x);
            return x;
        },
        memberships: function (MembershipService, $transition$) {
            "ngInject";
            return MembershipService.list({
                group__grower_organization__sid:
                    $transition$.params().grower_sid,
                active: true,
            });
        },
    },
    data: {
        title: "routes.HOUSING_REPORT_TITLE",
        mixpanelEvent: "Viewed housing report",
        breadcrumb: "crumbs.HOUSING_REPORT",
        permissions: {
            only: [MANAGE_HOUSING],
            redirectTo: previousIfForbidden,
        },
    },
};

export const growerHousingSiteCreateState = {
    name: "app.growers.grower.housing.create",
    url: "/create",
    component: "housingSiteCreate",
    data: {
        title: "routes.GROWER_HOUSING_SITE_CREATE_TITLE",
        mixpanelEvent: "Viewed grower housing site create",
    },
};

export const growerHousingSiteEditState = {
    name: "app.growers.grower.housing.edit",
    url: "/{housing_site_sid}/edit",
    component: "housingSiteEdit",
    resolve: {
        site: function ($transition$, HousingSiteService) {
            "ngInject";
            return HousingSiteService.retrieve(
                $transition$.params().housing_site_sid,
            );
        },
    },
    data: {
        title: "routes.GROWER_HOUSING_SITE_EDIT_TITLE",
        mixpanelEvent: "Viewed grower housing site edit",
    },
};

export const housingConfigurationEditState = {
    name: "app.growers.grower.housing.configuration",
    url: "/settings",
    component: "housingConfigurationEdit",
    resolve: {
        housingConfiguration: function (
            HousingConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return HousingConfigurationService.first({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.HOUSING_CONFIGURATION_EDIT_TITLE",
        mixpanelEvent: "Viewed housing configuration edit",
        breadcrumb: "crumbs.HOUSING_CONFIGURATION_EDIT",
        permissions: {
            only: [MANAGE_HOUSING],
            redirectTo: previousIfForbidden,
        },
    },
};

export const documentPackageCustomizationCreateState = {
    name: "app.growers.grower.manageOnboarding.documents.view.createDocumentPackageCustomization",
    url: "/versions/create",
    component: "documentPackageCustomizationCreate",
    resolve: {
        document: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.retrieve(
                $transition$.params().documentpackage_sid,
            );
        },
    },
    data: {
        title: "routes.DOCUMENT_PACKAGE_CUSTOMIZATION_CREATE_TITLE",
        mixpanelEvent: "Viewed document package customization create",
        breadcrumb: "crumbs.DOCUMENT_PACKAGE_CUSTOMIZATION_CREATE",
    },
};

export const documentPackageCustomizationTranslatedEditState = {
    name: "app.growers.grower.manageOnboarding.documents.view.documentPackageCustomizationTranslatedEdit",
    url: "/versions/{document_package_customization_sid}/{language}/edit?previous_document_package_customization__sid",
    component: "documentPackageCustomizationTranslatedEdit",
    resolve: {
        customization: function (
            $transition$,
            DocumentPackageCustomizationService,
        ) {
            "ngInject";
            return DocumentPackageCustomizationService.retrieve(
                $transition$.params().document_package_customization_sid,
            );
        },
        customizationTranslated: function (
            DocumentPackageCustomizationTranslatedService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageCustomizationTranslatedService.first({
                document_package_customization__sid:
                    $transition$.params().document_package_customization_sid,
                language: $transition$.params().language,
            });
        },
        previousCustomizationTranslated: function (
            DocumentPackageCustomizationTranslatedService,
            $transition$,
        ) {
            "ngInject";
            if (
                !$transition$.params()
                    .previous_document_package_customization__sid
            ) {
                return;
            }
            return DocumentPackageCustomizationTranslatedService.retrieve(
                $transition$.params()
                    .previous_document_package_customization__sid,
            );
        },
        document: function (DocumentPackageService, $transition$) {
            "ngInject";
            return DocumentPackageService.retrieve(
                $transition$.params().documentpackage_sid,
            );
        },
        documentTranslated: function (
            DocumentPackageTranslatedService,
            $transition$,
        ) {
            "ngInject";
            return DocumentPackageTranslatedService.first({
                document_package__sid:
                    $transition$.params().documentpackage_sid,
                language: $transition$.params().language,
            });
        },
    },
    data: {
        title: "routes.DOCUMENT_PACKAGE_CUSTOMIZATION_EDIT_TITLE",
        mixpanelEvent: "Viewed document package customization edit",
        breadcrumb: "crumbs.DOCUMENT_PACKAGE_CUSTOMIZATION_EDIT",
    },
};

export const disciplineListState = {
    name: "app.growers.grower.discipline",
    url: "/discipline",
    component: "disciplineList",
    resolve: {
        disciplineReports: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.list({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        subjectEmployees: function (
            GrowerMembershipService,
            disciplineReports,
        ) {
            "ngInject";
            return GrowerMembershipService.several(
                _.map(disciplineReports, "subject_employee"),
            );
        },
        reportingEmployees: function (
            GrowerMembershipService,
            disciplineReports,
        ) {
            "ngInject";
            return GrowerMembershipService.several(
                _.map(disciplineReports, "reporting_employee"),
            );
        },
        disciplineReportTranslateds: function (
            $transition$,
            DisciplineReportTranslatedService,
            $translate,
        ) {
            "ngInject";
            return DisciplineReportTranslatedService.list({
                discipline_report__grower_organization__sid:
                    $transition$.params().grower_sid,
                language: $translate.use(),
            });
        },
    },
    data: {
        title: "routes.DISCIPLINE_LIST_TITLE",
        mixpanelEvent: "Viewed discipline list",
        breadcrumb: "crumbs.DISCIPLINE_LIST",
        permissions: {
            only: [MANAGE_DISCIPLINE],
            redirectTo: previousIfForbidden,
        },
    },
};

export const disciplineNotificationSettingsState = {
    name: "app.growers.grower.discipline.notifications",
    url: "/notifications",
    component: "notificationSettings",
    resolve: getNotificationSettingsResolves(ACTION_APPROVAL_NEEDED),
    data: {
        title: "routes.DISCIPLINE_NOTIFICATIONS_TITLE",
        breadcrumb: "crumbs.DISCIPLINE_NOTIFICATIONS",
        mixpanelEvent: "Viewed discipline notification settings",
        permissions: {
            only: [MANAGE_DISCIPLINE],
            redirectTo: previousIfForbidden,
        },
    },
};

export const terminationListState = {
    name: "app.growers.grower.termination",
    url: "/termination",
    component: "terminationList",
    resolve: {
        terminationTypes: function (TerminationTypeService) {
            "ngInject";
            return TerminationTypeService.list();
        },
    },
    data: {
        title: "routes.TERMINATION_LIST_TITLE",
        mixpanelEvent: "Viewed termination list",
        breadcrumb: "crumbs.TERMINATION_LIST",
        permissions: {
            only: [ADD_TERMINATE_EMPLOYEES],
            redirectTo: previousIfForbidden,
        },
    },
};

export const editTerminationSettingsState = {
    name: "app.growers.grower.termination.editTerminationSettings",
    url: "/settings",
    component: "editTerminationSettings",
    resolve: {
        terminationConfiguration: function (
            TerminationConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return TerminationConfigurationService.first({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        terminationTypes: function (TerminationTypeService) {
            "ngInject";
            return TerminationTypeService.list();
        },
        terminationTypeTranslateds: function (
            TerminationTypeTranslatedService,
        ) {
            "ngInject";
            return TerminationTypeTranslatedService.list();
        },
    },
    data: {
        title: "routes.TERMINATION_SETTINGS_TITLE",
        mixpanelEvent: "Viewed termination settings",
        breadcrumb: "crumbs.TERMINATION_SETTINGS",
        permissions: {
            only: [ADD_TERMINATE_EMPLOYEES],
            redirectTo: previousIfForbidden,
        },
    },
};

export const bulkTerminationState = {
    name: "app.growers.grower.termination.bulk",
    // Accept optional repeated grower membership sids to pre-select workers
    // Example: /bulk?grower_memberships=sid1&grower_memberships=sid2
    url: "/bulk?grower_memberships",
    params: {
        grower_memberships: {
            array: true,
            value: [],
            squash: true,
            inherit: false,
        },
    },
    component: "bulkTermination",
    resolve: {
        growerMemberships: function (GrowerMembershipService, $transition$) {
            "ngInject";
            return GrowerMembershipService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                active: true,
            });
        },
        activeHousingAssignments: function (
            $transition$,
            HousingAssignmentService,
        ) {
            "ngInject";
            return HousingAssignmentService.list({
                active: true,
                housing_site__grower_organization__sid:
                    $transition$.params().grower_sid,
            });
        },
        terminationTypes: function (TerminationTypeService) {
            "ngInject";
            return TerminationTypeService.list();
        },
        terminationTypeTranslateds: function (
            TerminationTypeTranslatedService,
        ) {
            "ngInject";
            return TerminationTypeTranslatedService.list();
        },
        recordedByGrowerMembership: function (
            GrowerMembershipService,
            account,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                grower_organization__sid: $transition$.params().grower_sid,
                user__sid: account.sid,
                active: true,
            });
        },
        // Map incoming grower_memberships param to user sids (ignoring any that aren't active)
        preselectedUserSids: function ($transition$, growerMemberships) {
            "ngInject";
            const paramSids = $transition$.params().grower_memberships || [];
            if (!paramSids.length) return [];
            const gmBySid = _.keyBy(growerMemberships, "sid");
            return _.uniq(
                _.compact(
                    paramSids.map((sid) =>
                        gmBySid[sid] ? gmBySid[sid].user : null,
                    ),
                ),
            );
        },
        disciplineReports: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                ordering: "-created",
                limit: 25,
            });
        },
        disciplineSubjectGrowerMemberships: function (
            GrowerMembershipService,
            disciplineReports,
        ) {
            "ngInject";
            return GrowerMembershipService.several(
                _.uniq(_.map(disciplineReports, "subject_employee")),
            );
        },
    },
    data: {
        title: "routes.BULK_TERMINATION_TITLE",
        mixpanelEvent: "Viewed bulk termination",
        breadcrumb: "crumbs.BULK_TERMINATION",
        permissions: {
            only: [ADD_TERMINATE_EMPLOYEES],
            redirectTo: previousIfForbidden,
        },
    },
};

export const editTerminationState = {
    name: "app.growers.grower.termination.edit",
    url: "/{termination_sid}/edit",
    component: "editTermination",
    resolve: {
        termination: function ($transition$, TerminationService) {
            "ngInject";
            return TerminationService.retrieve(
                $transition$.params().termination_sid,
            );
        },
        terminationTypes: function (TerminationTypeService) {
            "ngInject";
            return TerminationTypeService.list();
        },
        terminationTypeTranslateds: function (
            TerminationTypeTranslatedService,
        ) {
            "ngInject";
            return TerminationTypeTranslatedService.list();
        },
        disciplineReports: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.list({
                grower_organization__sid: $transition$.params().grower_sid,
                ordering: "-created",
                limit: 25,
            });
        },
        disciplineSubjectGrowerMemberships: function (
            GrowerMembershipService,
            disciplineReports,
        ) {
            "ngInject";
            return GrowerMembershipService.several(
                _.uniq(_.map(disciplineReports, "subject_employee")),
            );
        },
    },
    data: {
        title: "routes.EDIT_TERMINATION_TITLE",
        mixpanelEvent: "Viewed edit termination",
        breadcrumb: "crumbs.EDIT_TERMINATION",
        permissions: {
            only: [ADD_TERMINATE_EMPLOYEES],
            redirectTo: previousIfForbidden,
        },
    },
};

export const disciplineDetailState = {
    name: "app.growers.grower.discipline.view",
    url: "/{discipline_report_sid}",
    component: "disciplineDetail",
    resolve: {
        disciplineReport: function ($transition$, DisciplineReportService) {
            "ngInject";
            return DisciplineReportService.retrieve(
                $transition$.params().discipline_report_sid,
            );
        },
        subjectEmployee: function (GrowerMembershipService, disciplineReport) {
            "ngInject";
            return GrowerMembershipService.retrieve(
                disciplineReport.subject_employee,
            );
        },
        subjectEmployeeUser: function (AccountService, subjectEmployee) {
            "ngInject";
            return AccountService.retrieve(subjectEmployee.user);
        },
        reportingEmployee: function (
            GrowerMembershipService,
            disciplineReport,
        ) {
            "ngInject";
            return GrowerMembershipService.retrieve(
                disciplineReport.reporting_employee,
            );
        },
        disciplineReportTranslated: function (
            $transition$,
            DisciplineReportTranslatedService,
            $translate,
        ) {
            "ngInject";
            return DisciplineReportTranslatedService.first({
                discipline_report__sid:
                    $transition$.params().discipline_report_sid,
                language: $translate.use(),
            });
        },
        pastDisciplineReports: function (
            $transition$,
            DisciplineReportService,
            disciplineReport,
        ) {
            "ngInject";
            return DisciplineReportService.list({
                subject_employee__sid: disciplineReport.subject_employee,
                created__lt: disciplineReport.created,
            });
        },
        pastDisciplineReportTranslateds: function (
            pastDisciplineReports,
            DisciplineReportTranslatedService,
            $translate,
        ) {
            "ngInject";
            return DisciplineReportTranslatedService.list({
                discipline_report__sid__in: _.join(
                    _.map(pastDisciplineReports, SID_LOOKUP),
                ),
                language: $translate.use(),
            });
        },
        actionApprovingEmployee: function (
            GrowerMembershipService,
            disciplineReport,
        ) {
            "ngInject";
            return GrowerMembershipService.retrieve(
                disciplineReport.action_approving_employee,
            );
        },
        actionExecutingEmployee: function (
            GrowerMembershipService,
            disciplineReport,
        ) {
            "ngInject";
            return GrowerMembershipService.retrieve(
                disciplineReport.action_executing_employee,
            );
        },
        breadcrumb: function (disciplineReport, subjectEmployee) {
            "ngInject";
            return (
                subjectEmployee.first_name +
                " " +
                subjectEmployee.last_name +
                " (" +
                subjectEmployee.employee_id +
                ") - " +
                moment(disciplineReport.created).format(COMMON_DATE_FORMAT)
            );
        },
        myGrowerMembership: function (
            account,
            GrowerMembershipService,
            $transition$,
        ) {
            "ngInject";
            return GrowerMembershipService.first({
                user__sid: account.sid,
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
        subjectEmployeeAcknowledgementSignature: function (
            SignatureService,
            disciplineReport,
        ) {
            "ngInject";
            if (disciplineReport.subject_employee_acknowledgement_signature) {
                return SignatureService.retrieve(
                    disciplineReport.subject_employee_acknowledgement_signature,
                );
            }
            return null;
        },
    },
    data: {
        title: "routes.DISCIPLINE_DETAIL_TITLE",
        mixpanelEvent: "Viewed discipline detail",
        breadcrumb: "crumbs.DISCIPLINE_DETAIL",
    },
};

export const disciplineHRCustomActionState = {
    name: "app.growers.grower.discipline.view.editAction",
    url: "/edit-action",
    component: "disciplineHRCustomAction",
    data: {
        title: "routes.DISCIPLINE_HR_CUSTOM_ACTION_TITLE",
        mixpanelEvent: "Viewed discipline HR custom action",
        breadcrumb: "crumbs.DISCIPLINE_HR_CUSTOM_ACTION",
    },
};

export const editDisciplinePolicyState = {
    name: "app.growers.grower.discipline.editPolicy",
    url: "/policy",
    component: "editDisciplinePolicy",
    resolve: {
        disciplineConfiguration: function (
            DisciplineConfigurationService,
            $transition$,
        ) {
            "ngInject";
            return DisciplineConfigurationService.first({
                grower_organization__sid: $transition$.params().grower_sid,
            });
        },
    },
    data: {
        title: "routes.DISCIPLINE_POLICY_EDIT_TITLE",
        mixpanelEvent: "Viewed discipline policy edit",
        breadcrumb: "crumbs.DISCIPLINE_POLICY_EDIT",
    },
};
